import { useState, useCallback } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, ArrowLeft, ArrowRight, Share2, Check, Loader2, Search, Plus, FileText, MoreVertical, Calendar, MapPin, ChevronDown, Copy, Map, BookOpen, Mail, Users, UserPlus, Clock, CheckCircle2, Building2, BarChart3, Wrench, Edit3, MessageSquare, Eye, Download, User, ClipboardCheck, CheckCircle, Trash2, Settings, LogOut, Image as ImageIcon, Upload, FolderTree, Layers, Lightbulb, FolderOpen, Target, TrendingUp, Shield, Zap, AlertCircle, ClipboardList, Presentation, FileSearch, Scale, Heart, Home, GraduationCap, Laptop } from 'lucide-react';
import { DocsSidebar } from './components/DocsSidebar';
import { DocsLayout } from './components/DocsLayout';
import { DocsSearch } from './components/DocsSearch';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Link } from 'react-router-dom';
import { SplashScreen } from '@/components/SplashScreen';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { ThemeToggle } from '@/components/ThemeToggle';
import { LocalizedPage } from './content';
import { DocsSidebarProvider } from './context/DocsSidebarContext';
import { usePageTitle } from '@/hooks/usePageTitle';

// Wrapper para página localizada: O que é o Atas Digitais
function IntroductionPageWrapper() {
  return <LocalizedPage page="IntroductionPage" />;
}

// Wrapper para página localizada: Conceitos fundamentais
function ConceitosFundamentaisPageWrapper() {
  return <LocalizedPage page="ConceitosFundamentaisPage" />;
}

// Wrapper para página localizada: Visão geral
function VisaoGeralPageWrapper() {
  return <LocalizedPage page="VisaoGeralPage" />;
}

// Wrapper para página localizada: Projetos
function ProjetosPageWrapper() {
  return <LocalizedPage page="ProjetosPage" />;
}

// Wrapper para página localizada: Atas de reunião
function AtasDeReuniaoPageWrapper() {
  return <LocalizedPage page="AtasDeReuniaoPage" />;
}

// Wrapper para página localizada: Tópicos e itens
function TopicosEItensPageWrapper() {
  return <LocalizedPage page="TopicosEItensPage" />;
}

// Wrapper para página localizada: Desenvolvimentos
function DesenvolvimentosPageWrapper() {
  return <LocalizedPage page="DesenvolvimentosPage" />;
}

// Wrapper para página localizada: Participantes
function ParticipantesPageWrapper() {
  return <LocalizedPage page="ParticipantesPage" />;
}

// Wrapper para página localizada: Convidar equipe
function ConvidarEquipePageWrapper() {
  return <LocalizedPage page="ConvidarEquipePage" />;
}

// Wrapper para página localizada: Mapa de atas
function MapaDeAtasPageWrapper() {
  return <LocalizedPage page="MapaDeAtasPage" />;
}

// Wrapper para página localizada: Preview e exportação
function PreviewExportacaoPageWrapper() {
  return <LocalizedPage page="PreviewExportacaoPage" />;
}

// Wrapper para página localizada: Personalização
function PersonalizacaoPageWrapper() {
  return <LocalizedPage page="PersonalizacaoPage" />;
}

// Wrapper para página localizada: Boas práticas
function BoasPraticasAtasPageWrapper() {
  return <LocalizedPage page="BoasPraticasAtasPage" />;
}

// Wrapper para página localizada: Organização e colaboração
function OrganizacaoColaboracaoPageWrapper() {
  return <LocalizedPage page="OrganizacaoColaboracaoPage" />;
}

// Wrapper para página localizada: Novidades
function NovidadesPageWrapper() {
  return <LocalizedPage page="NovidadesPage" />;
}

// Página: O que é o Atas Digitais (versão legacy - será removida quando todas as páginas forem migradas)
function IntroductionPageLegacy() {
  return (
    <DocsLayout
      title="ATAS DIGITAIS STANT"
      description="A forma moderna, segura e profissional de gerir decisões"
      keywords="atas digitais stant, sistema corporativo atas, gestão decisões, livro de atas digital, governança corporativa, conselho administrativo"
      nextPage={{ title: 'Conceitos fundamentais', slug: 'conceitos-fundamentais' }}
    >
      <div className="space-y-12">
        <section className="space-y-6">
          <h1 className="text-4xl font-bold text-foreground">ATAS DIGITAIS STANT</h1>
          <p className="text-2xl leading-relaxed text-foreground font-semibold">
            A forma moderna, segura e profissional de gerir decisões
          </p>
          <p className="text-xl leading-relaxed text-muted-foreground">
            Transforme suas reuniões em decisões rastreáveis, organizadas e à prova de falhas — sem Word, sem PDF solto, sem perda de informação.
          </p>
          <p className="text-lg leading-relaxed text-muted-foreground">
            Atas Digitais Stant é um <strong className="text-foreground">sistema corporativo</strong> para criação, gestão e governança de livros de atas — ideal para qualquer organização que precise registrar decisões com clareza, responsabilidade e histórico seguro.
          </p>
        </section>

        <section>
          <h2 className="mb-6 text-2xl font-semibold text-foreground">Para quem é</h2>
          <p className="mb-6 text-lg text-muted-foreground">
            Perfeito para:
          </p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <div className="flex items-start gap-3 rounded-lg border border-border p-4 bg-background">
              <Shield className="size-5 mt-0.5 text-primary shrink-0" />
              <div>
                <p className="font-medium text-foreground">Conselhos administrativos e comitês de governança</p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-border p-4 bg-background">
              <Scale className="size-5 mt-0.5 text-primary shrink-0" />
              <div>
                <p className="font-medium text-foreground">Departamentos jurídicos e administrativos</p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-border p-4 bg-background">
              <Building2 className="size-5 mt-0.5 text-primary shrink-0" />
              <div>
                <p className="font-medium text-foreground">Empresas de engenharia, manutenção e fiscalização</p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-border p-4 bg-background">
              <Heart className="size-5 mt-0.5 text-primary shrink-0" />
              <div>
                <p className="font-medium text-foreground">Associações, fundações e organizações sem fins lucrativos</p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-border p-4 bg-background">
              <Home className="size-5 mt-0.5 text-primary shrink-0" />
              <div>
                <p className="font-medium text-foreground">Síndicos e administradoras de condomínio</p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-border p-4 bg-background">
              <GraduationCap className="size-5 mt-0.5 text-primary shrink-0" />
              <div>
                <p className="font-medium text-foreground">Escolas, universidades e instituições públicas</p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-border p-4 bg-background">
              <Laptop className="size-5 mt-0.5 text-primary shrink-0" />
              <div>
                <p className="font-medium text-foreground">Times de tecnologia, produto e operações</p>
              </div>
            </div>
          </div>
          <div className="mt-6 rounded-xl border border-primary/30 bg-primary/5 p-6">
            <p className="text-lg text-foreground">
              👉 Se sua organização precisa <strong>registrar decisões</strong>, <strong>acompanhar pendências</strong> e <strong>provar histórico com segurança</strong>, Atas Digitais Stant é para você.
            </p>
          </div>
        </section>

        <section className="rounded-xl bg-gradient-to-br from-primary/10 via-primary/5 to-background border border-primary/20 p-8">
          <h2 className="mb-4 text-2xl font-bold text-foreground text-center">Comece agora — totalmente gratuito</h2>
          <p className="mb-2 text-lg text-center text-muted-foreground">
            Inicie gratuitamente por <strong className="text-foreground">30 dias</strong> (ou até <strong className="text-foreground">3 livros de atas</strong>).
          </p>
          <p className="mb-6 text-center text-muted-foreground">
            Sem cartão. Sem compromisso. 100% funcional.
          </p>
          <p className="mb-6 text-center text-lg text-foreground">
            👉 Crie sua conta, abra seus livros de atas e experimente governança real desde a primeira reunião.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/register" className="inline-flex h-12 items-center justify-center rounded-lg bg-primary px-8 text-base font-semibold text-white transition-colors hover:bg-primary/90 shadow-lg">
              Criar conta grátis
            </Link>
            <Link to="/login" className="inline-flex h-12 items-center justify-center rounded-lg border border-border bg-white px-8 text-base font-semibold text-foreground transition-colors hover:bg-muted">
              Já tenho conta
            </Link>
          </div>
        </section>
      </div>
    </DocsLayout>
  );
}

// Página: Conceitos fundamentais
function ConceitosFundamentaisPage() {
  return (
    <DocsLayout
      title="Conceitos fundamentais"
      description="Entenda a estrutura do sistema antes de começar"
      keywords="conceitos stant atas, estrutura sistema atas, hierarquia projetos atas, como funciona ata digital"
      previousPage={{ title: 'O que é o Atas Digitais', slug: 'o-que-e' }}
      nextPage={{ title: 'Visão geral', slug: 'visao-geral' }}
    >
      <div className="space-y-10">
        <section>
          <p className="text-lg leading-relaxed text-muted-foreground">
            Antes de usar o Atas Digitais, é importante entender como as informações se organizam.
            São apenas <strong className="text-foreground">5 conceitos principais</strong>.
          </p>
        </section>

        {/* Hierarquia visual - Design moderno com SVG */}
        <section>
          <h2 className="mb-6 text-2xl font-semibold text-foreground">A hierarquia do sistema</h2>
          <div className="space-y-3">
            {/* Projeto */}
            <div className="group flex items-start gap-3 rounded-xl bg-gradient-to-r from-slate-50 to-white p-4 shadow-sm ring-1 ring-slate-200/50 transition-all hover:shadow-md hover:ring-slate-300">
              <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-slate-700 text-sm font-semibold text-white">
                1
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-slate-900">Projeto</h3>
                <p className="mt-0.5 text-sm text-slate-500">Uma obra, contrato ou cliente</p>
              </div>
            </div>

            {/* Conector SVG 1→2 */}
            <div className="ml-4 h-6">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-gray-300">
                <path d="M12 0 L12 12 Q12 20 20 20 L24 20" stroke="currentColor" strokeWidth="2" fill="none" />
              </svg>
            </div>

            {/* Ata */}
            <div className="ml-8 group flex items-start gap-3 rounded-xl bg-gradient-to-r from-blue-50 to-white p-4 shadow-sm ring-1 ring-blue-200/50 transition-all hover:shadow-md hover:ring-blue-300">
              <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-blue-600 text-sm font-semibold text-white">
                2
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-blue-900">Ata</h3>
                <p className="mt-0.5 text-sm text-blue-600/70">Uma reunião específica</p>
              </div>
            </div>

            {/* Conector SVG 2→3 */}
            <div className="ml-12 h-6">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-gray-300">
                <path d="M12 0 L12 12 Q12 20 20 20 L24 20" stroke="currentColor" strokeWidth="2" fill="none" />
              </svg>
            </div>

            {/* Tópico */}
            <div className="ml-16 group flex items-start gap-3 rounded-xl bg-gradient-to-r from-amber-50 to-white p-4 shadow-sm ring-1 ring-amber-200/50 transition-all hover:shadow-md hover:ring-amber-300">
              <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-amber-500 text-sm font-semibold text-white">
                3
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-amber-900">Tópico</h3>
                <p className="mt-0.5 text-sm text-amber-600/70">Área ou assunto da reunião</p>
              </div>
            </div>

            {/* Conector SVG 3→4 */}
            <div className="ml-20 h-6">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-gray-300">
                <path d="M12 0 L12 12 Q12 20 20 20 L24 20" stroke="currentColor" strokeWidth="2" fill="none" />
              </svg>
            </div>

            {/* Item */}
            <div className="ml-24 group flex items-start gap-3 rounded-xl bg-gradient-to-r from-green-50 to-white p-4 shadow-sm ring-1 ring-green-200/50 transition-all hover:shadow-md hover:ring-green-300">
              <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-green-600 text-sm font-semibold text-white">
                4
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-green-900">Item</h3>
                <p className="mt-0.5 text-sm text-green-600/70">Uma pendência específica</p>
                <div className="mt-2 flex flex-wrap gap-1">
                  <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700">responsável</span>
                  <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700">prazo</span>
                  <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700">status</span>
                </div>
              </div>
            </div>

            {/* Conector SVG 4→5 */}
            <div className="ml-28 h-6">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-gray-300">
                <path d="M12 0 L12 12 Q12 20 20 20 L24 20" stroke="currentColor" strokeWidth="2" fill="none" />
              </svg>
            </div>

            {/* Desenvolvimento */}
            <div className="ml-32 group flex items-start gap-3 rounded-xl bg-gradient-to-r from-purple-50 to-white p-4 shadow-sm ring-1 ring-purple-200/50 transition-all hover:shadow-md hover:ring-purple-300">
              <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-purple-600 text-sm font-semibold text-white">
                5
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold text-purple-900">Desenvolvimento</h3>
                  <span className="rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700">chave</span>
                </div>
                <p className="mt-0.5 text-sm text-purple-600/70">Atualização semanal do item</p>
              </div>
            </div>
          </div>
        </section>

        {/* Explicações */}
        <section>
          <h2 className="mb-6 text-2xl font-semibold text-foreground">O que é cada conceito</h2>
          <div className="space-y-4">
            <div className="rounded-lg border border-border bg-white p-5">
              <div className="mb-2 flex items-center gap-3">
                <span className="flex size-6 items-center justify-center rounded bg-slate-700 text-xs font-bold text-white">1</span>
                <h3 className="font-semibold text-foreground">Projeto</h3>
              </div>
              <p className="text-muted-foreground">Uma obra, contrato, cliente ou qualquer iniciativa. Se você acompanha 3 projetos, terá 3 projetos no sistema.</p>
            </div>

            <div className="rounded-lg border border-border bg-white p-5">
              <div className="mb-2 flex items-center gap-3">
                <span className="flex size-6 items-center justify-center rounded bg-blue-600 text-xs font-bold text-white">2</span>
                <h3 className="font-semibold text-foreground">Ata</h3>
              </div>
              <p className="text-muted-foreground">O registro de uma reunião. Se você tem reuniões recorrentes, cada encontro gera uma ata.</p>
            </div>

            <div className="rounded-lg border border-border bg-white p-5">
              <div className="mb-2 flex items-center gap-3">
                <span className="flex size-6 items-center justify-center rounded bg-amber-500 text-xs font-bold text-white">3</span>
                <h3 className="font-semibold text-foreground">Tópico</h3>
              </div>
              <p className="text-muted-foreground">Seções que organizam a pauta: "Elétrica", "Hidráulica", "Cronograma", etc.</p>
            </div>

            <div className="rounded-lg border border-border bg-white p-5">
              <div className="mb-2 flex items-center gap-3">
                <span className="flex size-6 items-center justify-center rounded bg-green-600 text-xs font-bold text-white">4</span>
                <h3 className="font-semibold text-foreground">Item</h3>
              </div>
              <p className="text-muted-foreground">Uma pendência com descrição, responsável, prazo e status (pendente/resolvido).</p>
            </div>

            <div className="rounded-lg borderborder-purple-200 bg-purple-50 p-5">
              <div className="mb-2 flex items-center gap-3">
                <span className="flex size-6 items-center justify-center rounded bg-purple-600 text-xs font-bold text-white">5</span>
                <h3 className="font-semibold text-foreground">Desenvolvimento</h3>
                <span className="rounded-full bg-purple-200 px-2 py-0.5 text-xs font-medium text-purple-700">Conceito-chave</span>
              </div>
              <p className="mb-3 text-muted-foreground">A atualização semanal de um item. Exemplo:</p>
              <div className="space-y-2 border-l-2 border-purple-300 pl-4 text-sm">
                <div className="rounded bg-white p-2">
                  <span className="font-medium text-purple-600">Ata 28:</span> "Projeto enviado para análise"
                </div>
                <div className="rounded bg-white p-2">
                  <span className="font-medium text-purple-600">Ata 29:</span> "Cliente pediu ajustes"
                </div>
                <div className="rounded bg-white p-2">
                  <span className="font-medium text-purple-600">Ata 30:</span> "Aprovado!"
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Clonagem */}
        <section className="rounded-xl bg-gradient-to-r from-gray-50 to-white p-6 shadow-sm ring-1 ring-gray-200/60">
          <h2 className="mb-3 text-lg font-semibold text-foreground">O diferencial: Clonagem Inteligente</h2>
          <p className="mb-4 text-sm text-muted-foreground">Ao criar uma nova ata clonando a anterior:</p>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="size-4 text-green-500" />
              <span className="text-muted-foreground">Itens pendentes vêm automaticamente</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="size-4 text-green-500" />
              <span className="text-muted-foreground">Itens resolvidos ficam para trás</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="size-4 text-green-500" />
              <span className="text-muted-foreground">Tópicos são preservados</span>
            </div>
          </div>
        </section>

        {/* Resumo */}
        <section className="rounded-xl bg-gradient-to-r from-gray-50 to-white p-6 shadow-sm ring-1 ring-gray-200/60">
          <h2 className="mb-4 text-lg font-semibold text-foreground">Resumindo</h2>
          <div className="flex flex-col gap-2 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <span className="flex size-5 items-center justify-center rounded bg-gray-200 text-xs font-medium text-gray-600">1</span>
              <span><strong className="text-foreground">Projeto</strong> = sua iniciativa (obra, cliente, contrato)</span>
            </div>
            <div className="ml-3 flex items-center gap-2">
              <span className="flex size-5 items-center justify-center rounded bg-gray-200 text-xs font-medium text-gray-600">2</span>
              <span><strong className="text-foreground">Ata</strong> = uma reunião</span>
            </div>
            <div className="ml-6 flex items-center gap-2">
              <span className="flex size-5 items-center justify-center rounded bg-gray-200 text-xs font-medium text-gray-600">3</span>
              <span><strong className="text-foreground">Tópico</strong> = seção da pauta</span>
            </div>
            <div className="ml-9 flex items-center gap-2">
              <span className="flex size-5 items-center justify-center rounded bg-gray-200 text-xs font-medium text-gray-600">4</span>
              <span><strong className="text-foreground">Item</strong> = pendência</span>
            </div>
            <div className="ml-12 flex items-center gap-2">
              <span className="flex size-5 items-center justify-center rounded bg-gray-200 text-xs font-medium text-gray-600">5</span>
              <span><strong className="text-foreground">Desenvolvimento</strong> = atualização</span>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="text-center">
          <p className="mb-4 text-muted-foreground">Agora veja como o sistema funciona na prática:</p>
          <Link
            to="/docs/visao-geral"
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3 font-medium text-white hover:bg-primary/90"
          >
            Ver visão geral
            <ArrowRight size={18} />
          </Link>
        </section>
      </div>
    </DocsLayout>
  );
}

// Página: Por que usar
// Página: Por que usar
function PorQueUsarPage() {
  return (
    <DocsLayout
      title="Por que usar o Atas Digitais"
      description="Entenda os problemas do processo manual e como o sistema resolve cada um deles"
      keywords="por que usar stant atas, vantagens ata digital, problemas ata word, benefícios sistema atas, substituir ata manual"
      previousPage={{ title: 'O que é o Atas Digitais', slug: 'o-que-e' }}
      nextPage={{ title: 'Conceitos básicos', slug: 'conceitos-basicos' }}
    >
      <div className="space-y-8">
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">
            O cenário atual: atas em Word e PDF
          </h2>
          <p className="mb-4 leading-relaxed text-muted-foreground">
            A maioria das empresas ainda utiliza um processo manual para documentar reuniões recorrentes, seja em obras, projetos de gestão ou alinhamentos regulares:
          </p>
          <div className="rounded-lg border border-border bg-muted/50 p-5">
            <ol className="space-y-2 text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="font-mono text-sm text-primary">1.</span>
                <span>Abre o template da ata anterior no Word</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-mono text-sm text-primary">2.</span>
                <span>Copia e cola os itens pendentes manualmente</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-mono text-sm text-primary">3.</span>
                <span>Atualiza as informações da reunião atual</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-mono text-sm text-primary">4.</span>
                <span>Exporta para PDF</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-mono text-sm text-primary">5.</span>
                <span>Salva em uma pasta compartilhada ou envia por e-mail</span>
              </li>
            </ol>
          </div>
          <p className="mt-4 leading-relaxed text-muted-foreground">
            Funciona? Sim. Mas quando a empresa lida com <strong className="text-foreground">múltiplos projetos simultâneos</strong>,
            esse processo se torna extremamente limitado.
          </p>
        </section>

        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">
            Problemas do processo manual
          </h2>
          <div className="space-y-4">
            <div className="flex items-start gap-4 rounded-lg border border-destructive/30 bg-destructive/5 p-4">
              <span className="mt-0.5 text-lg text-destructive">✗</span>
              <div>
                <h4 className="font-semibold text-foreground">Difícil acompanhar pendências</h4>
                <p className="text-sm text-muted-foreground">
                  Não há visão clara do que está pendente ou já foi resolvido. Você precisa abrir várias atas para entender o status.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 rounded-lg border border-destructive/30 bg-destructive/5 p-4">
              <span className="mt-0.5 text-lg text-destructive">✗</span>
              <div>
                <h4 className="font-semibold text-foreground">Retrabalho com "copiar e colar"</h4>
                <p className="text-sm text-muted-foreground">
                  Itens que se repetem precisam ser copiados manualmente, gerando erros e inconsistências.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 rounded-lg border border-destructive/30 bg-destructive/5 p-4">
              <span className="mt-0.5 text-lg text-destructive">✗</span>
              <div>
                <h4 className="font-semibold text-foreground">Sem rastreabilidade adequada</h4>
                <p className="text-sm text-muted-foreground">
                  É muito difícil ver a evolução de um assunto ao longo das semanas. "Em qual ata isso foi discutido pela primeira vez?"
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 rounded-lg border border-destructive/30 bg-destructive/5 p-4">
              <span className="mt-0.5 text-lg text-destructive">✗</span>
              <div>
                <h4 className="font-semibold text-foreground">Volume crescente de arquivos</h4>
                <p className="text-sm text-muted-foreground">
                  Com muitos projetos e reuniões recorrentes, o número de arquivos cresce rapidamente e o contexto se perde.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 rounded-lg border border-destructive/30 bg-destructive/5 p-4">
              <span className="mt-0.5 text-lg text-destructive">✗</span>
              <div>
                <h4 className="font-semibold text-foreground">Falta de padronização</h4>
                <p className="text-sm text-muted-foreground">
                  Cada pessoa formata de um jeito. Não há padrão garantido entre projetos e equipes.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">
            Como o Atas Digitais resolve cada problema
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="p-3 text-left font-semibold text-foreground">Problema</th>
                  <th className="p-3 text-left font-semibold text-foreground">Solução no Atas Digitais</th>
                </tr>
              </thead>
              <tbody className="text-muted-foreground">
                <tr className="border-b border-border">
                  <td className="p-3">Difícil acompanhar pendências</td>
                  <td className="p-3">Itens têm status (pendente/resolvido) e ficam visíveis até serem resolvidos</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3">Retrabalho com copiar/colar</td>
                  <td className="p-3">Clone a ata anterior com um clique, itens pendentes vêm automaticamente</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3">Sem rastreabilidade</td>
                  <td className="p-3">Cada item mostra em qual ata surgiu e todos os desenvolvimentos semanais</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3">Volume de arquivos</td>
                  <td className="p-3">Tudo em um sistema organizado por projeto, com busca e filtros</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3">Falta de padronização</td>
                  <td className="p-3">Estrutura fixa: tópicos, itens, responsáveis e prazos sempre no mesmo formato</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">
            O valor para cada parte envolvida
          </h2>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-lg border border-border p-5">
              <h4 className="mb-2 font-semibold text-foreground">Para a Gestão</h4>
              <p className="text-sm text-muted-foreground">
                Visão clara do que está acontecendo em cada projeto. Histórico confiável de todas as decisões tomadas.
              </p>
            </div>
            <div className="rounded-lg border border-border p-5">
              <h4 className="mb-2 font-semibold text-foreground">Para Auditoria</h4>
              <p className="text-sm text-muted-foreground">
                Documentação padronizada e rastreável. Facilita auditorias e comprovações.
              </p>
            </div>
            <div className="rounded-lg border border-border p-5">
              <h4 className="mb-2 font-semibold text-foreground">Para a Equipe</h4>
              <p className="text-sm text-muted-foreground">
                Clareza sobre responsabilidades e prazos. Registro formal do que foi acordado.
              </p>
            </div>
          </div>
        </section>

        <section className="rounded-lg border border-border bg-muted/50 p-6">
          <h3 className="mb-4 text-lg font-semibold text-foreground">
            Por que é importante ter documentação estruturada?
          </h3>
          <p className="mb-4 text-muted-foreground">
            Atas de reuniões recorrentes são documentos essenciais para:
          </p>
          <ul className="space-y-2 text-muted-foreground">
            <li className="flex items-center gap-2">
              <span className="text-green-600">✓</span>
              Garantir alinhamento entre todas as partes envolvidas
            </li>
            <li className="flex items-center gap-2">
              <span className="text-green-600">✓</span>
              Evitar falhas de comunicação que geram retrabalho e custos
            </li>
            <li className="flex items-center gap-2">
              <span className="text-green-600">✓</span>
              Manter histórico confiável do que foi discutido e decidido
            </li>
            <li className="flex items-center gap-2">
              <span className="text-green-600">✓</span>
              Acompanhar pendências até a resolução efetiva
            </li>
            <li className="flex items-center gap-2">
              <span className="text-green-600">✓</span>
              Proteger juridicamente todas as partes envolvidas
            </li>
          </ul>
        </section>

      </div>
    </DocsLayout>
  );
}

// Página: Conceitos básicos
function ConceitosBasicosPage() {
  return (
    <DocsLayout
      title="Conceitos básicos"
      description="Entenda a estrutura do sistema: projetos, atas, tópicos, itens e desenvolvimentos"
      keywords="conceitos básicos ata, estrutura ata reunião, projetos atas tópicos itens, organização atas"
      previousPage={{ title: 'Por que usar', slug: 'por-que-usar' }}
      nextPage={{ title: 'Criar conta', slug: 'criar-conta' }}
    >
      <div className="space-y-8">
        <section>
          <p className="mb-6 leading-relaxed text-muted-foreground">
            O Atas Digitais organiza informações em uma hierarquia simples que reflete a estrutura
            natural das reuniões recorrentes. Entender esses conceitos vai ajudar você a aproveitar
            ao máximo o sistema.
          </p>
        </section>

        <section className="rounded-lg border border-border bg-muted/50 p-6">
          <h2 className="mb-4 text-xl font-semibold text-foreground">
            Hierarquia de organização
          </h2>
          <div className="space-y-2 font-mono text-sm">
            <div className="text-foreground">📁 <strong>Projeto</strong> (ex: "Obra Shopping Norte")</div>
            <div className="ml-6 text-muted-foreground">├── 📄 Ata 30 - 08/01/2025</div>
            <div className="ml-12 text-muted-foreground">├── 💬 Tópico: Instalações Elétricas</div>
            <div className="ml-[4.5rem] text-muted-foreground">├── ⏳ Item: Aguardando aprovação do projeto</div>
            <div className="ml-[5.5rem] text-muted-foreground text-xs">└── Desenvolvimento: "Projeto enviado para análise"</div>
            <div className="ml-[4.5rem] text-muted-foreground">└── ✅ Item: Compra de materiais [Resolvido]</div>
            <div className="ml-12 text-muted-foreground">└── 💬 Tópico: Cronograma</div>
            <div className="ml-[4.5rem] text-muted-foreground">└── ⏳ Item: Revisar prazo da etapa 3</div>
            <div className="ml-6 text-muted-foreground">├── 📄 Ata 31 - 15/01/2025</div>
            <div className="ml-6 text-muted-foreground">└── 📄 Ata 32 - 22/01/2025</div>
          </div>
        </section>

        <section>
          <h2 className="mb-6 text-2xl font-semibold text-foreground">
            Os 5 elementos do sistema
          </h2>

          <div className="space-y-6">
            {/* Projeto */}
            <div className="rounded-lg border border-border p-6">
              <div className="mb-3 flex items-center gap-3">
                <span className="flex size-10 items-center justify-center rounded-lg bg-slate-100 text-xl">📁</span>
                <h3 className="text-xl font-semibold text-foreground">Projeto</h3>
              </div>
              <p className="mb-4 text-muted-foreground">
                O projeto é o nível mais alto de organização. Ele agrupa todas as atas relacionadas
                a uma mesma obra, contrato ou iniciativa. Pode ser uma obra específica, um cliente
                ou qualquer agrupamento que faça sentido para sua operação.
              </p>
              <div className="rounded-md bg-muted/50 p-4">
                <p className="text-sm font-medium text-foreground">Exemplos de projetos:</p>
                <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                  <li>• "Obra Shopping Norte - Fase 2"</li>
                  <li>• "Manutenção Preventiva - Cliente Petrobras"</li>
                  <li>• "Fiscalização BR-101 - Lote 3"</li>
                  <li>• "Reforma Hospital Municipal"</li>
                </ul>
              </div>
            </div>

            {/* Ata */}
            <div className="rounded-lg border border-border p-6">
              <div className="mb-3 flex items-center gap-3">
                <span className="flex size-10 items-center justify-center rounded-lg bg-green-100 text-xl">📄</span>
                <h3 className="text-xl font-semibold text-foreground">Ata</h3>
              </div>
              <p className="mb-4 text-muted-foreground">
                Cada ata representa uma reunião semanal. Ela contém a data, local, participantes e
                todo o conteúdo discutido. As atas são numeradas sequencialmente (Ata 1, 2, 3...) e
                podem estar em dois estados: <strong className="text-foreground">Em andamento</strong> (editável) ou{' '}
                <strong className="text-foreground">Finalizada</strong> (bloqueada para edição).
              </p>
              <div className="rounded-md bg-muted/50 p-4">
                <p className="text-sm font-medium text-foreground">Informações de uma ata:</p>
                <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                  <li>• <strong>Número:</strong> Sequencial dentro do projeto (Ata 30, Ata 31...)</li>
                  <li>• <strong>Data:</strong> Quando a reunião aconteceu</li>
                  <li>• <strong>Local:</strong> Onde ocorreu (escritório, sede, videoconferência)</li>
                  <li>• <strong>Participantes:</strong> Quem estava presente com nome, empresa e cargo</li>
                  <li>• <strong>Status:</strong> Em andamento ou Finalizada</li>
                </ul>
              </div>
            </div>

            {/* Tópico */}
            <div className="rounded-lg border border-border p-6">
              <div className="mb-3 flex items-center gap-3">
                <span className="flex size-10 items-center justify-center rounded-lg bg-yellow-100 text-xl">💬</span>
                <h3 className="text-xl font-semibold text-foreground">Tópico</h3>
              </div>
              <p className="mb-4 text-muted-foreground">
                Tópicos são os assuntos ou áreas de discussão da reunião. Eles organizam os itens
                por tema, facilitando encontrar informações depois. Você pode ter tópicos fixos
                que se repetem em todas as reuniões ou criar novos conforme necessário.
              </p>
              <div className="rounded-md bg-muted/50 p-4">
                <p className="text-sm font-medium text-foreground">Exemplos de tópicos:</p>
                <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                  <li>• "Cronograma e Prazos"</li>
                  <li>• "Pendências Técnicas"</li>
                  <li>• "Pendências Administrativas"</li>
                  <li>• "Alinhamentos com Cliente"</li>
                  <li>• "Documentação"</li>
                  <li>• "Próximos Passos"</li>
                </ul>
              </div>
            </div>

            {/* Item */}
            <div className="rounded-lg border border-border p-6">
              <div className="mb-3 flex items-center gap-3">
                <span className="flex size-10 items-center justify-center rounded-lg bg-purple-100 text-xl">✅</span>
                <h3 className="text-xl font-semibold text-foreground">Item</h3>
              </div>
              <p className="mb-4 text-muted-foreground">
                Itens são os pontos específicos dentro de cada tópico. Cada item representa uma
                pendência, decisão ou ação que precisa ser acompanhada. Os itens permanecem
                visíveis nas atas subsequentes até serem marcados como resolvidos.
              </p>
              <div className="rounded-md bg-muted/50 p-4">
                <p className="text-sm font-medium text-foreground">Cada item contém:</p>
                <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                  <li>• <strong>Descrição:</strong> O que precisa ser feito ou acompanhado</li>
                  <li>• <strong>Responsável:</strong> Quem deve executar ou responder</li>
                  <li>• <strong>Prazo:</strong> Data limite para conclusão</li>
                  <li>• <strong>Status:</strong> Pendente ou Resolvido</li>
                  <li>• <strong>Ata de origem:</strong> Em qual ata o item foi criado</li>
                </ul>
              </div>
            </div>

            {/* Desenvolvimento */}
            <div className="rounded-lg borderborder-primary/30 bg-primary/5 p-6">
              <div className="mb-3 flex items-center gap-3">
                <span className="flex size-10 items-center justify-center rounded-lg bg-primary/20 text-xl">📝</span>
                <h3 className="text-xl font-semibold text-foreground">Desenvolvimento</h3>
                <span className="rounded-full bg-primary/20 px-2 py-0.5 text-xs font-medium text-primary">Conceito-chave</span>
              </div>
              <p className="mb-4 text-muted-foreground">
                Desenvolvimentos são as <strong className="text-foreground">atualizações semanais</strong> de um item.
                A cada reunião, você registra o que aconteceu desde a última vez, sem precisar
                recriar o item. Isso mantém o histórico completo de cada pendência.
              </p>
              <div className="rounded-md border border-border bg-background p-4">
                <p className="mb-3 text-sm font-medium text-foreground">Exemplo prático:</p>
                <div className="space-y-3 text-sm">
                  <div className="rounded-md bg-muted/50 p-3">
                    <div className="font-medium text-foreground">
                      Item: Aguardando liberação do projeto de combate a incêndio
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      Responsável: Engenheiro Silva | Prazo: 30/01/2025
                    </div>
                  </div>
                  <div className="ml-4 space-y-2 border-l-2 border-primary/30 pl-4">
                    <div className="rounded-md bg-yellow-50 p-2">
                      <span className="text-xs font-medium text-yellow-700">Ata 30 - 08/01</span>
                      <p className="text-muted-foreground">"Projeto enviado ao corpo de bombeiros para análise"</p>
                    </div>
                    <div className="rounded-md bg-yellow-50 p-2">
                      <span className="text-xs font-medium text-yellow-700">Ata 31 - 15/01</span>
                      <p className="text-muted-foreground">"Bombeiros solicitaram complementação. Retorno previsto para 20/01"</p>
                    </div>
                    <div className="rounded-md bg-green-50 p-2">
                      <span className="text-xs font-medium text-green-700">Ata 32 - 22/01</span>
                      <p className="text-muted-foreground">"Projeto aprovado. Alvará recebido em 21/01" ✓</p>
                    </div>
                  </div>
                </div>
              </div>
              <p className="mt-4 text-sm text-muted-foreground">
                <strong className="text-foreground">Importante:</strong> Desenvolvimentos novos ficam
                destacados para indicar que aquele item foi abordado na reunião atual.
              </p>
            </div>
          </div>
        </section>

        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">
            Fluxo típico de uma reunião
          </h2>
          <div className="space-y-3">
            <div className="flex items-start gap-3 rounded-lg border border-border p-4">
              <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">1</span>
              <p className="text-muted-foreground">
                <strong className="text-foreground">Clone a última ata</strong>, todos os itens pendentes vêm automaticamente
              </p>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-border p-4">
              <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">2</span>
              <p className="text-muted-foreground">
                <strong className="text-foreground">Atualize os participantes</strong>, adicione ou remova quem está presente
              </p>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-border p-4">
              <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">3</span>
              <p className="text-muted-foreground">
                <strong className="text-foreground">Percorra os itens pendentes</strong>, adicione desenvolvimentos com o que foi discutido
              </p>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-border p-4">
              <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">4</span>
              <p className="text-muted-foreground">
                <strong className="text-foreground">Marque itens resolvidos</strong>, eles saem da próxima ata automaticamente
              </p>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-border p-4">
              <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">5</span>
              <p className="text-muted-foreground">
                <strong className="text-foreground">Adicione novos itens</strong>, novas pendências que surgiram na reunião
              </p>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-border p-4">
              <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">6</span>
              <p className="text-muted-foreground">
                <strong className="text-foreground">Finalize e exporte</strong>, gere o PDF para distribuir às partes
              </p>
            </div>
          </div>
        </section>

      </div>
    </DocsLayout>
  );
}

// Página: Criar conta
function CriarContaPage() {
  return (
    <DocsLayout
      title="Criar conta"
      description="Como criar sua conta no Atas Digitais"
      keywords="criar conta stant, cadastro stant atas, registro sistema atas, como começar stant"
      previousPage={{ title: 'Conceitos básicos', slug: 'conceitos-basicos' }}
      nextPage={{ title: 'Criar projeto', slug: 'criar-projeto' }}
    >
      <div className="space-y-8">
        <section>
          <p className="mb-6 text-lg leading-relaxed text-muted-foreground">
            Para começar a usar o Atas Digitais, você precisa criar uma conta. O processo é rápido
            e em poucos minutos você estará pronto para criar seu primeiro projeto.
          </p>
        </section>

        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">
            Passo a passo
          </h2>
          <div className="space-y-4">
            <div className="flex items-start gap-4 rounded-lg border border-border p-4">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">1</span>
              <div>
                <h4 className="font-semibold text-foreground">Acesse a página de cadastro</h4>
                <p className="mt-1 text-sm text-muted-foreground">
                  Na tela de login, clique em <strong>"Criar conta gratuita"</strong>.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 rounded-lg border border-border p-4">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">2</span>
              <div>
                <h4 className="font-semibold text-foreground">Preencha seus dados</h4>
                <p className="mt-1 text-sm text-muted-foreground">
                  Informe seu nome completo, e-mail corporativo e crie uma senha segura.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 rounded-lg border border-border p-4">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">3</span>
              <div>
                <h4 className="font-semibold text-foreground">Confirme seu e-mail</h4>
                <p className="mt-1 text-sm text-muted-foreground">
                  Você receberá um e-mail de confirmação. Clique no link para ativar sua conta.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 rounded-lg border border-border p-4">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">4</span>
              <div>
                <h4 className="font-semibold text-foreground">Faça login</h4>
                <p className="mt-1 text-sm text-muted-foreground">
                  Após confirmar, volte à tela de login e acesse com seu e-mail e senha.
                </p>
              </div>
            </div>
          </div>
        </section>

      </div>
    </DocsLayout>
  );
}

// Página: Criar projeto
function CriarProjetoPage() {
  return (
    <DocsLayout
      title="Criar projeto"
      description="Como criar e configurar um projeto no Atas Digitais"
      keywords="criar projeto stant, novo projeto ata, configurar projeto obra, organizar projetos reunião"
      previousPage={{ title: 'Criar conta', slug: 'criar-conta' }}
      nextPage={{ title: 'Sua primeira ata', slug: 'primeira-ata' }}
    >
      <div className="space-y-8">
        <section>
          <p className="mb-6 text-lg leading-relaxed text-muted-foreground">
            Projetos são a forma de organizar suas atas. Cada projeto representa uma iniciativa, obra, contrato, cliente ou qualquer contexto que terá reuniões recorrentes documentadas.
          </p>
        </section>

        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">
            Criando um novo projeto
          </h2>
          <div className="space-y-4">
            <div className="flex items-start gap-4 rounded-lg border border-border p-4">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">1</span>
              <div>
                <h4 className="font-semibold text-foreground">Acesse o Dashboard</h4>
                <p className="mt-1 text-sm text-muted-foreground">
                  Após fazer login, você será direcionado ao Dashboard onde verá todos os seus projetos.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 rounded-lg border border-border p-4">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">2</span>
              <div>
                <h4 className="font-semibold text-foreground">Clique em "Novo Projeto"</h4>
                <p className="mt-1 text-sm text-muted-foreground">
                  No canto superior direito, clique no botão para criar um novo projeto.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 rounded-lg border border-border p-4">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">3</span>
              <div>
                <h4 className="font-semibold text-foreground">Preencha as informações</h4>
                <p className="mt-1 text-sm text-muted-foreground">
                  Informe o nome do projeto e uma descrição opcional. Você também pode adicionar
                  uma imagem de capa para facilitar a identificação visual.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 rounded-lg border border-border p-4">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">4</span>
              <div>
                <h4 className="font-semibold text-foreground">Salve o projeto</h4>
                <p className="mt-1 text-sm text-muted-foreground">
                  Clique em salvar e seu projeto estará pronto para receber atas.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="rounded-lg border border-border bg-muted/50 p-6">
          <h3 className="mb-3 text-lg font-semibold text-foreground">
            Boas práticas para nomear projetos
          </h3>
          <p className="mb-4 text-sm text-muted-foreground">
            Um bom nome de projeto facilita encontrar as atas depois. Algumas sugestões:
          </p>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-green-600">✓</span>
              <span><strong className="text-foreground">"Projeto Alpha - Fase 2"</strong>, inclui o nome do projeto e fase</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-600">✓</span>
              <span><strong className="text-foreground">"Cliente XYZ - Consultoria 2025"</strong>, identifica o cliente e escopo</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-600">✓</span>
              <span><strong className="text-foreground">"Comitê de Governança"</strong>, indica o contexto quando recorrente</span>
            </li>
          </ul>
        </section>

      </div>
    </DocsLayout>
  );
}

// Página: Sua primeira ata
function PrimeiraAtaPage() {
  return (
    <DocsLayout
      title="Sua primeira ata"
      description="Aprenda a criar e preencher uma ata de reunião"
      keywords="primeira ata reunião, como criar ata, preencher ata, modelo ata reunião obra, tutorial ata digital"
      previousPage={{ title: 'Criar projeto', slug: 'criar-projeto' }}
      nextPage={{ title: 'Convidar equipe', slug: 'convidar-equipe' }}
    >
      <div className="space-y-8">
        <section>
          <p className="mb-6 text-lg leading-relaxed text-muted-foreground">
            Agora que você tem um projeto, vamos criar sua primeira ata. Esse é o documento
            que registra tudo o que foi discutido na reunião.
          </p>
        </section>

        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">
            Criando a ata
          </h2>
          <div className="space-y-4">
            <div className="flex items-start gap-4 rounded-lg border border-border p-4">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">1</span>
              <div>
                <h4 className="font-semibold text-foreground">Entre no projeto</h4>
                <p className="mt-1 text-sm text-muted-foreground">
                  No Dashboard, clique no projeto onde deseja criar a ata.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 rounded-lg border border-border p-4">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">2</span>
              <div>
                <h4 className="font-semibold text-foreground">Clique em "Nova Ata"</h4>
                <p className="mt-1 text-sm text-muted-foreground">
                  O sistema criará uma nova ata com o próximo número sequencial automaticamente.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 rounded-lg border border-border p-4">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">3</span>
              <div>
                <h4 className="font-semibold text-foreground">Preencha os dados básicos</h4>
                <p className="mt-1 text-sm text-muted-foreground">
                  Informe a data da reunião e o local (escritório, sede do cliente, videoconferência, etc.).
                </p>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">
            Preenchendo a ata
          </h2>
          <div className="space-y-6">
            <div className="rounded-lg border border-border p-5">
              <h4 className="mb-3 font-semibold text-foreground">1. Adicione os participantes</h4>
              <p className="text-sm text-muted-foreground">
                Registre quem estava presente na reunião. Para cada participante, informe nome,
                empresa e cargo. Isso é importante para documentação e rastreabilidade.
              </p>
            </div>

            <div className="rounded-lg border border-border p-5">
              <h4 className="mb-3 font-semibold text-foreground">2. Crie os tópicos</h4>
              <p className="text-sm text-muted-foreground">
                Tópicos organizam os assuntos discutidos. Exemplos: "Cronograma",
                "Pendências Técnicas", "Alinhamentos". Você pode reutilizar tópicos de atas anteriores.
              </p>
            </div>

            <div className="rounded-lg border border-border p-5">
              <h4 className="mb-3 font-semibold text-foreground">3. Adicione itens em cada tópico</h4>
              <p className="text-sm text-muted-foreground">
                Para cada assunto discutido, crie um item com descrição, responsável e prazo.
                Itens pendentes aparecerão automaticamente nas próximas atas.
              </p>
            </div>

            <div className="rounded-lg border border-border p-5">
              <h4 className="mb-3 font-semibold text-foreground">4. Registre desenvolvimentos</h4>
              <p className="text-sm text-muted-foreground">
                Para itens que já existiam, adicione um desenvolvimento descrevendo o que foi
                discutido ou o progresso desde a última reunião.
              </p>
            </div>
          </div>
        </section>

        <section className="rounded-lg border border-border bg-muted/50 p-6">
          <h3 className="mb-3 text-lg font-semibold text-foreground">
            Salvamento automático
          </h3>
          <p className="text-sm text-muted-foreground">
            O Atas Digitais salva suas alterações automaticamente enquanto você digita.
            Você não precisa se preocupar em perder informações se fechar a página acidentalmente.
          </p>
        </section>

        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">
            Finalizando a ata
          </h2>
          <p className="mb-4 text-muted-foreground">
            Quando terminar de preencher todos os assuntos da reunião:
          </p>
          <div className="space-y-3">
            <div className="flex items-start gap-3 rounded-lg border border-border p-4">
              <span className="text-green-600">✓</span>
              <p className="text-sm text-muted-foreground">
                Revise se todos os participantes estão listados
              </p>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-border p-4">
              <span className="text-green-600">✓</span>
              <p className="text-sm text-muted-foreground">
                Verifique se todos os itens têm responsável e prazo
              </p>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-border p-4">
              <span className="text-green-600">✓</span>
              <p className="text-sm text-muted-foreground">
                Marque como resolvidos os itens que foram resolvidos
              </p>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-border p-4">
              <span className="text-green-600">✓</span>
              <p className="text-sm text-muted-foreground">
                Clique em "Finalizar" para bloquear a ata para edição
              </p>
            </div>
          </div>
        </section>

        <section className="rounded-lg borderborder-primary/20 bg-primary/5 p-6">
          <h3 className="mb-3 text-lg font-semibold text-foreground">
            Dica: Clone atas anteriores
          </h3>
          <p className="text-muted-foreground">
            A partir da segunda reunião, você pode <strong className="text-foreground">clonar a ata anterior</strong> em vez de
            criar uma nova do zero. Isso traz automaticamente todos os itens pendentes,
            economizando tempo e garantindo continuidade.
          </p>
        </section>
      </div>
    </DocsLayout>
  );
}

// Página: Convidar equipe
function ConvidarEquipePage() {
  return (
    <DocsLayout
      title="Convidar equipe"
      description="Como compartilhar projetos com outros membros da equipe"
      keywords="convidar equipe stant, compartilhar projeto, adicionar colaboradores, trabalho em equipe atas"
      previousPage={{ title: 'Preview e exportação', slug: 'preview-e-exportacao' }}
      nextPage={{ title: 'Organização e colaboração', slug: 'organizacao-e-colaboracao' }}
    >
      <div className="space-y-8">
        <section>
          <p className="mb-6 text-lg leading-relaxed text-muted-foreground">
            Você pode compartilhar projetos com outros membros da equipe para que todos
            possam visualizar e editar as atas. Isso facilita a colaboração e garante
            que todos tenham acesso à documentação.
          </p>
        </section>

        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">
            Compartilhando um projeto
          </h2>
          <div className="space-y-4">
            <div className="flex items-start gap-4 rounded-lg border border-border p-4">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">1</span>
              <div>
                <h4 className="font-semibold text-foreground">Acesse o projeto</h4>
                <p className="mt-1 text-sm text-muted-foreground">
                  No Dashboard, encontre o projeto que deseja compartilhar.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 rounded-lg border border-border p-4">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">2</span>
              <div>
                <h4 className="font-semibold text-foreground">Clique em "Compartilhar"</h4>
                <p className="mt-1 text-sm text-muted-foreground">
                  No menu do projeto (três pontos), selecione a opção "Compartilhar".
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 rounded-lg border border-border p-4">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">3</span>
              <div>
                <h4 className="font-semibold text-foreground">Adicione colaboradores</h4>
                <p className="mt-1 text-sm text-muted-foreground">
                  Digite o e-mail da pessoa que você quer convidar. Ela precisa ter uma conta
                  no Atas Digitais para acessar.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 rounded-lg border border-border p-4">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">4</span>
              <div>
                <h4 className="font-semibold text-foreground">Confirme o convite</h4>
                <p className="mt-1 text-sm text-muted-foreground">
                  O colaborador receberá acesso imediato ao projeto e poderá visualizar
                  e editar todas as atas.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Mockup do modal de compartilhamento */}
        <section className="rounded-lg border border-border bg-background p-4">
          <h3 className="mb-4 text-sm font-medium text-muted-foreground">Modal de compartilhamento</h3>
          <div className="rounded-lg border border-border bg-white p-6 shadow-lg">
            {/* Header do modal */}
            <div className="mb-6">
              <h4 className="text-xl font-semibold text-foreground">Compartilhar Projeto</h4>
              <p className="text-sm text-muted-foreground">Edificação Comercial - Torre Norte</p>
            </div>

            {/* Card Convidar Colaboradores */}
            <div className="mb-4 rounded-lg border border-border p-4">
              <div className="mb-3 flex items-center gap-2">
                <div className="rounded bg-primary/10 p-1.5">
                  <Mail className="size-4 text-primary" />
                </div>
                <div>
                  <h5 className="text-sm font-medium">Convidar Colaboradores</h5>
                  <p className="text-xs text-muted-foreground">Adicione emails para compartilhar este projeto</p>
                </div>
              </div>

              {/* Campo de email com tags */}
              <div className="mb-3 flex flex-wrap gap-2 rounded-lg border border-border bg-background p-2.5">
                <span className="inline-flex items-center gap-1.5 rounded bg-primary/10 px-2.5 py-1 text-xs text-primary">
                  <Mail className="size-3" />
                  joao@empresa.com
                  <button className="ml-0.5 rounded-full p-0.5 hover:bg-primary/20">
                    <X className="size-3" />
                  </button>
                </span>
                <span className="text-xs text-muted-foreground leading-6">Digite um email e pressione Enter</span>
              </div>

              {/* Linha com permissão e botão */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium">Permissão padrão</span>
                  <span className="rounded border border-border bg-background px-3 py-1.5 text-xs">Visualizar ▾</span>
                </div>
                <button className="rounded-md bg-primary px-4 py-2 text-xs font-medium text-primary-foreground">
                  <span className="flex items-center gap-1.5">
                    <UserPlus className="size-3.5" />
                    Enviar convites
                  </span>
                </button>
              </div>
            </div>

            {/* Card Colaboradores */}
            <div className="rounded-lg border border-border p-4">
              <div className="mb-4 flex items-center gap-2">
                <div className="rounded bg-primary/10 p-1.5">
                  <Users className="size-4 text-primary" />
                </div>
                <div>
                  <h5 className="text-sm font-medium">Colaboradores</h5>
                  <p className="text-xs text-muted-foreground">Gerencie as permissões dos colaboradores</p>
                </div>
              </div>

              {/* Seção Ativos */}
              <div className="mb-4">
                <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  <CheckCircle2 className="size-3.5 text-green-600" />
                  Ativos (1)
                </div>
                <div className="rounded-lg border border-border p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="flex size-8 items-center justify-center rounded-full bg-primary/10">
                        <Mail className="size-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-xs font-medium">maria@construtora.com</p>
                        <p className="text-xs text-muted-foreground">Editar</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="rounded border border-border bg-background px-2.5 py-1 text-xs">Editar ▾</span>
                      <button className="text-xs text-destructive hover:underline">Remover</button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Seção Pendentes */}
              <div>
                <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  <Clock className="size-3.5 text-orange-500" />
                  Pendentes (1)
                </div>
                <div className="rounded-lg border border-border bg-muted/30 p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="flex size-8 items-center justify-center rounded-full bg-orange-100">
                        <Clock className="size-4 text-orange-500" />
                      </div>
                      <div>
                        <p className="text-xs font-medium">carlos@fornecedor.com</p>
                        <p className="text-xs text-muted-foreground">Aguardando aceite</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button className="rounded border border-primary px-2.5 py-1 text-xs text-primary">Reenviar</button>
                      <button className="text-xs text-muted-foreground hover:underline">Cancelar</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="rounded-lg border border-border bg-muted/50 p-6">
          <h3 className="mb-3 text-lg font-semibold text-foreground">
            Níveis de permissão
          </h3>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <span className="rounded bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">Visualizar</span>
              <p className="text-sm text-muted-foreground">Pode ver todas as atas e exportar, mas não pode fazer alterações.</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="rounded bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">Editar</span>
              <p className="text-sm text-muted-foreground">Acesso completo para criar atas, editar itens e adicionar desenvolvimentos.</p>
            </div>
          </div>
        </section>

        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">
            O que colaboradores podem fazer
          </h2>
          <ul className="space-y-2 text-muted-foreground">
            <li className="flex items-start gap-2"><span className="text-green-600">✓</span>Visualizar todas as atas do projeto</li>
            <li className="flex items-start gap-2"><span className="text-green-600">✓</span>Criar novas atas</li>
            <li className="flex items-start gap-2"><span className="text-green-600">✓</span>Editar atas em andamento</li>
            <li className="flex items-start gap-2"><span className="text-green-600">✓</span>Exportar atas e Mapa de Atas</li>
          </ul>
        </section>

        <section className="rounded-lg border border-yellow-200 bg-yellow-50 p-6">
          <h3 className="mb-3 text-lg font-semibold text-yellow-800">Importante</h3>
          <p className="text-sm text-yellow-700">Apenas o criador do projeto pode adicionar ou remover colaboradores.</p>
        </section>
      </div>
    </DocsLayout>
  );
}

// Página: Visão geral
function VisaoGeralPage() {
  return (
    <DocsLayout
      title="Visão geral"
      description="Entenda como o sistema se organiza"
      keywords="visão geral stant atas, interface sistema atas, dashboard atas, navegação sistema reuniões"
      previousPage={{ title: 'Conceitos fundamentais', slug: 'conceitos-fundamentais' }}
      nextPage={{ title: 'Projetos', slug: 'projetos' }}
    >
      <div className="space-y-8">
        {/* Introdução */}
        <section>
          <p className="text-lg leading-relaxed text-muted-foreground">
            O Atas Digitais organiza seu trabalho em telas conectadas. Cada funcionalidade tem uma página dedicada nesta documentação.
          </p>
        </section>

        {/* Para que serve */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Para que serve esta página?</h2>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-border p-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="flex size-8 items-center justify-center rounded-lg bg-blue-100">
                  <Map size={16} className="text-blue-600" />
                </div>
                <h4 className="font-semibold text-foreground">Mapa do sistema</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Entenda como as telas se conectam antes de começar a usar.
              </p>
            </div>
            <div className="rounded-lg border border-border p-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="flex size-8 items-center justify-center rounded-lg bg-green-100">
                  <Eye size={16} className="text-green-600" />
                </div>
                <h4 className="font-semibold text-foreground">Tour visual</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Veja previews de cada tela para saber o que esperar.
              </p>
            </div>
            <div className="rounded-lg border border-border p-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="flex size-8 items-center justify-center rounded-lg bg-orange-100">
                  <ArrowRight size={16} className="text-orange-600" />
                </div>
                <h4 className="font-semibold text-foreground">Fluxos de trabalho</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Descubra os caminhos principais de navegação.
              </p>
            </div>
            <div className="rounded-lg border border-border p-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="flex size-8 items-center justify-center rounded-lg bg-purple-100">
                  <BookOpen size={16} className="text-purple-600" />
                </div>
                <h4 className="font-semibold text-foreground">Links para detalhes</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Acesse a documentação específica de cada funcionalidade.
              </p>
            </div>
          </div>
        </section>

        {/* Fluxo visual */}
        <section className="rounded-lg border border-border bg-white p-6">
          <h2 className="mb-6 text-lg font-semibold text-foreground">Fluxo de navegação</h2>

          {/* Fluxo principal */}
          <div className="mb-6">
            <p className="text-xs text-gray-500 mb-3 text-center font-medium uppercase tracking-wide">Fluxo principal de edição</p>
            <div className="flex flex-wrap items-center justify-center gap-2 text-sm">
              <div className="rounded-lg bg-primary/10 px-4 py-2 font-medium text-primary">Dashboard</div>
              <span className="text-gray-400">→</span>
              <div className="rounded-lg bg-primary/10 px-4 py-2 font-medium text-primary">Projeto</div>
              <span className="text-gray-400">→</span>
              <div className="rounded-lg bg-primary/10 px-4 py-2 font-medium text-primary">Editor de Ata</div>
              <span className="text-gray-400">→</span>
              <div className="rounded-lg bg-gray-100 px-4 py-2 font-medium text-gray-700">Tópicos</div>
              <span className="text-gray-400">→</span>
              <div className="rounded-lg bg-gray-100 px-4 py-2 font-medium text-gray-700">Itens</div>
              <span className="text-gray-400">→</span>
              <div className="rounded-lg bg-gray-100 px-4 py-2 font-medium text-gray-700">Desenvolvimentos</div>
            </div>
            <p className="mt-2 text-center text-xs text-gray-500">Dentro de cada item você adiciona desenvolvimentos a cada reunião</p>
          </div>

          {/* Fluxos de visualização/exportação */}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <p className="text-xs text-gray-500 mb-3 font-medium uppercase tracking-wide">Visualização e exportação de ata</p>
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <div className="rounded bg-primary/10 px-3 py-1.5 font-medium text-primary">Editor de Ata</div>
                <span className="text-gray-400">→</span>
                <div className="rounded bg-amber-100 px-3 py-1.5 font-medium text-amber-700">Pré-visualização</div>
                <span className="text-gray-400">→</span>
                <div className="rounded bg-green-100 px-3 py-1.5 font-medium text-green-700">PDF da Ata</div>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <p className="text-xs text-gray-500 mb-3 font-medium uppercase tracking-wide">Mapa de Atas do projeto</p>
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <div className="rounded bg-primary/10 px-3 py-1.5 font-medium text-primary">Projeto</div>
                <span className="text-gray-400">→</span>
                <div className="rounded bg-purple-100 px-3 py-1.5 font-medium text-purple-700">Mapa de Atas</div>
                <span className="text-gray-400">→</span>
                <div className="rounded bg-green-100 px-3 py-1.5 font-medium text-green-700">PDF do Mapa</div>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 md:col-span-2">
              <p className="text-xs text-gray-500 mb-3 font-medium uppercase tracking-wide">Exportar todas as atas</p>
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <div className="rounded bg-primary/10 px-3 py-1.5 font-medium text-primary">Projeto</div>
                <span className="text-gray-400">→</span>
                <div className="rounded bg-cyan-100 px-3 py-1.5 font-medium text-cyan-700">Exportar Todas as Atas</div>
                <span className="text-gray-400">→</span>
                <div className="rounded bg-green-100 px-3 py-1.5 font-medium text-green-700">PDF Consolidado</div>
                <span className="text-gray-400 mx-1">|</span>
                <span className="text-xs text-gray-500">Todas as atas do projeto em um único documento</span>
              </div>
            </div>
          </div>
        </section>

        {/* Tour pelas telas principais */}
        <section>
          <h2 className="mb-2 text-2xl font-semibold text-foreground">Tour pelas telas principais</h2>
          <p className="mb-6 text-muted-foreground">
            Conheça as principais telas do sistema e como elas se conectam. Cada tela tem uma página dedicada
            com mais detalhes nesta documentação.
          </p>
        </section>

        {/* 1. Dashboard */}
        <section className="rounded-lg border border-border bg-white p-6">
          <div className="mb-4 flex items-center gap-3">
            <span className="flex size-8 items-center justify-center rounded-full bg-primary text-sm font-bold text-white">1</span>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Dashboard - Seus Projetos</h3>
              <p className="text-sm text-muted-foreground">Tela inicial ao fazer login. Veja todos os seus projetos em cards.</p>
            </div>
          </div>
          <div className="overflow-hidden rounded-lg border border-border bg-muted shadow-sm">
            <div className="border-b border-border bg-white px-4 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-bold text-gray-900">Meus Projetos</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                    <div className="flex h-8 w-36 items-center rounded-md border border-border bg-white pl-7 text-sm text-gray-400">
                      Buscar...
                    </div>
                  </div>
                  <button className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white">
                    <Plus size={14} />
                    Criar Projeto
                  </button>
                </div>
              </div>
            </div>
            <div className="grid gap-4 p-4 sm:grid-cols-3">
              <div className="group relative overflow-hidden rounded-lg border border-border bg-white shadow-sm">
                <div className="absolute right-3 top-3 z-10">
                  <button className="rounded bg-black/20 p-1 text-white"><MoreVertical size={14} /></button>
                </div>
                <div className="h-20 bg-gradient-to-br from-blue-500 to-blue-600" />
                <div className="p-3">
                  <h4 className="text-sm font-semibold text-gray-900">Obra Shopping Norte</h4>
                  <p className="mt-1 text-xs text-gray-500">Construção do novo shopping</p>
                </div>
                <div className="flex items-center gap-2 border-t border-gray-100 px-3 py-2 text-xs text-gray-500">
                  <FileText size={12} />
                  <span>12 atas</span>
                </div>
              </div>
              <div className="group relative overflow-hidden rounded-lg border border-border bg-white shadow-sm">
                <div className="absolute right-3 top-3 z-10">
                  <button className="rounded bg-black/20 p-1 text-white"><MoreVertical size={14} /></button>
                </div>
                <div className="h-20 bg-gradient-to-br from-emerald-500 to-emerald-600" />
                <div className="p-3">
                  <h4 className="text-sm font-semibold text-gray-900">Residencial Parque Verde</h4>
                  <p className="mt-1 text-xs text-gray-500">Condomínio - Fase 2</p>
                </div>
                <div className="flex items-center gap-2 border-t border-gray-100 px-3 py-2 text-xs text-gray-500">
                  <FileText size={12} />
                  <span>8 atas</span>
                </div>
              </div>
              <div className="group relative overflow-hidden rounded-lg border border-border bg-white shadow-sm">
                <div className="absolute right-3 top-3 z-10">
                  <button className="rounded bg-black/20 p-1 text-white"><MoreVertical size={14} /></button>
                </div>
                <div className="h-20 bg-gradient-to-br from-orange-500 to-orange-600" />
                <div className="p-3">
                  <h4 className="text-sm font-semibold text-gray-900">Reforma Ed. Central</h4>
                  <p className="mt-1 text-xs text-gray-500">Modernização</p>
                </div>
                <div className="flex items-center gap-2 border-t border-gray-100 px-3 py-2 text-xs text-gray-500">
                  <FileText size={12} />
                  <span>3 atas</span>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-3 flex items-center justify-between">
            <p className="text-xs text-muted-foreground">Clique em um projeto para ver suas atas</p>
            <Link to="/docs/projetos" className="text-xs font-medium text-primary hover:underline flex items-center gap-1">
              Ver detalhes <ArrowRight size={12} />
            </Link>
          </div>
        </section>

        {/* 2. Tela do Projeto */}
        <section className="rounded-lg border border-border bg-white p-6">
          <div className="mb-4 flex items-center gap-3">
            <span className="flex size-8 items-center justify-center rounded-full bg-primary text-sm font-bold text-white">2</span>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Tela do Projeto - Lista de Atas</h3>
              <p className="text-sm text-muted-foreground">Dentro de um projeto, veja todas as atas. Crie novas, clone ou acesse o Mapa de Atas.</p>
            </div>
          </div>
          <div className="overflow-hidden rounded-lg border border-border bg-white shadow-sm">
            <div className="border-b border-border bg-white px-4 py-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-gray-900">Obra Shopping Norte</h4>
                <div className="flex items-center gap-2">
                  <button className="flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs text-gray-700">
                    <Map size={12} />
                    Mapa de Atas
                  </button>
                  <button className="flex items-center gap-1.5 rounded-md bg-primary px-2.5 py-1.5 text-xs font-medium text-white">
                    <Plus size={12} />
                    Nova Ata
                    <ChevronDown size={10} />
                  </button>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between border-b border-gray-200 px-4">
              <div className="flex">
                <button className="!rounded-none border-b-2 border-primary px-3 py-2 text-xs font-medium text-primary -mb-px">Todas</button>
                <button className="!rounded-none px-3 py-2 text-xs text-gray-500">Em andamento</button>
                <button className="!rounded-none px-3 py-2 text-xs text-gray-500">Finalizadas</button>
              </div>
            </div>
            <div className="divide-y divide-border">
              <div className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50">
                <div className="flex-1">
                  <h5 className="text-xs font-medium text-gray-900">Ata 30</h5>
                  <p className="mt-0.5 text-xs text-gray-500">22/01/2025 - Videoconferência</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="rounded-md bg-amber-100 px-2 py-1 text-xs font-medium text-amber-700">Em andamento</span>
                  <MoreVertical size={12} className="text-gray-400" />
                </div>
              </div>
              <div className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50">
                <div className="flex-1">
                  <h5 className="text-xs font-medium text-gray-900">Ata 29</h5>
                  <p className="mt-0.5 text-xs text-gray-500">15/01/2025 - Escritório</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="rounded-md bg-green-100 px-2 py-1 text-xs font-medium text-green-700">Finalizada</span>
                  <MoreVertical size={12} className="text-gray-400" />
                </div>
              </div>
            </div>
          </div>
          <div className="mt-3 flex items-center justify-between">
            <p className="text-xs text-muted-foreground">Clique em uma ata para editar ou visualizar</p>
            <Link to="/docs/projetos" className="text-xs font-medium text-primary hover:underline flex items-center gap-1">
              Ver detalhes <ArrowRight size={12} />
            </Link>
          </div>
        </section>

        {/* 3. Editor de Ata */}
        <section className="rounded-lg border border-border bg-white p-6">
          <div className="mb-4 flex items-center gap-3">
            <span className="flex size-8 items-center justify-center rounded-full bg-primary text-sm font-bold text-white">3</span>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Editor de Ata</h3>
              <p className="text-sm text-muted-foreground">Edite a ata com tópicos, itens, participantes e desenvolvimentos. Tudo em uma interface organizada.</p>
            </div>
          </div>
          <div className="overflow-hidden rounded-lg border border-border bg-white shadow-sm">
            <div className="border-b border-border bg-white px-3 py-2">
              <div className="flex items-center gap-2">
                <button className="rounded-md p-1.5 hover:bg-gray-100">
                  <ArrowLeft size={14} className="text-gray-600" />
                </button>
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-semibold text-gray-900">Ata 30</span>
                  <span className="text-gray-400">•</span>
                  <span className="text-xs text-gray-600">Obra Shopping Norte</span>
                </div>
                <div className="ml-3 flex items-center gap-1.5 text-xs text-gray-500">
                  <Calendar className="size-3" />
                  <span>22 jan. 2025</span>
                </div>
                <span className="ml-auto rounded-md bg-amber-100 px-2 py-1 text-xs font-medium text-amber-700">Em andamento</span>
              </div>
            </div>
            <div className="bg-gray-50 p-4">
              <div className="mb-3">
                <h4 className="mb-2 text-xs font-semibold text-primary">Instalações Elétricas</h4>
                <div className="overflow-x-auto rounded-md border border-gray-200 bg-white">
                  <table className="w-full border-collapse text-xs">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="border-b border-gray-200 px-2 py-1.5 text-left font-semibold" style={{ width: '40px' }}>ITEM</th>
                        <th className="border-b border-gray-200 px-2 py-1.5 text-left font-semibold">DESCRIÇÃO</th>
                        <th className="border-b border-gray-200 px-2 py-1.5 text-left font-semibold" style={{ width: '70px' }}>RESP.</th>
                        <th className="border-b border-gray-200 px-2 py-1.5 text-left font-semibold" style={{ width: '55px' }}>PRAZO</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="cursor-pointer hover:bg-gray-50">
                        <td className="border-b border-gray-200 px-2 py-1.5 font-mono text-gray-600">1.1</td>
                        <td className="border-b border-gray-200 px-2 py-1.5">
                          <span className="text-gray-900">Substituir disjuntor do quadro QD-02</span>
                          <div className="mt-1 rounded border border-border bg-muted/30 p-1.5 text-xs">
                            <div className="mb-0.5 flex items-center gap-1.5">
                              <span className="rounded bg-red-100 px-1 py-0.5 text-xs font-bold text-red-700">NOVO</span>
                              <span className="font-medium text-primary">A30 |</span>
                            </div>
                            <p className="text-gray-600">"Disjuntor adquirido. Troca dia 12/02."</p>
                          </div>
                        </td>
                        <td className="border-b border-gray-200 px-2 py-1.5 text-gray-600">Eng. Carlos</td>
                        <td className="border-b border-gray-200 px-2 py-1.5 text-gray-600">15/02</td>
                      </tr>
                      <tr className="cursor-pointer bg-green-50/50 hover:bg-green-50">
                        <td className="border-b border-gray-200 px-2 py-1.5 font-mono text-gray-600">1.2</td>
                        <td className="border-b border-gray-200 px-2 py-1.5">
                          <span className="text-gray-900 line-through opacity-60">Teste de aterramento geral</span>
                          <span className="ml-1.5 inline-flex items-center rounded-md bg-green-100 px-1.5 py-0.5 text-xs font-medium text-green-700">Resolvido</span>
                        </td>
                        <td className="border-b border-gray-200 px-2 py-1.5 text-gray-600">Téc. João</td>
                        <td className="border-b border-gray-200 px-2 py-1.5 text-green-600">22/01</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-3 flex items-center justify-between">
            <p className="text-xs text-muted-foreground">Clique em um item para abrir o painel de edição</p>
            <Link to="/docs/atas-de-reuniao" className="text-xs font-medium text-primary hover:underline flex items-center gap-1">
              Ver detalhes <ArrowRight size={12} />
            </Link>
          </div>
        </section>

        {/* 4. Pré-visualização e PDF */}
        <section className="rounded-lg border border-border bg-white p-6">
          <div className="mb-4 flex items-center gap-3">
            <span className="flex size-8 items-center justify-center rounded-full bg-primary text-sm font-bold text-white">4</span>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Pré-visualização e Exportação PDF</h3>
              <p className="text-sm text-muted-foreground">Visualize como a ata ficará no PDF e exporte para compartilhar com a equipe.</p>
            </div>
          </div>
          <div className="overflow-hidden rounded-lg border border-border shadow-sm">
            <div className="flex items-center justify-between border-b border-border bg-white px-4 py-3">
              <button className="flex items-center gap-2 rounded-md px-3 py-1.5 text-xs text-muted-foreground hover:bg-muted">
                <ArrowLeft className="size-4" />
                Voltar
              </button>
              <button className="flex items-center gap-2 rounded-lg bg-foreground px-4 py-2 text-xs font-medium text-background">
                <FileText className="size-4" />
                Exportar PDF
              </button>
            </div>
            <div className="bg-gray-100 p-4">
              <div className="mx-auto max-w-lg rounded-lg border border-border bg-white p-4 shadow-sm">
                <div className="mb-4 border-b border-border pb-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <h4 className="text-sm font-bold text-foreground">Ata de Reunião #30</h4>
                      <div className="mt-1 flex flex-wrap gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="size-3" />
                          22/01/2025
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="size-3" />
                          Videoconferência
                        </span>
                      </div>
                    </div>
                    <div className="flex h-10 w-20 shrink-0 items-center justify-center rounded borderborder-dashed border-primary/40 bg-primary/5">
                      <span className="text-xs font-medium text-primary">Sua Logo</span>
                    </div>
                  </div>
                </div>
                <div className="mb-3">
                  <h5 className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Participantes</h5>
                  <div className="flex flex-wrap gap-1.5">
                    <span className="rounded bg-muted px-1.5 py-0.5 text-xs">João Silva</span>
                    <span className="rounded bg-muted px-1.5 py-0.5 text-xs">Maria Santos</span>
                    <span className="rounded bg-muted px-1.5 py-0.5 text-xs">Pedro Costa</span>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <div className="h-2.5 w-3/4 rounded bg-gray-100"></div>
                  <div className="h-2.5 w-full rounded bg-gray-100"></div>
                  <div className="h-2.5 w-5/6 rounded bg-gray-100"></div>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-3 flex items-center justify-between">
            <p className="text-xs text-muted-foreground">Inclui logo, participantes, tópicos, itens e desenvolvimentos</p>
            <Link to="/docs/preview-e-exportacao" className="text-xs font-medium text-primary hover:underline flex items-center gap-1">
              Ver detalhes <ArrowRight size={12} />
            </Link>
          </div>
        </section>

        {/* Links rápidos para outras páginas */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Saiba mais</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Link to="/docs/topicos-e-itens" className="group rounded-lg border border-border p-4 hover:border-primary/50 hover:bg-primary/5 transition-colors">
              <div className="flex items-center gap-3">
                <Layers className="size-5 text-primary" />
                <div>
                  <h3 className="font-semibold text-foreground group-hover:text-primary">Tópicos e itens</h3>
                  <p className="text-xs text-muted-foreground">Organize assuntos e pendências</p>
                </div>
              </div>
            </Link>
            <Link to="/docs/desenvolvimentos" className="group rounded-lg border border-border p-4 hover:border-primary/50 hover:bg-primary/5 transition-colors">
              <div className="flex items-center gap-3">
                <MessageSquare className="size-5 text-primary" />
                <div>
                  <h3 className="font-semibold text-foreground group-hover:text-primary">Desenvolvimentos</h3>
                  <p className="text-xs text-muted-foreground">Atualizações semanais</p>
                </div>
              </div>
            </Link>
            <Link to="/docs/mapa-de-atas" className="group rounded-lg border border-border p-4 hover:border-primary/50 hover:bg-primary/5 transition-colors">
              <div className="flex items-center gap-3">
                <Map className="size-5 text-purple-600" />
                <div>
                  <h3 className="font-semibold text-foreground group-hover:text-primary">Mapa de Atas</h3>
                  <p className="text-xs text-muted-foreground">Visão consolidada do projeto</p>
                </div>
              </div>
            </Link>
            <Link to="/docs/convidar-equipe" className="group rounded-lg border border-border p-4 hover:border-primary/50 hover:bg-primary/5 transition-colors">
              <div className="flex items-center gap-3">
                <Users className="size-5 text-primary" />
                <div>
                  <h3 className="font-semibold text-foreground group-hover:text-primary">Convidar equipe</h3>
                  <p className="text-xs text-muted-foreground">Colabore com outros usuários</p>
                </div>
              </div>
            </Link>
            <Link to="/docs/personalizacao" className="group rounded-lg border border-border p-4 hover:border-primary/50 hover:bg-primary/5 transition-colors">
              <div className="flex items-center gap-3">
                <Settings className="size-5 text-primary" />
                <div>
                  <h3 className="font-semibold text-foreground group-hover:text-primary">Personalização</h3>
                  <p className="text-xs text-muted-foreground">Logo, tópicos padrão e mais</p>
                </div>
              </div>
            </Link>
            <Link to="/docs/boas-praticas-atas" className="group rounded-lg border border-border p-4 hover:border-primary/50 hover:bg-primary/5 transition-colors">
              <div className="flex items-center gap-3">
                <BookOpen className="size-5 text-primary" />
                <div>
                  <h3 className="font-semibold text-foreground group-hover:text-primary">Boas práticas</h3>
                  <p className="text-xs text-muted-foreground">Dicas para atas eficientes</p>
                </div>
              </div>
            </Link>
          </div>
        </section>
      </div>
    </DocsLayout>
  );
}

// Página: Projetos
function ProjetosPage() {
  return (
    <DocsLayout
      title="Projetos"
      description="Como organizar e gerenciar seus projetos"
      keywords="projetos stant atas, gerenciar projetos obra, organização projetos reunião, dashboard projetos"
      previousPage={{ title: 'Visão geral', slug: 'visao-geral' }}
      nextPage={{ title: 'Atas de reunião', slug: 'atas-de-reuniao' }}
    >
      <div className="space-y-8">
        <section>
          <p className="mb-6 text-lg leading-relaxed text-muted-foreground">
            Projetos são o nível mais alto de organização no Atas Digitais. Cada projeto agrupa
            todas as atas relacionadas a uma mesma iniciativa, contrato ou cliente.
          </p>
        </section>

        {/* Para que serve */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Para que serve?</h2>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-border p-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="flex size-8 items-center justify-center rounded-lg bg-blue-100">
                  <FolderOpen size={16} className="text-blue-600" />
                </div>
                <h4 className="font-semibold text-foreground">Organização por iniciativa</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Agrupe todas as atas de uma mesma iniciativa, contrato ou cliente em um único lugar.
              </p>
            </div>
            <div className="rounded-lg border border-border p-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="flex size-8 items-center justify-center rounded-lg bg-green-100">
                  <Users size={16} className="text-green-600" />
                </div>
                <h4 className="font-semibold text-foreground">Colaboração em equipe</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Convide colaboradores para acessar e contribuir com as atas do projeto.
              </p>
            </div>
            <div className="rounded-lg border border-border p-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="flex size-8 items-center justify-center rounded-lg bg-orange-100">
                  <Map size={16} className="text-orange-600" />
                </div>
                <h4 className="font-semibold text-foreground">Visão consolidada</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Acesse o Mapa de Atas para ver todas as pendências do projeto de uma vez.
              </p>
            </div>
            <div className="rounded-lg border border-border p-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="flex size-8 items-center justify-center rounded-lg bg-purple-100">
                  <FileText size={16} className="text-purple-600" />
                </div>
                <h4 className="font-semibold text-foreground">Histórico completo</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Mantenha o registro de todas as reuniões e decisões em um só lugar.
              </p>
            </div>
          </div>
        </section>

        {/* Dashboard - Tela de projetos */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Dashboard - Meus Projetos</h2>
          <p className="mb-4 text-muted-foreground">Ao fazer login, você verá o Dashboard com todos os seus projetos:</p>
          <div className="overflow-hidden rounded-lg border border-border bg-muted shadow-sm">
            {/* Header do Dashboard */}
            <div className="border-b border-border bg-white px-4 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-bold text-gray-900">Meus Projetos</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                    <div className="flex h-8 w-36 items-center rounded-md border border-border bg-white pl-7 text-sm text-gray-400">
                      Buscar...
                    </div>
                  </div>
                  <button className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white">
                    <Plus size={14} />
                    Criar Projeto
                  </button>
                </div>
              </div>
            </div>

            {/* Grid de projetos */}
            <div className="grid gap-4 p-4 sm:grid-cols-3">
              {/* Card 1 */}
              <div className="group relative overflow-hidden rounded-lg border border-border bg-white shadow-sm">
                <div className="absolute right-3 top-3 z-10">
                  <button className="rounded bg-black/20 p-1 text-white"><MoreVertical size={14} /></button>
                </div>
                <div className="h-20 bg-gradient-to-br from-blue-500 to-blue-600" />
                <div className="p-3">
                  <h4 className="text-sm font-semibold text-gray-900">Obra Shopping Norte</h4>
                  <p className="mt-1 text-xs text-gray-500">Construção do novo shopping</p>
                </div>
                <div className="flex items-center gap-2 border-t border-gray-100 px-3 py-2 text-xs text-gray-500">
                  <FileText size={12} />
                  <span>12 atas</span>
                  <span className="text-xs">• há 2 dias</span>
                </div>
              </div>

              {/* Card 2 */}
              <div className="group relative overflow-hidden rounded-lg border border-border bg-white shadow-sm">
                <div className="absolute right-3 top-3 z-10">
                  <button className="rounded bg-black/20 p-1 text-white"><MoreVertical size={14} /></button>
                </div>
                <div className="h-20 bg-gradient-to-br from-emerald-500 to-emerald-600" />
                <div className="p-3">
                  <h4 className="text-sm font-semibold text-gray-900">Residencial Parque Verde</h4>
                  <p className="mt-1 text-xs text-gray-500">Condomínio - Fase 2</p>
                </div>
                <div className="flex items-center gap-2 border-t border-gray-100 px-3 py-2 text-xs text-gray-500">
                  <FileText size={12} />
                  <span>8 atas</span>
                  <span className="text-xs">• há 1 semana</span>
                </div>
              </div>

              {/* Card 3 */}
              <div className="group relative overflow-hidden rounded-lg border border-border bg-white shadow-sm">
                <div className="absolute right-3 top-3 z-10">
                  <button className="rounded bg-black/20 p-1 text-white"><MoreVertical size={14} /></button>
                </div>
                <div className="h-20 bg-gradient-to-br from-orange-500 to-orange-600" />
                <div className="p-3">
                  <h4 className="text-sm font-semibold text-gray-900">Reforma Ed. Central</h4>
                  <p className="mt-1 text-xs text-gray-500">Modernização</p>
                </div>
                <div className="flex items-center gap-2 border-t border-gray-100 px-3 py-2 text-xs text-gray-500">
                  <FileText size={12} />
                  <span>3 atas</span>
                  <span className="text-xs">• há 3 dias</span>
                </div>
              </div>
            </div>
          </div>
          <p className="mt-2 text-center text-xs text-muted-foreground">Clique em um projeto para ver suas atas</p>
        </section>

        {/* Como criar um projeto */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Criando um novo projeto</h2>
          <div className="space-y-4">
            <div className="flex items-start gap-4 rounded-lg border border-border p-4">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">1</span>
              <div>
                <h4 className="font-semibold text-foreground">Acesse o Dashboard</h4>
                <p className="mt-1 text-sm text-muted-foreground">Após fazer login, você verá todos os seus projetos.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 rounded-lg border border-border p-4">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">2</span>
              <div>
                <h4 className="font-semibold text-foreground">Clique em "Novo Projeto"</h4>
                <p className="mt-1 text-sm text-muted-foreground">No canto superior direito, clique no botão para criar.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 rounded-lg border border-border p-4">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">3</span>
              <div>
                <h4 className="font-semibold text-foreground">Preencha as informações</h4>
                <p className="mt-1 text-sm text-muted-foreground">Informe nome, descrição opcional e imagem de capa.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 rounded-lg border border-border p-4">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">4</span>
              <div>
                <h4 className="font-semibold text-foreground">Salve o projeto</h4>
                <p className="mt-1 text-sm text-muted-foreground">Pronto! Seu projeto está criado e pode receber atas.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Mockup visual da tela de projeto */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Tela do projeto</h2>
          <div className="overflow-hidden rounded-lg border border-border bg-white shadow-sm">
            {/* Header com título e botões */}
            <div className="border-b border-border bg-white px-4 py-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900">Obra Shopping Norte</h3>
                <div className="flex items-center gap-2">
                  <button className="flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs text-gray-700">
                    <Map size={12} />
                    Mapa de Atas
                  </button>
                  <button className="flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs text-gray-700">
                    <BookOpen size={12} />
                    Exportar Todas
                  </button>
                  <button className="flex items-center gap-1.5 rounded-md bg-primary px-2.5 py-1.5 text-xs font-medium text-white">
                    <Plus size={12} />
                    Nova Ata
                    <ChevronDown size={10} />
                  </button>
                </div>
              </div>
            </div>

            {/* Abas de filtro e busca */}
            <div className="flex items-center justify-between border-b border-gray-200 px-4">
              <div className="flex">
                <button className="!rounded-none px-3 py-2 text-xs font-medium text-primary border-b-2 border-primary -mb-px">Todas</button>
                <button className="!rounded-none px-3 py-2 text-xs text-gray-500">Em andamento</button>
                <button className="!rounded-none px-3 py-2 text-xs text-gray-500">Finalizadas</button>
              </div>
              <div className="relative py-1.5">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" size={12} />
                <div className="flex h-6 w-32 items-center rounded-md border border-border bg-white pl-6 text-xs text-gray-400">
                  Buscar atas...
                </div>
              </div>
            </div>

            {/* Lista de atas */}
            <div className="divide-y divide-border">
              <div className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50">
                <div className="flex-1">
                  <h4 className="text-xs font-medium text-gray-900">Ata 30</h4>
                  <p className="text-xs text-gray-500 mt-0.5">22/01/2025 - Videoconferência</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="rounded-md bg-amber-100 px-2 py-1 text-xs font-medium text-amber-700">Em andamento</span>
                  <button className="rounded p-1 text-gray-400 hover:bg-gray-100">
                    <MoreVertical size={12} />
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50">
                <div className="flex-1">
                  <h4 className="text-xs font-medium text-gray-900">Ata 29</h4>
                  <p className="text-xs text-gray-500 mt-0.5">15/01/2025 - Escritório</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="rounded-md bg-green-100 px-2 py-1 text-xs font-medium text-green-700">Finalizada</span>
                  <button className="rounded p-1 text-gray-400 hover:bg-gray-100">
                    <MoreVertical size={12} />
                  </button>
                </div>
              </div>
            </div>
          </div>
          <p className="mt-2 text-center text-xs text-muted-foreground">Exemplo ilustrativo da tela de projeto</p>
        </section>

        {/* Botões de ação */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Ações disponíveis</h2>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-border p-5">
              <div className="mb-3 flex items-center gap-2">
                <span className="flex size-8 items-center justify-center rounded-lg bg-primary/10 text-primary">+</span>
                <h4 className="font-semibold text-foreground">Nova Ata</h4>
              </div>
              <p className="mb-3 text-sm text-muted-foreground">Menu dropdown com duas opções:</p>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li>• <strong className="text-foreground">Ata em branco</strong>, cria ata do zero</li>
                <li>• <strong className="text-foreground">Clonar última ata</strong>, traz tópicos e itens pendentes</li>
              </ul>
            </div>
            <div className="rounded-lg border border-border p-5">
              <div className="mb-3 flex items-center gap-2">
                <span className="flex size-8 items-center justify-center rounded-lg bg-primary/10">📊</span>
                <h4 className="font-semibold text-foreground">Mapa de Atas</h4>
              </div>
              <p className="text-sm text-muted-foreground">Visualização consolidada de todos os itens e em quais atas aparecem. Ideal para acompanhamento geral.</p>
            </div>
            <div className="rounded-lg border border-border p-5">
              <div className="mb-3 flex items-center gap-2">
                <span className="flex size-8 items-center justify-center rounded-lg bg-primary/10">📚</span>
                <h4 className="font-semibold text-foreground">Exportar todas as atas</h4>
              </div>
              <p className="text-sm text-muted-foreground">Gera um PDF único compilando todas as atas do projeto em ordem cronológica.</p>
            </div>
            <div className="rounded-lg border border-border p-5">
              <div className="mb-3 flex items-center gap-2">
                <span className="flex size-8 items-center justify-center rounded-lg bg-primary/10">🔍</span>
                <h4 className="font-semibold text-foreground">Busca e filtros</h4>
              </div>
              <p className="text-sm text-muted-foreground">Busque por número, nome ou local. Filtre por status (Em andamento ou Finalizadas).</p>
            </div>
          </div>
        </section>

        {/* Menu de ações por ata */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Menu de ações por ata</h2>
          <p className="mb-4 text-muted-foreground">Cada ata na lista tem um menu de ações (⋮) com as seguintes opções:</p>
          <div className="flex items-start gap-4">
            <div className="shrink-0 overflow-hidden rounded-lg border border-border bg-background shadow-lg">
              <div className="flex h-9 items-center gap-2 border-b border-border px-3 hover:bg-muted">
                <Share2 className="size-4 text-muted-foreground" />
                <span className="text-sm text-foreground">Compartilhar</span>
              </div>
              <div className="flex h-9 items-center gap-2 border-b border-border px-3 hover:bg-muted">
                <Copy className="size-4 text-muted-foreground" />
                <span className="text-sm text-foreground">Clonar</span>
              </div>
              <div className="flex h-9 items-center gap-2 border-b border-border px-3 hover:bg-muted">
                <Edit3 className="size-4 text-muted-foreground" />
                <span className="text-sm text-foreground">Editar</span>
              </div>
              <div className="flex h-9 items-center gap-2 border-b border-border px-3 hover:bg-muted">
                <Eye className="size-4 text-muted-foreground" />
                <span className="text-sm text-foreground">Visualizar</span>
              </div>
              <div className="flex h-9 items-center gap-2 px-3 text-destructive hover:bg-destructive/10">
                <Trash2 className="size-4" />
                <span className="text-sm">Excluir</span>
              </div>
            </div>
            <div className="flex flex-col">
              <div className="flex h-9 items-center gap-2 border-b border-transparent">
                <span className="text-sm text-primary">→</span>
                <span className="text-sm text-muted-foreground">Envia link de acesso para outros usuários</span>
              </div>
              <div className="flex h-9 items-center gap-2 border-b border-transparent">
                <span className="text-sm text-primary">→</span>
                <span className="text-sm text-muted-foreground">Cria nova ata a partir desta</span>
              </div>
              <div className="flex h-9 items-center gap-2 border-b border-transparent">
                <span className="text-sm text-primary">→</span>
                <span className="text-sm text-muted-foreground">Abre o editor (bloqueado se finalizada)</span>
              </div>
              <div className="flex h-9 items-center gap-2 border-b border-transparent">
                <span className="text-sm text-primary">→</span>
                <span className="text-sm text-muted-foreground">Abre preview para impressão/PDF</span>
              </div>
              <div className="flex h-9 items-center gap-2">
                <span className="text-sm text-destructive">→</span>
                <span className="text-sm text-destructive/80">Remove a ata permanentemente</span>
              </div>
            </div>
          </div>
        </section>

        {/* Status das atas */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Status das atas</h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="p-3 text-left font-semibold text-foreground">Status</th>
                  <th className="p-3 text-left font-semibold text-foreground">Descrição</th>
                  <th className="p-3 text-left font-semibold text-foreground">Pode editar?</th>
                </tr>
              </thead>
              <tbody className="text-muted-foreground">
                <tr className="border-b border-border">
                  <td className="p-3"><span className="rounded-md bg-yellow-100 px-1.5 py-0.5 text-xs font-medium text-yellow-700">Em andamento</span></td>
                  <td className="p-3">Ata ainda está sendo preenchida</td>
                  <td className="p-3">Sim</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3"><span className="rounded-md bg-green-100 px-1.5 py-0.5 text-xs font-medium text-green-700">Finalizada</span></td>
                  <td className="p-3">Ata foi concluída e bloqueada para edição</td>
                  <td className="p-3">Não</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="rounded-lg border border-yellow-200 bg-yellow-50 p-6">
          <h3 className="mb-3 text-lg font-semibold text-yellow-800">Atas finalizadas são bloqueadas</h3>
          <p className="text-sm text-yellow-700">Após finalizar uma ata, ela não pode mais ser editada. Isso garante a integridade do registro. Se precisar fazer alterações, crie uma nova ata clonando a anterior.</p>
        </section>
      </div>
    </DocsLayout>
  );
}

// Página: Atas de reunião
function AtasDeReuniaoPage() {
  return (
    <DocsLayout
      title="Atas de reunião"
      description="Estrutura e funcionamento das atas"
      keywords="atas de reunião, estrutura ata digital, como funciona ata, ata reunião obra, documentar reunião"
      previousPage={{ title: 'Projetos', slug: 'projetos' }}
      nextPage={{ title: 'Tópicos e itens', slug: 'topicos-e-itens' }}
    >
      <div className="space-y-8">
        {/* Introdução */}
        <section>
          <p className="text-lg leading-relaxed text-muted-foreground">
            Cada ata representa uma reunião semanal documentada. Ela contém data, local, participantes,
            tópicos e itens de acompanhamento.
          </p>
        </section>

        {/* Para que serve */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Para que serve?</h2>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-border p-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="flex size-8 items-center justify-center rounded-lg bg-blue-100">
                  <ClipboardCheck size={16} className="text-blue-600" />
                </div>
                <h4 className="font-semibold text-foreground">Registro formal</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Documenta oficialmente o que foi discutido e decidido em cada reunião.
              </p>
            </div>
            <div className="rounded-lg border border-border p-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="flex size-8 items-center justify-center rounded-lg bg-green-100">
                  <FileText size={16} className="text-green-600" />
                </div>
                <h4 className="font-semibold text-foreground">Rastreabilidade</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Permite consultar quando cada decisão foi tomada e quem estava presente.
              </p>
            </div>
            <div className="rounded-lg border border-border p-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="flex size-8 items-center justify-center rounded-lg bg-orange-100">
                  <Clock size={16} className="text-orange-600" />
                </div>
                <h4 className="font-semibold text-foreground">Continuidade</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Ao clonar, os itens pendentes vêm automaticamente para a próxima reunião.
              </p>
            </div>
            <div className="rounded-lg border border-border p-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="flex size-8 items-center justify-center rounded-lg bg-purple-100">
                  <Share2 size={16} className="text-purple-600" />
                </div>
                <h4 className="font-semibold text-foreground">Distribuição</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Exporte em PDF para enviar a todos os participantes e interessados.
              </p>
            </div>
          </div>
        </section>

        {/* Criando uma ata */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Criando uma ata</h2>
          <p className="mb-4 text-muted-foreground">Você tem duas opções ao criar uma nova ata:</p>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-border p-5">
              <div className="mb-3 flex items-center gap-2">
                <FileText className="size-5 text-muted-foreground" />
                <h4 className="font-semibold text-foreground">Nova ata em branco</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Use para a primeira ata do projeto. Você precisará adicionar participantes e criar os tópicos do zero.
              </p>
            </div>
            <div className="rounded-lg borderborder-primary/30 bg-primary/5 p-5">
              <div className="mb-3 flex items-center gap-2">
                <Copy className="size-5 text-primary" />
                <h4 className="font-semibold text-foreground">Clonar ata anterior</h4>
                <span className="rounded bg-primary/20 px-1.5 py-0.5 text-xs font-medium text-primary">Recomendado</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Traz automaticamente os itens pendentes, participantes e tópicos. Ideal para continuidade.
              </p>
            </div>
          </div>
        </section>

        {/* O que acontece ao clonar */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">O que acontece ao clonar</h2>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-lg border border-border bg-muted/30 p-4">
              <div className="mb-3 text-center">
                <span className="rounded bg-muted px-2 py-1 text-xs font-medium text-muted-foreground">Ata 29 (anterior)</span>
              </div>
              <div className="space-y-2 text-xs">
                <div className="flex items-center gap-2 rounded bg-background p-2">
                  <span className="size-2 rounded-full bg-yellow-500" />
                  <span className="flex-1 text-foreground">Item pendente A</span>
                </div>
                <div className="flex items-center gap-2 rounded bg-background p-2">
                  <span className="size-2 rounded-full bg-yellow-500" />
                  <span className="flex-1 text-foreground">Item pendente B</span>
                </div>
                <div className="flex items-center gap-2 rounded bg-green-50 p-2 opacity-60">
                  <span className="size-2 rounded-full bg-green-500" />
                  <span className="flex-1 line-through text-muted-foreground">Item resolvido</span>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-center">
              <div className="flex flex-col items-center gap-1">
                <ArrowRight className="size-6 text-primary" />
                <span className="rounded bg-primary/10 px-2 py-1 text-xs font-medium text-primary">Clonar</span>
              </div>
            </div>

            <div className="rounded-lg borderborder-primary/30 bg-primary/5 p-4">
              <div className="mb-3 text-center">
                <span className="rounded bg-primary/20 px-2 py-1 text-xs font-medium text-primary">Ata 30 (nova)</span>
              </div>
              <div className="space-y-2 text-xs">
                <div className="flex items-center gap-2 rounded border border-primary/20 bg-background p-2">
                  <span className="size-2 rounded-full bg-yellow-500" />
                  <span className="flex-1 text-foreground">Item pendente A</span>
                  <span className="rounded bg-primary/20 px-1 text-xs text-primary">clonado</span>
                </div>
                <div className="flex items-center gap-2 rounded border border-primary/20 bg-background p-2">
                  <span className="size-2 rounded-full bg-yellow-500" />
                  <span className="flex-1 text-foreground">Item pendente B</span>
                  <span className="rounded bg-primary/20 px-1 text-xs text-primary">clonado</span>
                </div>
                <div className="rounded border border-dashed border-border bg-background p-2 text-center text-muted-foreground">
                  Item resolvido não vem
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Estrutura da ata */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Estrutura da ata</h2>
          <p className="mb-4 text-muted-foreground">Uma ata é composta por:</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="flex items-start gap-3 rounded-lg border border-border p-4">
              <Calendar className="size-5 shrink-0 text-primary" />
              <div>
                <h4 className="font-semibold text-foreground">Data e local</h4>
                <p className="text-sm text-muted-foreground">Quando e onde aconteceu a reunião</p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-border p-4">
              <Users className="size-5 shrink-0 text-primary" />
              <div>
                <h4 className="font-semibold text-foreground">Participantes</h4>
                <p className="text-sm text-muted-foreground">Quem estava presente na reunião</p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-border p-4">
              <Layers className="size-5 shrink-0 text-primary" />
              <div>
                <h4 className="font-semibold text-foreground">Tópicos</h4>
                <p className="text-sm text-muted-foreground">Seções que agrupam os itens (ex: Elétrica)</p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-border p-4">
              <FileText className="size-5 shrink-0 text-primary" />
              <div>
                <h4 className="font-semibold text-foreground">Itens</h4>
                <p className="text-sm text-muted-foreground">Pendências com responsável e prazo</p>
              </div>
            </div>
          </div>
          <p className="mt-4 text-sm text-muted-foreground">
            Saiba mais sobre tópicos e itens em <Link to="/docs/topicos-e-itens" className="font-medium text-primary hover:underline">Tópicos e itens</Link>.
          </p>
        </section>

        {/* Visualização de uma ata */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Visualização de uma ata</h2>
          <div className="overflow-hidden rounded-lg border border-border bg-white shadow-sm">
            {/* Header da ata */}
            <div className="border-b border-border bg-white px-3 py-2">
              <div className="flex items-center gap-2">
                <button className="p-1.5 hover:bg-gray-100 rounded-md">
                  <ArrowLeft size={14} className="text-gray-600" />
                </button>
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-semibold text-gray-900">Ata 30</span>
                  <span className="text-gray-400">•</span>
                  <span className="text-xs text-gray-600">Obra Shopping Norte</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-gray-500 ml-3">
                  <Calendar className="size-3" />
                  <span>22 jan. 2025</span>
                  <span className="text-gray-300">•</span>
                  <MapPin className="size-3" />
                  <span>Videoconferência</span>
                </div>
                <span className="ml-auto rounded-md bg-amber-100 px-2 py-1 text-xs font-medium text-amber-700">Em andamento</span>
              </div>
            </div>

            {/* Conteúdo da ata */}
            <div className="p-4 bg-gray-50">
              <div className="mb-4">
                <h3 className="mb-2 text-xs font-semibold text-primary">Instalações Elétricas</h3>
                <div className="overflow-x-auto bg-white rounded-md border border-gray-200">
                  <table className="w-full border-collapse text-xs">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-2 py-1.5 text-left font-semibold border-b border-gray-200" style={{ width: '40px' }}>ITEM</th>
                        <th className="px-2 py-1.5 text-left font-semibold border-b border-gray-200" style={{ width: '50px' }}>Nº ATA</th>
                        <th className="px-2 py-1.5 text-left font-semibold border-b border-gray-200">DESCRIÇÃO</th>
                        <th className="px-2 py-1.5 text-left font-semibold border-b border-gray-200" style={{ width: '80px' }}>RESP.</th>
                        <th className="px-2 py-1.5 text-left font-semibold border-b border-gray-200" style={{ width: '60px' }}>PRAZO</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="hover:bg-gray-50 cursor-pointer">
                        <td className="px-2 py-1.5 border-b border-gray-200 font-mono text-gray-600">1.1</td>
                        <td className="px-2 py-1.5 border-b border-gray-200">
                          <span className="inline-flex items-center rounded-md bg-blue-50 px-1.5 py-0.5 text-xs font-medium text-blue-700">28</span>
                        </td>
                        <td className="px-2 py-1.5 border-b border-gray-200">
                          <span className="text-gray-900">Substituir disjuntor do quadro QD-02</span>
                          <div className="mt-1.5 rounded border border-border bg-muted/30 p-1.5 text-xs">
                            <div className="mb-0.5 flex items-center gap-1.5">
                              <span className="rounded bg-blue-100 px-1 py-0.5 text-xs font-bold text-blue-700">NOVO</span>
                              <span className="font-medium text-primary">A30 |</span>
                            </div>
                            <p className="text-gray-600">"Disjuntor adquirido. Troca dia 12/02."</p>
                          </div>
                        </td>
                        <td className="px-2 py-1.5 border-b border-gray-200 text-gray-600">Eng. Carlos</td>
                        <td className="px-2 py-1.5 border-b border-gray-200 text-gray-600">15/02</td>
                      </tr>
                      <tr className="hover:bg-gray-50 bg-green-50/50">
                        <td className="px-2 py-1.5 border-b border-gray-200 font-mono text-gray-600">1.2</td>
                        <td className="px-2 py-1.5 border-b border-gray-200">
                          <span className="inline-flex items-center rounded-md bg-blue-50 px-1.5 py-0.5 text-xs font-medium text-blue-700">30</span>
                        </td>
                        <td className="px-2 py-1.5 border-b border-gray-200">
                          <span className="text-gray-900 line-through opacity-60">Teste de aterramento geral</span>
                          <span className="ml-1.5 inline-flex items-center rounded-md bg-green-100 px-1.5 py-0.5 text-xs font-medium text-green-700">Resolvido</span>
                        </td>
                        <td className="px-2 py-1.5 border-b border-gray-200 text-gray-600">Téc. João</td>
                        <td className="px-2 py-1.5 border-b border-gray-200 text-green-600">22/01</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          <p className="mt-2 text-center text-xs text-muted-foreground">Clique em um item para abrir o painel de edição</p>
        </section>

        {/* Participantes */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Participantes</h2>
          <p className="mb-4 text-muted-foreground">
            Clique em "Participantes" no cabeçalho para abrir o painel lateral. Três formas de adicionar:
          </p>
          <div className="grid gap-3 md:grid-cols-3">
            <div className="flex items-start gap-3 rounded-lg border border-border p-4">
              <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/10">
                <Edit3 className="size-4 text-primary" />
              </div>
              <div>
                <h4 className="font-semibold text-foreground">Criar novo</h4>
                <p className="text-xs text-muted-foreground">
                  Preencha nome, empresa, cargo e email. A empresa vem de <Link to="/docs/personalizacao" className="text-primary hover:underline">Personalização</Link>.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-border p-4">
              <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-amber-500/10">
                <Clock className="size-4 text-amber-600" />
              </div>
              <div>
                <h4 className="font-semibold text-foreground">Do histórico</h4>
                <p className="text-xs text-muted-foreground">Pessoas que já participaram antes. Um clique adiciona.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-border p-4">
              <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-emerald-500/10">
                <UserPlus className="size-4 text-emerald-600" />
              </div>
              <div>
                <h4 className="font-semibold text-foreground">Colaboradores</h4>
                <p className="text-xs text-muted-foreground">Membros com acesso ao projeto.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Callouts */}
        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-lg border border-border bg-muted/50 p-5">
            <h3 className="mb-2 font-semibold text-foreground">Salvamento automático</h3>
            <p className="text-sm text-muted-foreground">Alterações são salvas automaticamente. Pode fechar a página sem perder trabalho.</p>
          </div>
          <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-5">
            <h3 className="mb-2 font-semibold text-yellow-800">Atas finalizadas</h3>
            <p className="text-sm text-yellow-700">Após finalizar, a ata não pode mais ser editada.</p>
          </div>
        </div>
      </div>
    </DocsLayout>
  );
}

// Página: Tópicos e itens
function TopicosEItensPage() {
  return (
    <DocsLayout
      title="Tópicos e itens"
      description="Como organizar os assuntos e pendências da reunião"
      keywords="tópicos ata reunião, itens pendências, organizar assuntos reunião, pendências obra, registro decisões"
      previousPage={{ title: 'Atas de reunião', slug: 'atas-de-reuniao' }}
      nextPage={{ title: 'Desenvolvimentos', slug: 'desenvolvimentos' }}
    >
      <div className="space-y-8">
        <section>
          <p className="mb-6 text-lg leading-relaxed text-muted-foreground">
            Tópicos organizam os assuntos por área (ex: Elétrica, Hidráulica), enquanto itens
            registram as pendências específicas que precisam ser acompanhadas dentro de cada tópico.
          </p>
        </section>

        {/* Para que serve */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Para que serve?</h2>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-border p-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="flex size-8 items-center justify-center rounded-lg bg-blue-100">
                  <Layers size={16} className="text-blue-600" />
                </div>
                <h4 className="font-semibold text-foreground">Organização por área</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Agrupe pendências relacionadas em seções temáticas (Elétrica, Hidráulica, etc).
              </p>
            </div>
            <div className="rounded-lg border border-border p-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="flex size-8 items-center justify-center rounded-lg bg-green-100">
                  <CheckCircle size={16} className="text-green-600" />
                </div>
                <h4 className="font-semibold text-foreground">Acompanhamento de pendências</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Cada item tem responsável, prazo e status para facilitar o controle.
              </p>
            </div>
            <div className="rounded-lg border border-border p-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="flex size-8 items-center justify-center rounded-lg bg-orange-100">
                  <Clock size={16} className="text-orange-600" />
                </div>
                <h4 className="font-semibold text-foreground">Histórico de evolução</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Desenvolvimentos registram o progresso semana a semana.
              </p>
            </div>
            <div className="rounded-lg border border-border p-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="flex size-8 items-center justify-center rounded-lg bg-purple-100">
                  <Copy size={16} className="text-purple-600" />
                </div>
                <h4 className="font-semibold text-foreground">Continuidade automática</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Itens pendentes são clonados automaticamente para a próxima ata.
              </p>
            </div>
          </div>
        </section>

        {/* Hierarquia visual */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Hierarquia de organização</h2>
          <p className="mb-4 text-muted-foreground">O sistema se organiza em três níveis:</p>
          <div className="rounded-xl border border-border bg-white p-6">
            <div className="flex flex-col gap-2">
              {/* Nível 1 - Tópico */}
              <div className="rounded-lg border border-gray-200 bg-gray-50/50 px-4 py-3">
                <div className="flex items-center gap-3">
                  <span className="flex size-6 items-center justify-center rounded-md bg-gray-900 text-xs font-semibold text-white">1</span>
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold text-gray-900">Tópico</h4>
                    <span className="text-sm text-gray-500">— Área ou assunto geral</span>
                  </div>
                </div>
                <p className="mt-1.5 ml-9 text-sm text-gray-600">ex: "Instalações Elétricas", "Cronograma"</p>
              </div>

              {/* Conector */}
              <div className="ml-7 flex h-4 items-center">
                <div className="h-full w-px bg-gray-300" />
              </div>

              {/* Nível 2 - Item */}
              <div className="ml-4 rounded-lg border border-blue-200 bg-blue-50/50 px-4 py-3">
                <div className="flex items-center gap-3">
                  <span className="flex size-6 items-center justify-center rounded-md bg-blue-600 text-xs font-semibold text-white">2</span>
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold text-blue-900">Item</h4>
                    <span className="text-sm text-blue-600/70">— Pendência específica</span>
                  </div>
                </div>
                <p className="mt-1.5 ml-9 text-sm text-gray-600">Tem responsável, prazo e status. ex: "Substituir disjuntor QD-02"</p>
              </div>

              {/* Conector */}
              <div className="ml-11 flex h-4 items-center">
                <div className="h-full w-px bg-gray-300" />
              </div>

              {/* Nível 3 - Desenvolvimento */}
              <div className="ml-8 rounded-lg border border-emerald-200 bg-emerald-50/50 px-4 py-3">
                <div className="flex items-center gap-3">
                  <span className="flex size-6 items-center justify-center rounded-md bg-emerald-600 text-xs font-semibold text-white">3</span>
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold text-emerald-900">Desenvolvimento</h4>
                    <span className="text-sm text-emerald-600/70">— Atualização semanal</span>
                  </div>
                </div>
                <p className="mt-1.5 ml-9 text-sm text-gray-600">ex: "Material entregue, instalação em andamento"</p>
              </div>
            </div>
          </div>
          <p className="mt-3 text-sm text-muted-foreground">
            Um tópico contém vários itens. Cada item pode ter vários desenvolvimentos ao longo das reuniões.
          </p>
        </section>

        {/* Como criar um tópico */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Criando um tópico</h2>
          <div className="space-y-4">
            <div className="flex items-start gap-4 rounded-lg border border-border p-4">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">1</span>
              <div>
                <h4 className="font-semibold text-foreground">Acesse a ata</h4>
                <p className="mt-1 text-sm text-muted-foreground">Abra a ata onde deseja adicionar o tópico.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 rounded-lg border border-border p-4">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">2</span>
              <div>
                <h4 className="font-semibold text-foreground">Clique em "Novo Tópico"</h4>
                <p className="mt-1 text-sm text-muted-foreground">No final da lista de tópicos ou no botão de ação.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 rounded-lg border border-border p-4">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">3</span>
              <div>
                <h4 className="font-semibold text-foreground">Defina o nome do tópico</h4>
                <p className="mt-1 text-sm text-muted-foreground">Use nomes claros como "Instalações Elétricas" ou "Cronograma".</p>
              </div>
            </div>
            <div className="flex items-start gap-4 rounded-lg border border-border p-4">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">4</span>
              <div>
                <h4 className="font-semibold text-foreground">Adicione itens ao tópico</h4>
                <p className="mt-1 text-sm text-muted-foreground">Use o botão "Novo item" para criar pendências dentro do tópico.</p>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Exemplos de tópicos</h2>
          <p className="mb-4 text-muted-foreground">Tópicos são as seções da ata que agrupam itens relacionados.</p>
          <div className="rounded-lg border border-border bg-muted/50 p-5">
            <p className="mb-3 text-sm font-medium text-foreground">Exemplos:</p>
            <div className="grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
              <div>• Instalações Elétricas</div>
              <div>• Instalações Hidráulicas</div>
              <div>• Estrutura</div>
              <div>• Cronograma</div>
              <div>• Segurança do Trabalho</div>
              <div>• Pendências Administrativas</div>
            </div>
          </div>
        </section>

        {/* Mockup visual de tópico com itens */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Visualização de um tópico</h2>
          <div className="overflow-hidden rounded-lg border border-border bg-white">
            {/* Header do tópico */}
            <div className="flex items-center justify-between border-b border-border bg-gray-50 px-6 py-4">
              <h3 className="text-lg font-semibold text-primary">Instalações Elétricas</h3>
              <button className="flex items-center gap-1.5 rounded-md text-sm font-medium text-primary">
                <Plus size={16} /> Novo item
              </button>
            </div>

            {/* Tabela de itens */}
            <div className="overflow-x-auto bg-white">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left font-semibold border-b border-gray-200" style={{ width: '60px' }}>ITEM</th>
                    <th className="px-4 py-3 text-left font-semibold border-b border-gray-200" style={{ width: '80px' }}>Nº ATA</th>
                    <th className="px-4 py-3 text-left font-semibold border-b border-gray-200">DESCRIÇÃO</th>
                    <th className="px-4 py-3 text-left font-semibold border-b border-gray-200" style={{ width: '120px' }}>RESPONSÁVEL</th>
                    <th className="px-4 py-3 text-left font-semibold border-b border-gray-200" style={{ width: '100px' }}>PRAZO</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 border-b border-gray-200 font-mono text-gray-600">1.1</td>
                    <td className="px-4 py-3 border-b border-gray-200">
                      <span className="inline-flex items-center rounded bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">28</span>
                    </td>
                    <td className="px-4 py-3 border-b border-gray-200">
                      <span className="text-gray-900">Substituir disjuntor do quadro QD-02</span>
                      <div className="mt-2 space-y-1">
                        <div className="rounded border border-border bg-muted/30 p-2 text-xs">
                          <div className="mb-1 flex items-center gap-2">
                            <span className="rounded bg-blue-100 px-1.5 py-0.5 text-xs font-bold text-blue-700">NOVO</span>
                            <span className="font-medium text-primary">A30 |</span>
                          </div>
                          <p className="text-gray-600">"Disjuntor já foi adquirido. Troca programada para dia 12/02."</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 border-b border-gray-200 text-gray-600">Eng. Carlos</td>
                    <td className="px-4 py-3 border-b border-gray-200 text-gray-600">15/02/2025</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 border-b border-gray-200 font-mono text-gray-600">1.2</td>
                    <td className="px-4 py-3 border-b border-gray-200">
                      <span className="inline-flex items-center rounded bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">30</span>
                    </td>
                    <td className="px-4 py-3 border-b border-gray-200 text-gray-900">Teste de aterramento geral</td>
                    <td className="px-4 py-3 border-b border-gray-200 text-gray-600">Técnico João</td>
                    <td className="px-4 py-3 border-b border-gray-200 text-gray-600">20/02/2025</td>
                  </tr>
                  <tr className="hover:bg-gray-50 bg-green-50/50">
                    <td className="px-4 py-3 border-b border-gray-200 font-mono text-gray-600">1.3</td>
                    <td className="px-4 py-3 border-b border-gray-200">
                      <span className="inline-flex items-center rounded bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">27</span>
                    </td>
                    <td className="px-4 py-3 border-b border-gray-200">
                      <span className="text-gray-900 line-through opacity-60">Revisar fiação do 3º andar</span>
                      <span className="ml-2 inline-flex items-center rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">Resolvido</span>
                    </td>
                    <td className="px-4 py-3 border-b border-gray-200 text-gray-600">Eng. Carlos</td>
                    <td className="px-4 py-3 border-b border-gray-200 text-green-600">10/01/2025</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <p className="mt-2 text-center text-xs text-muted-foreground">Exemplo ilustrativo de um tópico com itens</p>
        </section>

        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Campos de um item</h2>
          <p className="mb-4 text-muted-foreground">Cada item contém:</p>
          <ul className="space-y-2 text-muted-foreground">
            <li className="flex items-start gap-2"><span className="text-primary">•</span><strong className="text-foreground">ITEM:</strong> Numeração do item dentro do tópico (ex: 1.1, 1.2, 1.3)</li>
            <li className="flex items-start gap-2"><span className="text-primary">•</span><strong className="text-foreground">Nº ATA:</strong> Número da ata onde o item foi criado originalmente</li>
            <li className="flex items-start gap-2"><span className="text-primary">•</span><strong className="text-foreground">Descrição:</strong> O que precisa ser feito</li>
            <li className="flex items-start gap-2"><span className="text-primary">•</span><strong className="text-foreground">Responsável:</strong> Quem deve executar</li>
            <li className="flex items-start gap-2"><span className="text-primary">•</span><strong className="text-foreground">Prazo:</strong> Data limite</li>
            <li className="flex items-start gap-2"><span className="text-primary">•</span><strong className="text-foreground">Status:</strong> Pendente ou Resolvido</li>
          </ul>
        </section>

        {/* Resolvendo um item */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Resolvendo um item</h2>
          <p className="mb-4 text-muted-foreground">
            Quando uma pendência é concluída, você pode marcá-la como <strong className="text-foreground">Resolvida</strong>.
            Isso ajuda a manter a ata organizada e a distinguir o que já foi feito do que ainda está pendente.
          </p>

          <div className="grid gap-6 md:grid-cols-2">
            {/* Como resolver */}
            <div className="rounded-lg border border-border p-5">
              <h3 className="mb-3 font-semibold text-foreground">Como resolver um item</h3>
              <ol className="space-y-3 text-sm text-muted-foreground">
                <li className="flex items-start gap-3">
                  <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">1</span>
                  <span>Clique no item para abrir o painel de edição</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">2</span>
                  <span>Adicione um desenvolvimento final explicando a conclusão</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">3</span>
                  <span>Clique no botão <strong>"Resolver"</strong> no rodapé do painel</span>
                </li>
              </ol>
            </div>

            {/* O que acontece */}
            <div className="rounded-lg border border-border p-5">
              <h3 className="mb-3 font-semibold text-foreground">O que acontece visualmente</h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <CheckCircle className="size-5 shrink-0 text-green-600" />
                  <span className="text-sm text-muted-foreground">A descrição fica <span className="line-through opacity-60">riscada</span></span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="inline-flex items-center rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">Resolvido</span>
                  <span className="text-sm text-muted-foreground">Badge verde aparece ao lado</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="size-5 shrink-0 rounded bg-green-50/50" />
                  <span className="text-sm text-muted-foreground">Linha ganha fundo verde claro</span>
                </div>
                <div className="flex items-start gap-3">
                  <Calendar className="size-5 shrink-0 text-green-600" />
                  <span className="text-sm text-muted-foreground">Prazo fica em verde (foi cumprido)</span>
                </div>
              </div>
            </div>
          </div>

          {/* Regra importante */}
          <div className="mt-6 rounded-lg borderborder-amber-200 bg-amber-50 p-5">
            <div className="flex items-start gap-3">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-amber-100">
                <Lightbulb className="size-5 text-amber-600" />
              </div>
              <div>
                <h4 className="font-semibold text-foreground">Regra importante: Itens resolvidos não são clonados</h4>
                <p className="mt-2 text-sm text-muted-foreground">
                  Ao usar <strong>"Clonar última ata"</strong> para criar uma nova reunião, apenas os itens <strong>pendentes</strong> são copiados.
                  Itens marcados como resolvidos ficam apenas na ata onde foram concluídos.
                </p>
                <div className="mt-3 rounded-lg border border-amber-200 bg-white p-3">
                  <p className="text-xs text-muted-foreground">
                    <strong className="text-foreground">Exemplo:</strong> A Ata 29 tem 5 itens (3 pendentes, 2 resolvidos).
                    Ao clonar para criar a Ata 30, apenas os 3 pendentes serão copiados.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Explicação do Nº ATA */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Entendendo o Nº ATA</h2>
          <div className="rounded-lg borderborder-primary/20 bg-primary/5 p-5">
            <div className="mb-4 flex items-start gap-3">
              <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-blue-100">
                <span className="text-sm font-bold text-blue-700">28</span>
              </span>
              <div>
                <h4 className="font-semibold text-foreground">O que significa?</h4>
                <p className="mt-1 text-sm text-muted-foreground">
                  O <strong className="text-primary">Nº ATA</strong> indica em qual ata o item foi <strong>criado originalmente</strong>.
                  Este número nunca muda, mesmo que o item apareça em várias atas seguintes.
                </p>
              </div>
            </div>
            <div className="rounded-lg border border-border bg-white p-4">
              <p className="mb-3 text-sm font-medium text-foreground">Exemplo prático:</p>
              <div className="space-y-2 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <span className="rounded bg-blue-100 px-2 py-0.5 text-xs font-bold text-blue-700">28</span>
                  <span>→ Item criado na Ata 28</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Ata 29:</span>
                  <span>Item aparece (pendente), continua mostrando <span className="rounded bg-blue-100 px-1.5 py-0.5 text-xs font-bold text-blue-700">28</span></span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Ata 30:</span>
                  <span>Item ainda pendente, continua mostrando <span className="rounded bg-blue-100 px-1.5 py-0.5 text-xs font-bold text-blue-700">28</span></span>
                </div>
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                Isso permite rastrear há quanto tempo um item está em aberto.
              </p>
            </div>
          </div>
        </section>

        {/* Drawer de edição de item */}
        <section>
          <h2 className="mb-2 text-2xl font-semibold text-foreground">Editando um item</h2>
          <p className="mb-6 text-muted-foreground">
            Ao clicar em qualquer item da tabela, um painel lateral (drawer) abre à direita da tela.
            Nele você pode editar todas as informações do item e acompanhar seu histórico de desenvolvimentos.
          </p>

          <div className="grid gap-6 lg:grid-cols-2">
            {/* Mockup do drawer */}
            <div className="order-2 lg:order-1">
              <div className="overflow-hidden rounded-lg border border-border bg-white shadow-lg">
                {/* Header do drawer */}
                <div className="flex items-center justify-between border-b border-gray-200 px-4 py-4">
                  <h3 className="font-semibold text-gray-900">Editar Item</h3>
                  <button className="rounded p-1 text-gray-600 hover:bg-gray-100">
                    <X size={20} />
                  </button>
                </div>

                {/* Conteúdo do drawer */}
                <div className="space-y-5 p-5 pl-8">
                  {/* Descrição */}
                  <div className="relative">
                    <div className="absolute -left-6 top-0 flex size-5 items-center justify-center rounded-full bg-primary text-xs font-bold text-white">1</div>
                    <label className="mb-1.5 block text-sm font-medium text-gray-900">Descrição</label>
                    <div className="min-h-[80px] rounded border border-gray-200 px-3 py-2 text-sm text-gray-900">
                      Substituir disjuntor do quadro QD-02
                    </div>
                  </div>

                  {/* Responsável */}
                  <div className="relative">
                    <div className="absolute -left-6 top-0 flex size-5 items-center justify-center rounded-full bg-primary text-xs font-bold text-white">2</div>
                    <label className="mb-1.5 block text-sm font-medium text-gray-900">Responsável</label>
                    <div className="flex h-9 items-center justify-between rounded border border-gray-200 bg-white px-3 text-sm text-gray-900">
                      <span>Eng. Carlos</span>
                      <ChevronDown size={16} className="text-gray-400" />
                    </div>
                  </div>

                  {/* Prazo */}
                  <div className="relative">
                    <div className="absolute -left-6 top-0 flex size-5 items-center justify-center rounded-full bg-primary text-xs font-bold text-white">3</div>
                    <label className="mb-1.5 block text-sm font-medium text-gray-900">Prazo</label>
                    <div className="flex h-9 items-center rounded border border-gray-200 bg-white px-3 text-sm text-gray-900">
                      2025-02-15
                    </div>
                  </div>

                  {/* Seção de desenvolvimentos */}
                  <div className="relative">
                    <div className="absolute -left-6 top-0 flex size-5 items-center justify-center rounded-full bg-emerald-500 text-xs font-bold text-white">4</div>
                    <label className="mb-1.5 block text-sm font-medium text-gray-900">Desenvolvimentos</label>
                    <div className="mb-2 space-y-2">
                      <div className="rounded border border-gray-200 bg-gray-50 p-2.5">
                        <div className="mb-1 flex items-center gap-2 text-xs font-semibold">
                          <span className="rounded bg-red-100 px-1.5 py-0.5 text-xs font-bold text-red-700">NOVO</span>
                          <span className="text-primary">A30 |</span>
                        </div>
                        <p className="text-sm font-medium text-primary">
                          Disjuntor adquirido. Troca dia 12/02.
                        </p>
                      </div>
                      <div className="rounded border border-gray-200 bg-gray-50 p-2.5">
                        <div className="mb-1 text-xs font-semibold text-gray-500">
                          A29 |
                        </div>
                        <p className="text-sm text-gray-500">
                          Aguardando chegada do material
                        </p>
                      </div>
                    </div>
                    {/* Input para novo desenvolvimento */}
                    <div className="flex gap-2">
                      <div className="flex-1 rounded border border-gray-200 px-3 py-1.5 text-sm text-gray-400">
                        Digite o desenvolvimento...
                      </div>
                      <button className="flex items-center gap-1.5 rounded bg-primary px-3 py-1.5 text-sm font-medium text-white">
                        <Plus size={16} />
                        Adicionar
                      </button>
                    </div>
                  </div>
                </div>

                {/* Footer do drawer */}
                <div className="flex gap-2 border-t border-gray-200 bg-white px-4 py-3 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]">
                  <button className="flex-1 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700">
                    Cancelar
                  </button>
                  <button className="flex-1 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white">
                    Salvar
                  </button>
                </div>
              </div>
            </div>

            {/* Explicações dos campos */}
            <div className="order-1 space-y-4 lg:order-2">
              {/* Card 1 - Descrição */}
              <div className="rounded-lg border border-border p-4">
                <div className="mb-2 flex items-center gap-2">
                  <span className="flex size-6 items-center justify-center rounded-full bg-primary text-xs font-bold text-white">1</span>
                  <h4 className="font-semibold text-foreground">Descrição</h4>
                </div>
                <p className="text-sm text-muted-foreground">
                  Texto que descreve a pendência ou tarefa. Seja claro e objetivo para facilitar o entendimento de todos os envolvidos.
                </p>
              </div>

              {/* Card 2 - Responsável */}
              <div className="rounded-lg border border-border p-4">
                <div className="mb-2 flex items-center gap-2">
                  <span className="flex size-6 items-center justify-center rounded-full bg-primary text-xs font-bold text-white">2</span>
                  <h4 className="font-semibold text-foreground">Responsável</h4>
                </div>
                <p className="text-sm text-muted-foreground">
                  Selecione quem é responsável por resolver este item. A lista mostra os <strong>participantes da ata</strong>, adicione-os primeiro se necessário.
                </p>
              </div>

              {/* Card 3 - Prazo */}
              <div className="rounded-lg border border-border p-4">
                <div className="mb-2 flex items-center gap-2">
                  <span className="flex size-6 items-center justify-center rounded-full bg-primary text-xs font-bold text-white">3</span>
                  <h4 className="font-semibold text-foreground">Prazo</h4>
                </div>
                <p className="text-sm text-muted-foreground">
                  Data limite para conclusão. O formato é <code className="rounded bg-muted px-1.5 py-0.5 text-xs">AAAA-MM-DD</code>. Itens vencidos são destacados no Mapa de Atas.
                </p>
              </div>

              {/* Card 4 - Desenvolvimentos */}
              <div className="rounded-lg borderborder-emerald-200 bg-emerald-50 p-4">
                <div className="mb-2 flex items-center gap-2">
                  <span className="flex size-6 items-center justify-center rounded-full bg-emerald-500 text-xs font-bold text-white">4</span>
                  <h4 className="font-semibold text-foreground">Desenvolvimentos</h4>
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  Histórico de atualizações do item. Cada desenvolvimento registra o progresso em uma reunião específica.
                </p>
                <div className="space-y-2 text-xs">
                  <div className="flex items-start gap-2">
                    <span className="rounded bg-red-100 px-1.5 py-0.5 text-xs font-bold text-red-700 shrink-0">NOVO</span>
                    <span className="text-muted-foreground">Indica desenvolvimento da ata atual (mais recente)</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="font-semibold text-primary shrink-0">A30 |</span>
                    <span className="text-muted-foreground">Número da ata onde o desenvolvimento foi registrado</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Plus size={14} className="text-primary shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">Clique para adicionar novo desenvolvimento nesta reunião</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Dica */}
          <div className="mt-6 rounded-lg border border-border bg-muted/50 p-4">
            <div className="flex items-start gap-3">
              <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-amber-100">
                <Lightbulb className="size-4 text-amber-600" />
              </div>
              <div>
                <h4 className="font-semibold text-foreground">Dica: Clique no texto para editar</h4>
                <p className="mt-1 text-sm text-muted-foreground">
                  Os textos dos desenvolvimentos são clicáveis. Clique em qualquer desenvolvimento para editar diretamente.
                  Pressione <kbd className="rounded bg-muted px-1.5 py-0.5 text-xs font-mono">Enter</kbd> para salvar ou <kbd className="rounded bg-muted px-1.5 py-0.5 text-xs font-mono">Esc</kbd> para cancelar.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Link para página de desenvolvimentos */}
        <section className="rounded-lg borderborder-emerald-200 bg-emerald-50 p-5">
          <div className="flex items-start gap-3">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white">3</span>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Desenvolvimentos</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Cada item pode ter vários <strong>desenvolvimentos</strong>, atualizações semanais que registram o progresso.
                É assim que você mantém o histórico completo de cada pendência.
              </p>
              <Link to="/docs/desenvolvimentos" className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-emerald-700 hover:underline">
                Ver detalhes sobre Desenvolvimentos
                <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </section>

        <section className="rounded-lg borderborder-primary/20 bg-primary/5 p-6">
          <h3 className="mb-3 text-lg font-semibold text-foreground">Comportamento dos itens</h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li>• <strong className="text-foreground">Itens pendentes</strong> aparecem automaticamente na próxima ata ao clonar</li>
            <li>• <strong className="text-foreground">Itens resolvidos</strong> deixam de aparecer nas atas seguintes</li>
            <li>• O <strong className="text-foreground">histórico completo</strong> fica disponível no Mapa de Atas</li>
          </ul>
        </section>
      </div>
    </DocsLayout>
  );
}

// Página: Desenvolvimentos
function DesenvolvimentosPage() {
  return (
    <DocsLayout
      title="Desenvolvimentos"
      description="Como funciona o acompanhamento semanal dos itens"
      keywords="desenvolvimentos ata, acompanhamento semanal, histórico pendências, atualização itens, evolução decisões"
      previousPage={{ title: 'Tópicos e itens', slug: 'topicos-e-itens' }}
      nextPage={{ title: 'Participantes', slug: 'participantes' }}
    >
      <div className="space-y-8">
        <section>
          <p className="mb-6 text-lg leading-relaxed text-muted-foreground">
            Um desenvolvimento é uma atualização de status de um item feita durante uma reunião.
            Enquanto o item representa "o que precisa ser feito", o desenvolvimento registra
            "o que aconteceu esta semana". Isso forma um histórico completo de evolução de cada pendência.
          </p>
        </section>

        {/* Para que serve */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Para que serve?</h2>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-border p-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="flex size-8 items-center justify-center rounded-lg bg-blue-100">
                  <Clock size={16} className="text-blue-600" />
                </div>
                <h4 className="font-semibold text-foreground">Registro semanal</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Documenta o que aconteceu com cada pendência entre uma reunião e outra.
              </p>
            </div>
            <div className="rounded-lg border border-border p-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="flex size-8 items-center justify-center rounded-lg bg-green-100">
                  <FileText size={16} className="text-green-600" />
                </div>
                <h4 className="font-semibold text-foreground">Histórico completo</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Mantém a linha do tempo de cada item desde a criação até a resolução.
              </p>
            </div>
            <div className="rounded-lg border border-border p-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="flex size-8 items-center justify-center rounded-lg bg-orange-100">
                  <ClipboardCheck size={16} className="text-orange-600" />
                </div>
                <h4 className="font-semibold text-foreground">Evidência documental</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Serve como prova do que foi discutido e acordado em cada reunião.
              </p>
            </div>
            <div className="rounded-lg border border-border p-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="flex size-8 items-center justify-center rounded-lg bg-purple-100">
                  <Eye size={16} className="text-purple-600" />
                </div>
                <h4 className="font-semibold text-foreground">Visibilidade do progresso</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Permite identificar itens que estão parados há muito tempo.
              </p>
            </div>
          </div>
        </section>

        {/* Como adicionar um desenvolvimento */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Adicionando um desenvolvimento</h2>
          <div className="space-y-4">
            <div className="flex items-start gap-4 rounded-lg border border-border p-4">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">1</span>
              <div>
                <h4 className="font-semibold text-foreground">Localize o item na ata</h4>
                <p className="mt-1 text-sm text-muted-foreground">Encontre o item que deseja atualizar dentro do tópico correspondente.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 rounded-lg border border-border p-4">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">2</span>
              <div>
                <h4 className="font-semibold text-foreground">Clique em "Adicionar desenvolvimento"</h4>
                <p className="mt-1 text-sm text-muted-foreground">Ao lado do item, clique no botão para adicionar uma atualização.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 rounded-lg border border-border p-4">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">3</span>
              <div>
                <h4 className="font-semibold text-foreground">Descreva o progresso</h4>
                <p className="mt-1 text-sm text-muted-foreground">Escreva o que aconteceu desde a última reunião (ex: "Material entregue, instalação em andamento").</p>
              </div>
            </div>
            <div className="flex items-start gap-4 rounded-lg border border-border p-4">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">4</span>
              <div>
                <h4 className="font-semibold text-foreground">Atualize o status se necessário</h4>
                <p className="mt-1 text-sm text-muted-foreground">Marque como "Resolvido" se o item foi finalizado, ou mantenha "Pendente" para continuar acompanhando.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Histórico de desenvolvimentos */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Histórico de desenvolvimentos</h2>
          <div className="rounded-lg border border-border bg-background p-5">
            <div className="mb-4 rounded-md bg-muted/50 p-3">
              <span className="text-xs font-medium text-muted-foreground">Item:</span>
              <p className="font-medium text-foreground">Entregar projeto elétrico para aprovação</p>
              <div className="mt-1 flex gap-4 text-xs text-muted-foreground">
                <span>Responsável: Eng. Carlos</span>
                <span>Prazo: 15/02/2025</span>
              </div>
            </div>
            <div className="ml-4 space-y-2 border-l-2 border-primary/30 pl-4">
              <div className="rounded-md bg-muted/30 p-2">
                <div className="flex items-center gap-2">
                  <span className="rounded bg-muted px-1.5 py-0.5 text-xs font-medium text-muted-foreground">Ata 28</span>
                  <span className="text-xs text-muted-foreground">15/01/2025</span>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">"Projeto em elaboração, 60% concluído"</p>
              </div>
              <div className="rounded-md bg-muted/30 p-2">
                <div className="flex items-center gap-2">
                  <span className="rounded bg-muted px-1.5 py-0.5 text-xs font-medium text-muted-foreground">Ata 29</span>
                  <span className="text-xs text-muted-foreground">22/01/2025</span>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">"Enviado para revisão do cliente"</p>
              </div>
              <div className="rounded-md border border-primary/20 bg-primary/5 p-2">
                <div className="flex items-center gap-2">
                  <span className="rounded bg-primary/20 px-1.5 py-0.5 text-xs font-medium text-primary">Ata 30</span>
                  <span className="text-xs text-primary">Atual</span>
                </div>
                <p className="mt-1 text-sm text-foreground">"Cliente solicitou ajustes na iluminação do hall"</p>
              </div>
            </div>
          </div>
        </section>

        {/* Regras de negócio principais */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Regras de negócio</h2>
          <div className="space-y-4">
            <div className="rounded-lg borderborder-primary/20 bg-primary/5 p-5">
              <h4 className="mb-2 flex items-center gap-2 font-semibold text-foreground">
                <span className="flex size-6 items-center justify-center rounded-full bg-primary/20 text-xs font-bold text-primary">1</span>
                Itens pendentes vêm automaticamente ao clonar
              </h4>
              <p className="text-sm text-muted-foreground">
                Quando você clona uma ata para criar a próxima reunião, todos os itens com status
                "Pendente" são trazidos automaticamente. Você não precisa recriar nada.
              </p>
            </div>

            <div className="rounded-lg borderborder-green-200 bg-green-50 p-5">
              <h4 className="mb-2 flex items-center gap-2 font-semibold text-foreground">
                <span className="flex size-6 items-center justify-center rounded-full bg-green-200 text-xs font-bold text-green-700">2</span>
                Itens resolvidos não são clonados
              </h4>
              <p className="text-sm text-muted-foreground">
                Itens marcados como "Resolvido" não aparecem na nova ata. Eles ficam apenas
                no histórico da ata onde foram finalizados.
              </p>
            </div>

            <div className="rounded-lg borderborder-yellow-200 bg-yellow-50 p-5">
              <h4 className="mb-2 flex items-center gap-2 font-semibold text-foreground">
                <span className="flex size-6 items-center justify-center rounded-full bg-yellow-200 text-xs font-bold text-yellow-700">3</span>
                Novos desenvolvimentos são destacados
              </h4>
              <p className="text-sm text-muted-foreground">
                Quando você adiciona um desenvolvimento a um item, ele aparece com a badge "NOVO" em destaque.
                Ao clonar a ata, o desenvolvimento vem junto mas sem a badge "NOVO".
              </p>
            </div>
          </div>
        </section>

        {/* Exemplo visual do badge NOVO */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Como funciona a badge "NOVO"</h2>
          <p className="mb-4 text-muted-foreground">
            A badge "NOVO" indica que o desenvolvimento foi adicionado na ata atual. Ao clonar, ela desaparece.
          </p>
          <div className="grid gap-4 md:grid-cols-2">
            {/* Ata atual - com NOVO */}
            <div className="rounded-lg borderborder-primary/30 bg-primary/5 p-4">
              <div className="mb-3 text-center">
                <span className="rounded bg-primary/20 px-2 py-1 text-xs font-medium text-primary">Ata 30 (atual)</span>
              </div>
              <div className="rounded-md border border-border bg-background p-3">
                <div className="mb-2 text-xs font-medium text-foreground">Item: Substituir disjuntor QD-02</div>
                <div className="space-y-2">
                  <div className="rounded border border-border bg-muted/30 p-2">
                    <div className="mb-1 flex items-center gap-2 text-xs">
                      <span className="text-muted-foreground">A28 |</span>
                    </div>
                    <p className="text-xs text-muted-foreground">"Identificado problema de aquecimento."</p>
                  </div>
                  <div className="rounded borderborder-primary/30 bg-primary/5 p-2">
                    <div className="mb-1 flex items-center gap-2 text-xs">
                      <span className="rounded bg-blue-100 px-1.5 py-0.5 text-xs font-bold text-blue-700">NOVO</span>
                      <span className="font-medium text-primary">A30 |</span>
                    </div>
                    <p className="text-xs font-medium text-primary">"Disjuntor adquirido. Troca programada para 12/02."</p>
                  </div>
                </div>
              </div>
              <p className="mt-2 text-center text-xs text-primary">Desenvolvimento da ata atual aparece com NOVO</p>
            </div>

            {/* Após clonar - sem NOVO */}
            <div className="rounded-lg border border-border bg-muted/30 p-4">
              <div className="mb-3 text-center">
                <span className="rounded bg-muted px-2 py-1 text-xs font-medium text-muted-foreground">Ata 31 (clonada)</span>
              </div>
              <div className="rounded-md border border-border bg-background p-3">
                <div className="mb-2 text-xs font-medium text-foreground">Item: Substituir disjuntor QD-02</div>
                <div className="space-y-2">
                  <div className="rounded border border-border bg-muted/30 p-2">
                    <div className="mb-1 flex items-center gap-2 text-xs">
                      <span className="text-muted-foreground">A28 |</span>
                    </div>
                    <p className="text-xs text-muted-foreground">"Identificado problema de aquecimento."</p>
                  </div>
                  <div className="rounded border border-border bg-muted/30 p-2">
                    <div className="mb-1 flex items-center gap-2 text-xs">
                      <span className="text-muted-foreground">A30 |</span>
                    </div>
                    <p className="text-xs text-muted-foreground">"Disjuntor adquirido. Troca programada para 12/02."</p>
                  </div>
                </div>
              </div>
              <p className="mt-2 text-center text-xs text-muted-foreground">Após clonar, o desenvolvimento vem sem NOVO</p>
            </div>
          </div>
          <div className="mt-4 rounded-lg border border-border bg-muted/50 p-4">
            <p className="text-sm text-muted-foreground">
              <strong className="text-foreground">Regra:</strong> A badge "NOVO" aparece apenas quando o número da ata do desenvolvimento é igual ao número da ata atual.
              Na Ata 30, o desenvolvimento da A30 é "NOVO". Na Ata 31 (clonada), esse mesmo desenvolvimento não é mais "NOVO" porque A30 ≠ A31.
            </p>
          </div>
        </section>

        {/* Regras de negócio principais - continuação */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Regras de negócio</h2>
          <div className="space-y-4">
            <div className="rounded-lg border border-border bg-muted/50 p-5">
              <h4 className="mb-2 flex items-center gap-2 font-semibold text-foreground">
                <span className="flex size-6 items-center justify-center rounded-full bg-muted text-xs font-bold text-muted-foreground">4</span>
                Histórico completo no Mapa de Atas
              </h4>
              <p className="text-sm text-muted-foreground">
                O Mapa de Atas consolida todos os desenvolvimentos de cada item ao longo do tempo,
                permitindo ver a evolução completa de qualquer pendência.
              </p>
            </div>
          </div>
        </section>

        {/* O que NÃO é clonado */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">O que NÃO é clonado</h2>
          <p className="mb-4 text-muted-foreground">
            Ao clonar uma ata, alguns dados são reiniciados ou não são transferidos:
          </p>
          <div className="space-y-3">
            <div className="flex items-start gap-3 rounded-lg border border-orange-200 bg-orange-50 p-4">
              <span className="text-orange-600">⚠</span>
              <div>
                <p className="font-medium text-foreground">Prazos são reiniciados</p>
                <p className="text-sm text-muted-foreground">Os prazos dos itens clonados são atualizados para a data atual. Você deve definir novos prazos.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-orange-200 bg-orange-50 p-4">
              <span className="text-orange-600">⚠</span>
              <div>
                <p className="font-medium text-foreground">Desenvolvimentos não são clonados</p>
                <p className="text-sm text-muted-foreground">Os desenvolvimentos ficam vinculados à ata original. Na nova ata, você adiciona novos desenvolvimentos.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-orange-200 bg-orange-50 p-4">
              <span className="text-orange-600">⚠</span>
              <div>
                <p className="font-medium text-foreground">Participantes não são clonados</p>
                <p className="text-sm text-muted-foreground">A lista de participantes começa vazia. Você pode adicionar rapidamente do histórico de atas anteriores.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Fluxo visual de clonagem */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Fluxo de clonagem na prática</h2>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-lg border border-border bg-muted/30 p-4">
              <div className="mb-3 text-center">
                <span className="rounded bg-muted px-2 py-1 text-xs font-medium text-muted-foreground">Ata 29</span>
              </div>
              <div className="space-y-2 text-xs">
                <div className="flex items-center gap-2 rounded bg-background p-2">
                  <span className="size-2 rounded-full bg-yellow-500" />
                  <span className="flex-1 text-foreground">Instalar luminárias</span>
                </div>
                <div className="flex items-center gap-2 rounded bg-background p-2">
                  <span className="size-2 rounded-full bg-yellow-500" />
                  <span className="flex-1 text-foreground">Pintura corredor</span>
                </div>
                <div className="flex items-center gap-2 rounded bg-green-50 p-2 opacity-60">
                  <span className="size-2 rounded-full bg-green-500" />
                  <span className="flex-1 line-through text-muted-foreground">Aterramento</span>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-center">
              <div className="flex flex-col items-center gap-1">
                <span className="text-2xl text-primary">→</span>
                <span className="rounded bg-primary/10 px-2 py-1 text-xs font-medium text-primary">Clonar</span>
              </div>
            </div>

            <div className="rounded-lg borderborder-primary/30 bg-primary/5 p-4">
              <div className="mb-3 text-center">
                <span className="rounded bg-primary/20 px-2 py-1 text-xs font-medium text-primary">Ata 30 (nova)</span>
              </div>
              <div className="space-y-2 text-xs">
                <div className="flex items-center gap-2 rounded border border-primary/20 bg-background p-2">
                  <span className="size-2 rounded-full bg-yellow-500" />
                  <span className="flex-1 text-foreground">Instalar luminárias</span>
                  <span className="rounded bg-primary/20 px-1 text-xs text-primary">clonado</span>
                </div>
                <div className="flex items-center gap-2 rounded border border-primary/20 bg-background p-2">
                  <span className="size-2 rounded-full bg-yellow-500" />
                  <span className="flex-1 text-foreground">Pintura corredor</span>
                  <span className="rounded bg-primary/20 px-1 text-xs text-primary">clonado</span>
                </div>
                <div className="rounded border border-dashed border-border bg-background p-2 text-center text-muted-foreground">
                  Item resolvido não vem
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Como adicionar um desenvolvimento */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Como adicionar um desenvolvimento</h2>
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">1</div>
              <div>
                <h4 className="font-medium text-foreground">Abra o item na ata atual</h4>
                <p className="text-sm text-muted-foreground">Clique no item que deseja atualizar</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">2</div>
              <div>
                <h4 className="font-medium text-foreground">Escreva a atualização</h4>
                <p className="text-sm text-muted-foreground">Descreva o que aconteceu desde a última reunião</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">3</div>
              <div>
                <h4 className="font-medium text-foreground">Atualize o status se necessário</h4>
                <p className="text-sm text-muted-foreground">Se o item foi resolvido, marque como "Resolvido"</p>
              </div>
            </div>
          </div>
        </section>

        {/* Dica importante */}
        <section className="rounded-lg border border-primary/20 bg-primary/5 p-6">
          <h3 className="mb-3 text-lg font-semibold text-foreground">Dica importante</h3>
          <p className="text-sm text-muted-foreground">
            Mesmo que não haja novidades, é uma boa prática adicionar um desenvolvimento breve como
            "Sem alterações esta semana" ou "Aguardando aprovação". Isso documenta que o item foi
            revisado na reunião e mantém o histórico completo.
          </p>
        </section>
      </div>
    </DocsLayout>
  );
}

// Página: Participantes
function ParticipantesPage() {
  return (
    <DocsLayout
      title="Participantes"
      description="Como registrar os participantes da reunião"
      keywords="participantes reunião, registrar presença, lista participantes ata, presentes reunião obra"
      previousPage={{ title: 'Desenvolvimentos', slug: 'desenvolvimentos' }}
      nextPage={{ title: 'Mapa de atas', slug: 'mapa-de-atas' }}
    >
      <div className="space-y-8">
        {/* O que são participantes */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">O que são participantes?</h2>
          <p className="mb-6 text-lg leading-relaxed text-muted-foreground">
            Participantes são as pessoas que estiveram presentes em uma reunião específica.
            O registro de participantes documenta <strong className="text-foreground">quem estava na sala</strong> (física ou virtual)
            e, portanto, <strong className="text-foreground">quem foi informado das decisões tomadas</strong>.
          </p>
          <div className="rounded-lg borderborder-primary/20 bg-primary/5 p-4">
            <p className="text-sm text-muted-foreground">
              <strong className="text-foreground">Importante:</strong> Participantes são diferentes de <Link to="/docs/convidar-equipe" className="text-primary hover:underline">Colaboradores</Link>.
              Participante é quem compareceu a uma reunião específica. Colaborador é quem tem acesso ao projeto no sistema.
            </p>
          </div>
        </section>

        {/* Para que serve */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Para que serve?</h2>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-border p-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="flex size-8 items-center justify-center rounded-lg bg-blue-100">
                  <ClipboardCheck size={16} className="text-blue-600" />
                </div>
                <h4 className="font-semibold text-foreground">Rastreabilidade</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Comprova quem estava presente quando determinadas decisões foram tomadas ou pendências foram atribuídas.
              </p>
            </div>
            <div className="rounded-lg border border-border p-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="flex size-8 items-center justify-center rounded-lg bg-green-100">
                  <FileText size={16} className="text-green-600" />
                </div>
                <h4 className="font-semibold text-foreground">Documentação oficial</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                O PDF exportado inclui a lista de participantes, servindo como registro formal da reunião.
              </p>
            </div>
            <div className="rounded-lg border border-border p-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="flex size-8 items-center justify-center rounded-lg bg-orange-100">
                  <Users size={16} className="text-orange-600" />
                </div>
                <h4 className="font-semibold text-foreground">Identificação dos envolvidos</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Facilita identificar quem são as pessoas envolvidas no projeto e suas respectivas empresas.
              </p>
            </div>
            <div className="rounded-lg border border-border p-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="flex size-8 items-center justify-center rounded-lg bg-purple-100">
                  <Mail size={16} className="text-purple-600" />
                </div>
                <h4 className="font-semibold text-foreground">Contato facilitado</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Ter os e-mails registrados ajuda a identificar para quem enviar a ata após exportar o PDF.
              </p>
            </div>
          </div>
        </section>

        {/* Informações de cada participante */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Informações de cada participante</h2>
          <p className="mb-4 text-muted-foreground">Cada participante pode ter os seguintes dados registrados:</p>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="p-3 text-left font-semibold text-foreground">Campo</th>
                  <th className="p-3 text-left font-semibold text-foreground">Obrigatório</th>
                  <th className="p-3 text-left font-semibold text-foreground">Descrição</th>
                </tr>
              </thead>
              <tbody className="text-muted-foreground">
                <tr className="border-b border-border">
                  <td className="p-3 font-medium text-foreground">Nome</td>
                  <td className="p-3"><span className="rounded bg-primary/10 px-1.5 py-0.5 text-xs font-medium text-primary">Sim</span></td>
                  <td className="p-3">Nome completo do participante</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 font-medium text-foreground">Empresa</td>
                  <td className="p-3"><span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">Não</span></td>
                  <td className="p-3">Empresa que o participante representa</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 font-medium text-foreground">Cargo</td>
                  <td className="p-3"><span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">Não</span></td>
                  <td className="p-3">Função ou cargo (ex: Engenheiro, Gerente)</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 font-medium text-foreground">E-mail</td>
                  <td className="p-3"><span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">Não</span></td>
                  <td className="p-3">E-mail para contato e envio da ata</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Como adicionar participantes */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Como adicionar participantes</h2>
          <div className="space-y-4">
            <div className="flex items-start gap-4 rounded-lg border border-border p-4">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">1</span>
              <div>
                <h4 className="font-semibold text-foreground">Abra a ata em edição</h4>
                <p className="mt-1 text-sm text-muted-foreground">Na tela do projeto, clique na ata que deseja editar.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 rounded-lg border border-border p-4">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">2</span>
              <div>
                <h4 className="font-semibold text-foreground">Clique em "Adicionar participante"</h4>
                <p className="mt-1 text-sm text-muted-foreground">No painel lateral ou seção de participantes, clique no botão para adicionar.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 rounded-lg border border-border p-4">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">3</span>
              <div>
                <h4 className="font-semibold text-foreground">Preencha as informações</h4>
                <p className="mt-1 text-sm text-muted-foreground">Digite o nome e, opcionalmente, empresa, cargo e e-mail do participante.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Mockup visual */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Visualização dos participantes</h2>
          <div className="rounded-lg border border-border bg-background overflow-hidden">
            {/* Header do painel */}
            <div className="flex items-center justify-between border-b border-border bg-muted/30 px-4 py-3">
              <div className="flex items-center gap-2">
                <Users size={16} className="text-primary" />
                <span className="font-medium text-foreground">Participantes</span>
                <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">4</span>
              </div>
              <button className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground">
                <Plus size={14} />
                Adicionar
              </button>
            </div>
            {/* Lista de participantes */}
            <div className="divide-y divide-border">
              <div className="flex items-center gap-3 p-3 hover:bg-muted/30">
                <div className="flex size-9 items-center justify-center rounded-full bg-blue-100 text-sm font-semibold text-blue-600">JC</div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">João Carlos Silva</p>
                  <p className="text-xs text-muted-foreground">Construtora ABC • Engenheiro de Obras</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 hover:bg-muted/30">
                <div className="flex size-9 items-center justify-center rounded-full bg-green-100 text-sm font-semibold text-green-600">MF</div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">Maria Fernanda Oliveira</p>
                  <p className="text-xs text-muted-foreground">Arquitetura XYZ • Arquiteta</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 hover:bg-muted/30">
                <div className="flex size-9 items-center justify-center rounded-full bg-orange-100 text-sm font-semibold text-orange-600">RS</div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">Roberto Santos</p>
                  <p className="text-xs text-muted-foreground">Cliente • Proprietário</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 hover:bg-muted/30">
                <div className="flex size-9 items-center justify-center rounded-full bg-purple-100 text-sm font-semibold text-purple-600">AL</div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">Ana Luíza Costa</p>
                  <p className="text-xs text-muted-foreground">Fiscalização • Engenheira Civil</p>
                </div>
              </div>
            </div>
          </div>
          <p className="mt-2 text-center text-xs text-muted-foreground">Exemplo ilustrativo da lista de participantes</p>
        </section>

        {/* Histórico de participantes */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Histórico de participantes</h2>
          <p className="mb-4 text-muted-foreground">
            Para agilizar o preenchimento, você pode <strong className="text-foreground">selecionar participantes de atas anteriores</strong>.
            Ao adicionar um participante, escolha a opção "Selecionar do histórico" para ver a lista.
          </p>
          <div className="rounded-lg border border-border bg-background p-4">
            <p className="mb-3 text-sm font-medium text-foreground">Opções ao adicionar participante:</p>
            <div className="space-y-2">
              <div className="flex items-center gap-3 rounded-md border border-border bg-white p-3">
                <div className="flex size-10 items-center justify-center rounded-full bg-primary/10">
                  <Edit3 size={18} className="text-primary" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">Criar novo</p>
                  <p className="text-xs text-muted-foreground">Digite os dados manualmente</p>
                </div>
              </div>
              <div className="flex items-center gap-3 rounded-md borderborder-amber-200 bg-amber-50 p-3">
                <div className="flex size-10 items-center justify-center rounded-full bg-amber-100">
                  <Clock size={18} className="text-amber-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">Selecionar do histórico</p>
                  <p className="text-xs text-muted-foreground">Participantes de atas anteriores</p>
                </div>
              </div>
              <div className="flex items-center gap-3 rounded-md border border-border bg-white p-3">
                <div className="flex size-10 items-center justify-center rounded-full bg-emerald-100">
                  <UserPlus size={18} className="text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">Adicionar colaborador</p>
                  <p className="text-xs text-muted-foreground">Membros do projeto</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Comportamento na clonagem */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Participantes e clonagem de atas</h2>
          <div className="rounded-lg borderborder-orange-200 bg-orange-50 p-5">
            <div className="flex items-start gap-3">
              <span className="text-orange-600 text-lg">⚠</span>
              <div>
                <h4 className="font-semibold text-foreground">Participantes NÃO são clonados</h4>
                <p className="mt-1 text-sm text-muted-foreground">
                  Ao clonar uma ata para criar uma nova reunião, a lista de participantes começa vazia.
                  Isso porque cada reunião pode ter pessoas diferentes presentes. Use a opção "Selecionar do histórico"
                  para adicionar rapidamente os participantes recorrentes.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Onde aparecem os participantes */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Onde os participantes aparecem</h2>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-lg border border-border p-4 text-center">
              <div className="mx-auto mb-3 flex size-12 items-center justify-center rounded-lg bg-blue-100">
                <Edit3 size={20} className="text-blue-600" />
              </div>
              <h4 className="mb-1 font-semibold text-foreground">Tela de edição</h4>
              <p className="text-xs text-muted-foreground">Painel lateral para gerenciar participantes</p>
            </div>
            <div className="rounded-lg border border-border p-4 text-center">
              <div className="mx-auto mb-3 flex size-12 items-center justify-center rounded-lg bg-green-100">
                <Eye size={20} className="text-green-600" />
              </div>
              <h4 className="mb-1 font-semibold text-foreground">Preview da ata</h4>
              <p className="text-xs text-muted-foreground">Lista de presentes na visualização</p>
            </div>
            <div className="rounded-lg border border-border p-4 text-center">
              <div className="mx-auto mb-3 flex size-12 items-center justify-center rounded-lg bg-orange-100">
                <FileText size={20} className="text-orange-600" />
              </div>
              <h4 className="mb-1 font-semibold text-foreground">PDF exportado</h4>
              <p className="text-xs text-muted-foreground">Seção formal de participantes</p>
            </div>
          </div>
        </section>

        {/* Boas práticas */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Boas práticas</h2>
          <div className="space-y-3">
            <div className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 p-3">
              <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-green-500 text-white">
                <Check size={12} />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground">Registre todos os presentes</h4>
                <p className="text-xs text-muted-foreground">Mesmo que alguém tenha participado brevemente, registre sua presença.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 p-3">
              <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-green-500 text-white">
                <Check size={12} />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground">Preencha a empresa e cargo</h4>
                <p className="text-xs text-muted-foreground">Facilita identificar o papel de cada pessoa no projeto.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 p-3">
              <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-green-500 text-white">
                <Check size={12} />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground">Mantenha os e-mails atualizados</h4>
                <p className="text-xs text-muted-foreground">Facilita o contato posterior para envio da ata ou esclarecimentos.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 p-3">
              <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-green-500 text-white">
                <Check size={12} />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground">Padronize nomes de empresas</h4>
                <p className="text-xs text-muted-foreground">Use sempre o mesmo nome para a mesma empresa (ex: "Construtora ABC" e não "ABC Construtora").</p>
              </div>
            </div>
          </div>
        </section>

        {/* Dica importante */}
        <section className="rounded-lg borderborder-primary/20 bg-primary/5 p-6">
          <h3 className="mb-3 text-lg font-semibold text-foreground">Participante vs Responsável</h3>
          <p className="text-muted-foreground">
            <strong className="text-foreground">Participante</strong> é quem estava presente na reunião.
            <strong className="text-foreground"> Responsável</strong> é quem está encarregado de resolver um item específico.
            Uma pessoa pode ser participante sem ser responsável por nenhum item,
            assim como um responsável pode não ter comparecido à reunião onde seu item foi discutido.
          </p>
        </section>
      </div>
    </DocsLayout>
  );
}

// Página: Mapa de atas
function MapaDeAtasPage() {
  return (
    <DocsLayout
      title="Mapa de atas"
      description="Visualização consolidada de todos os itens do projeto"
      keywords="mapa de atas, visualização consolidada, dashboard itens, acompanhar pendências, visão geral projeto"
      previousPage={{ title: 'Participantes', slug: 'participantes' }}
      nextPage={{ title: 'Preview e exportação', slug: 'preview-e-exportacao' }}
    >
      <div className="space-y-8">
        <section>
          <p className="mb-6 text-lg leading-relaxed text-muted-foreground">
            O Mapa de Atas consolida todos os itens do projeto em uma única visualização,
            mostrando em quais atas cada item foi discutido. É a ferramenta ideal para
            acompanhar a evolução das pendências ao longo do tempo.
          </p>
        </section>

        {/* Para que serve */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Para que serve?</h2>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-border p-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="flex size-8 items-center justify-center rounded-lg bg-blue-100">
                  <Map size={16} className="text-blue-600" />
                </div>
                <h4 className="font-semibold text-foreground">Visão consolidada</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Veja todos os itens do projeto em um só lugar, independente da ata.
              </p>
            </div>
            <div className="rounded-lg border border-border p-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="flex size-8 items-center justify-center rounded-lg bg-green-100">
                  <Eye size={16} className="text-green-600" />
                </div>
                <h4 className="font-semibold text-foreground">Identificar itens travados</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Veja quais pendências estão abertas há muito tempo sem progresso.
              </p>
            </div>
            <div className="rounded-lg border border-border p-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="flex size-8 items-center justify-center rounded-lg bg-orange-100">
                  <Calendar size={16} className="text-orange-600" />
                </div>
                <h4 className="font-semibold text-foreground">Preparar reuniões</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Antes da reunião, revise os itens pendentes para saber o que cobrar.
              </p>
            </div>
            <div className="rounded-lg border border-border p-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="flex size-8 items-center justify-center rounded-lg bg-purple-100">
                  <Download size={16} className="text-purple-600" />
                </div>
                <h4 className="font-semibold text-foreground">Exportar relatório</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Gere um PDF do mapa para apresentar o status geral à diretoria ou cliente.
              </p>
            </div>
          </div>
        </section>

        {/* Onde encontrar */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Onde encontrar</h2>
          <div className="rounded-lg border border-border bg-muted/30 p-5">
            <p className="mb-4 text-muted-foreground">
              O Mapa de Atas está disponível na <strong className="text-foreground">Tela do Projeto</strong>.
            </p>
            <div className="flex items-start gap-4 rounded-lg border border-border bg-background p-4">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">1</span>
              <div>
                <h4 className="font-semibold text-foreground">Acesse o projeto desejado</h4>
                <p className="mt-1 text-sm text-muted-foreground">No Dashboard, clique no projeto que deseja visualizar.</p>
              </div>
            </div>
            <div className="mt-4 flex items-start gap-4 rounded-lg border border-border bg-background p-4">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">2</span>
              <div>
                <h4 className="font-semibold text-foreground">Clique no botão "Mapa de Atas"</h4>
                <p className="mt-1 text-sm text-muted-foreground">No header da tela do projeto, ao lado dos botões de ação, você encontrará o botão "Mapa de Atas".</p>
                <div className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs text-gray-700">
                  <Map size={12} />
                  Mapa de Atas
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mockup visual do Mapa de Atas - CORRIGIDO */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Visualização do Mapa</h2>
          <div className="overflow-hidden rounded-lg border border-border bg-background">
            {/* Header do Mapa */}
            <div className="flex items-center justify-between border-b border-border bg-muted/30 px-4 py-3">
              <div className="flex items-center gap-3">
                <div>
                  <h3 className="font-semibold text-foreground">Mapa de Atas</h3>
                  <p className="text-xs text-muted-foreground">Obra Shopping Norte • 12 itens</p>
                </div>
              </div>
              <div className="flex h-8 items-center gap-2 rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground">
                Exportar PDF
              </div>
            </div>

            {/* Grid do Mapa */}
            <div className="overflow-x-auto">
              <div className="min-w-[500px] p-4">
                {/* Tabela do Mapa */}
                <div className="rounded-lg border border-border text-xs">
                  {/* Header */}
                  <div className="flex border-b border-border bg-muted/50 font-medium">
                    <div className="w-16 shrink-0 border-r border-border p-2 text-center text-muted-foreground">Código</div>
                    <div className="w-44 shrink-0 border-r border-border p-2 text-foreground">Descrição</div>
                    <div className="w-14 shrink-0 border-r border-border p-2 text-center text-muted-foreground">
                      <div>Ata</div>
                      <div className="font-normal">28</div>
                    </div>
                    <div className="w-14 shrink-0 border-r border-border p-2 text-center text-muted-foreground">
                      <div>Ata</div>
                      <div className="font-normal">29</div>
                    </div>
                    <div className="w-14 shrink-0 p-2 text-center text-muted-foreground">
                      <div>Ata</div>
                      <div className="font-normal">30</div>
                    </div>
                  </div>

                  {/* Tópico 1 */}
                  <div className="border-b border-border bg-slate-50/70">
                    <div className="flex items-center gap-2 p-2 text-xs font-medium text-slate-600">
                      <span className="flex size-5 items-center justify-center rounded bg-slate-100 text-xs">1</span>
                      Instalações Elétricas
                    </div>
                  </div>

                  {/* Item 1.1 */}
                  <div className="flex border-b border-border hover:bg-muted/30">
                    <div className="flex w-16 shrink-0 items-center justify-center border-r border-border p-2 font-mono text-muted-foreground">1.1</div>
                    <div className="w-44 shrink-0 border-r border-border p-2">
                      <span className="text-foreground">Substituir disjuntor QD-02</span>
                      <button className="mt-1 flex items-center gap-1 text-xs text-primary">
                        <span className="flex size-4 items-center justify-center rounded-full bg-slate-100 text-xs font-bold text-slate-600">2</span>
                        Ver desenvolvimentos
                      </button>
                    </div>
                    <div className="flex w-14 shrink-0 items-center justify-center border-r border-border">
                      <span className="inline-flex size-6 items-center justify-center rounded-full bg-primary/10">
                        <span className="size-2.5 rounded-full bg-primary" />
                      </span>
                    </div>
                    <div className="flex w-14 shrink-0 items-center justify-center border-r border-border">
                      <span className="inline-flex size-6 items-center justify-center rounded-full bg-gray-50/50">
                        <span className="size-1.5 rounded-full bg-gray-200" />
                      </span>
                    </div>
                    <div className="flex w-14 shrink-0 items-center justify-center">
                      <span className="inline-flex size-6 items-center justify-center rounded-full bg-primary/10">
                        <span className="size-2.5 rounded-full bg-primary" />
                      </span>
                    </div>
                  </div>

                  {/* Item 1.2 */}
                  <div className="flex border-b border-border hover:bg-muted/30">
                    <div className="flex w-16 shrink-0 items-center justify-center border-r border-border p-2 font-mono text-muted-foreground">1.2</div>
                    <div className="w-44 shrink-0 border-r border-border p-2">
                      <span className="text-foreground">Teste de aterramento geral</span>
                    </div>
                    <div className="flex w-14 shrink-0 items-center justify-center border-r border-border">
                      <span className="inline-flex size-6 items-center justify-center rounded-full bg-primary/10">
                        <span className="size-2.5 rounded-full bg-primary" />
                      </span>
                    </div>
                    <div className="flex w-14 shrink-0 items-center justify-center border-r border-border">
                      <span className="inline-flex size-6 items-center justify-center rounded-full bg-primary/10">
                        <span className="size-2.5 rounded-full bg-primary" />
                      </span>
                    </div>
                    <div className="flex w-14 shrink-0 items-center justify-center">
                      <span className="inline-flex size-6 items-center justify-center rounded-full bg-primary/10">
                        <span className="size-2.5 rounded-full bg-primary" />
                      </span>
                    </div>
                  </div>

                  {/* Tópico 2 */}
                  <div className="border-b border-border bg-slate-50/70">
                    <div className="flex items-center gap-2 p-2 text-xs font-medium text-slate-600">
                      <span className="flex size-5 items-center justify-center rounded bg-slate-100 text-xs">2</span>
                      Instalações Hidráulicas
                    </div>
                  </div>

                  {/* Item 2.1 */}
                  <div className="flex hover:bg-muted/30">
                    <div className="flex w-16 shrink-0 items-center justify-center border-r border-border p-2 font-mono text-muted-foreground">2.1</div>
                    <div className="w-44 shrink-0 border-r border-border p-2">
                      <span className="text-foreground">Aprovar projeto de esgoto</span>
                      <button className="mt-1 flex items-center gap-1 text-xs text-primary">
                        <span className="flex size-4 items-center justify-center rounded-full bg-slate-100 text-xs font-bold text-slate-600">3</span>
                        Ver desenvolvimentos
                      </button>
                    </div>
                    <div className="flex w-14 shrink-0 items-center justify-center border-r border-border">
                      <span className="inline-flex size-6 items-center justify-center rounded-full bg-gray-50/50">
                        <span className="size-1.5 rounded-full bg-gray-200" />
                      </span>
                    </div>
                    <div className="flex w-14 shrink-0 items-center justify-center border-r border-border">
                      <span className="inline-flex size-6 items-center justify-center rounded-full bg-primary/10">
                        <span className="size-2.5 rounded-full bg-primary" />
                      </span>
                    </div>
                    <div className="flex w-14 shrink-0 items-center justify-center">
                      <span className="inline-flex size-6 items-center justify-center rounded-full bg-primary/10">
                        <span className="size-2.5 rounded-full bg-primary" />
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <p className="mt-2 text-center text-xs text-muted-foreground">Exemplo ilustrativo do Mapa de Atas</p>
        </section>

        {/* Como ler o mapa */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Como interpretar o mapa</h2>
          <div className="space-y-3">
            <div className="flex items-start gap-4 rounded-lg border border-border p-4">
              <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/10">
                <span className="size-3 rounded-full bg-primary" />
              </div>
              <div>
                <h4 className="font-semibold text-foreground">Bolinha azul</h4>
                <p className="text-sm text-muted-foreground">
                  Indica que o item foi discutido naquela ata. Houve algum desenvolvimento ou atualização registrada.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 rounded-lg border border-border p-4">
              <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-gray-50">
                <span className="size-2 rounded-full bg-gray-300" />
              </div>
              <div>
                <h4 className="font-semibold text-foreground">Bolinha cinza</h4>
                <p className="text-sm text-muted-foreground">
                  Indica que o item não foi discutido naquela ata. Pode significar que não houve novidades ou que o item ainda não existia.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 rounded-lg border border-border p-4">
              <div className="flex size-9 shrink-0 items-center justify-center">
                <span className="flex size-5 items-center justify-center rounded-full bg-slate-100 text-xs font-bold text-slate-600">3</span>
              </div>
              <div>
                <h4 className="font-semibold text-foreground">Contador de desenvolvimentos</h4>
                <p className="text-sm text-muted-foreground">
                  Mostra quantos desenvolvimentos o item possui. Clique para abrir o painel lateral com o histórico completo.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Visualizando desenvolvimentos */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Histórico de desenvolvimentos</h2>
          <p className="mb-4 text-muted-foreground">
            Ao clicar em "Ver desenvolvimentos", um painel lateral abre mostrando todo o histórico do item:
          </p>
          <div className="rounded-lg border border-border bg-background p-4">
            <div className="mb-3 rounded-md bg-muted/50 p-3">
              <div className="mb-1 font-mono text-xs text-muted-foreground">1.1</div>
              <div className="font-medium text-foreground">Substituir disjuntor QD-02</div>
            </div>
            <div className="relative space-y-3 border-l-2 border-slate-200 pl-6">
              <div className="relative">
                <div className="absolute -left-[37px] flex size-6 items-center justify-center rounded-full bg-slate-100 text-xs font-medium text-slate-500">1</div>
                <div className="rounded-md bg-muted/30 p-2">
                  <span className="inline-flex items-center rounded bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">Ata 28</span>
                  <p className="mt-1 text-sm text-muted-foreground">"Identificado problema de aquecimento no disjuntor."</p>
                </div>
              </div>
              <div className="relative">
                <div className="absolute -left-[37px] flex size-6 items-center justify-center rounded-full bg-slate-100 text-xs font-medium text-slate-500">2</div>
                <div className="rounded-md bg-muted/30 p-2">
                  <span className="inline-flex items-center rounded bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">Ata 30</span>
                  <p className="mt-1 text-sm text-muted-foreground">"Disjuntor adquirido. Troca programada para 12/02."</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="rounded-lg border border-border bg-muted/50 p-6">
          <h3 className="mb-3 text-lg font-semibold text-foreground">Exportação para PDF</h3>
          <p className="mb-3 text-sm text-muted-foreground">
            O Mapa pode ser exportado em PDF no formato paisagem. Ideal para:
          </p>
          <ul className="space-y-1 text-sm text-muted-foreground">
            <li className="flex items-start gap-2"><span className="text-primary">•</span>Apresentações de status para diretoria ou cliente</li>
            <li className="flex items-start gap-2"><span className="text-primary">•</span>Documentação física do projeto</li>
            <li className="flex items-start gap-2"><span className="text-primary">•</span>Reuniões onde não há acesso ao sistema</li>
          </ul>
        </section>

        <section className="rounded-lg borderborder-primary/20 bg-primary/5 p-6">
          <h3 className="mb-3 text-lg font-semibold text-foreground">Quando usar o Mapa de Atas</h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2"><span className="text-primary">•</span><strong className="text-foreground">Antes de reuniões:</strong> revise as pendências para saber o que cobrar</li>
            <li className="flex items-start gap-2"><span className="text-primary">•</span><strong className="text-foreground">Itens travados:</strong> identifique quais itens têm poucas bolinhas azuis (pouca evolução)</li>
            <li className="flex items-start gap-2"><span className="text-primary">•</span><strong className="text-foreground">Auditorias:</strong> consulte quando cada decisão foi tomada</li>
            <li className="flex items-start gap-2"><span className="text-primary">•</span><strong className="text-foreground">Encerramento:</strong> tenha o histórico completo do projeto</li>
          </ul>
        </section>
      </div>
    </DocsLayout>
  );
}

// Página: Preview e exportação
function PreviewExportacaoPage() {
  return (
    <DocsLayout
      title="Preview e exportação"
      description="Como visualizar e exportar atas em PDF"
      keywords="exportar ata pdf, preview ata, imprimir ata reunião, gerar pdf ata, documento ata"
      previousPage={{ title: 'Mapa de atas', slug: 'mapa-de-atas' }}
      nextPage={{ title: 'Convidar equipe', slug: 'convidar-equipe' }}
    >
      <div className="space-y-8">
        {/* O que é */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">O que é o Preview?</h2>
          <p className="mb-6 text-lg leading-relaxed text-muted-foreground">
            O Preview é a visualização da ata em formato de documento, exatamente como ela será impressa ou exportada em PDF.
            É a forma oficial de apresentar a ata para <strong className="text-foreground">distribuição aos participantes</strong>,
            <strong className="text-foreground"> arquivamento</strong> e <strong className="text-foreground">validação das decisões</strong>.
          </p>
        </section>

        {/* Para que serve */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Para que serve?</h2>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-border p-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="flex size-8 items-center justify-center rounded-lg bg-blue-100">
                  <FileText size={16} className="text-blue-600" />
                </div>
                <h4 className="font-semibold text-foreground">Documentação formal</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Gera um documento oficial que pode ser arquivado, impresso ou anexado a contratos.
              </p>
            </div>
            <div className="rounded-lg border border-border p-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="flex size-8 items-center justify-center rounded-lg bg-green-100">
                  <Share2 size={16} className="text-green-600" />
                </div>
                <h4 className="font-semibold text-foreground">Distribuição</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                O PDF pode ser enviado por e-mail ou WhatsApp para todos os participantes e interessados.
              </p>
            </div>
            <div className="rounded-lg border border-border p-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="flex size-8 items-center justify-center rounded-lg bg-orange-100">
                  <ClipboardCheck size={16} className="text-orange-600" />
                </div>
                <h4 className="font-semibold text-foreground">Validação</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Permite revisar o conteúdo antes de finalizar, garantindo que tudo está correto.
              </p>
            </div>
            <div className="rounded-lg border border-border p-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="flex size-8 items-center justify-center rounded-lg bg-purple-100">
                  <Download size={16} className="text-purple-600" />
                </div>
                <h4 className="font-semibold text-foreground">Backup offline</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                O PDF pode ser salvo localmente como backup independente do sistema online.
              </p>
            </div>
          </div>
        </section>

        {/* Como acessar */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Como acessar o Preview</h2>
          <div className="space-y-4">
            <div className="flex items-start gap-4 rounded-lg border border-border p-4">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">1</span>
              <div>
                <h4 className="font-semibold text-foreground">Acesse a ata desejada</h4>
                <p className="mt-1 text-sm text-muted-foreground">Na tela do projeto, localize a ata que deseja visualizar.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 rounded-lg border border-border p-4">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">2</span>
              <div>
                <h4 className="font-semibold text-foreground">Clique no ícone de Preview</h4>
                <p className="mt-1 text-sm text-muted-foreground">O ícone de olho ou o botão "Preview" abre a visualização do documento.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Exportando para PDF */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Exportando para PDF</h2>
          <div className="space-y-4">
            <div className="flex items-start gap-4 rounded-lg border border-border p-4">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">1</span>
              <div>
                <h4 className="font-semibold text-foreground">Acesse o preview da ata</h4>
                <p className="mt-1 text-sm text-muted-foreground">Clique no ícone de visualização para abrir o preview.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 rounded-lg border border-border p-4">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">2</span>
              <div>
                <h4 className="font-semibold text-foreground">Clique em "Exportar PDF"</h4>
                <p className="mt-1 text-sm text-muted-foreground">O botão fica no canto superior direito da tela de preview.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 rounded-lg border border-border p-4">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">3</span>
              <div>
                <h4 className="font-semibold text-foreground">Configure e salve</h4>
                <p className="mt-1 text-sm text-muted-foreground">Selecione "Salvar como PDF" no menu de impressão e escolha o local para salvar.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Mockup da tela de preview */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Visualização do Preview</h2>
          <div className="rounded-lg border border-border bg-background p-4">
            <p className="mb-4 text-sm text-muted-foreground">Exemplo ilustrativo da tela de Preview</p>
          <div className="overflow-hidden rounded-lg border border-border shadow-lg">
            {/* Header do preview */}
            <div className="flex items-center justify-between border-b border-border bg-white px-4 py-3">
              <button className="flex items-center gap-2 rounded-md px-3 py-1.5 text-xs text-muted-foreground hover:bg-muted">
                <ArrowLeft className="size-4" />
                Voltar para atas
              </button>
              <button className="flex items-center gap-2 rounded-lg bg-foreground px-4 py-2 text-xs font-medium text-background">
                <FileText className="size-4" />
                Exportar PDF
              </button>
            </div>

            {/* Conteúdo do preview */}
            <div className="bg-gray-100 p-6">
              <div className="mx-auto max-w-2xl rounded-lg border border-border bg-white p-6 shadow-sm">
                {/* Cabeçalho da ata */}
                <div className="mb-6 border-b border-border pb-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h4 className="text-lg font-bold text-foreground">Ata de Reunião #3</h4>
                      <div className="mt-2 flex flex-wrap gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="size-3" />
                          27/11/2024
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="size-3" />
                          Sala de Reuniões
                        </span>
                      </div>
                    </div>
                    <div className="flex h-12 w-24 shrink-0 items-center justify-center rounded borderborder-dashed border-primary/40 bg-primary/5">
                      <span className="text-xs font-medium text-primary">Sua Logo</span>
                    </div>
                  </div>
                </div>

                {/* Participantes */}
                <div className="mb-4">
                  <h5 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Participantes</h5>
                  <div className="flex flex-wrap gap-2">
                    <span className="rounded bg-muted px-2 py-1 text-xs">João Silva</span>
                    <span className="rounded bg-muted px-2 py-1 text-xs">Maria Santos</span>
                    <span className="rounded bg-muted px-2 py-1 text-xs">Pedro Costa</span>
                  </div>
                </div>

                {/* Tabela de itens */}
                <div className="mb-4">
                  <h5 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">1. Instalações Elétricas</h5>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-muted/50">
                          <th className="border border-border px-1.5 py-1 text-center font-medium" style={{ width: '30px' }}>ITEM</th>
                          <th className="border border-border px-1.5 py-1 text-center font-medium" style={{ width: '35px' }}>Nº ATA</th>
                          <th className="border border-border px-1.5 py-1 text-center font-medium" style={{ width: '50px' }}>DATA</th>
                          <th className="border border-border px-1.5 py-1 text-left font-medium">DESCRIÇÃO</th>
                          <th className="border border-border px-1.5 py-1 text-center font-medium" style={{ width: '55px' }}>RESP.</th>
                          <th className="border border-border px-1.5 py-1 text-center font-medium" style={{ width: '50px' }}>PRAZO</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="bg-gray-50">
                          <td className="border border-border px-1.5 py-1 text-center">1.1</td>
                          <td className="border border-border px-1.5 py-1 text-center">#28</td>
                          <td className="border border-border px-1.5 py-1 text-center">15/01</td>
                          <td className="border border-border px-1.5 py-1">Verificar quadro elétrico do 2º andar</td>
                          <td className="border border-border px-1.5 py-1 text-center">João</td>
                          <td className="border border-border px-1.5 py-1 text-center">30/11</td>
                        </tr>
                        <tr>
                          <td className="border border-border px-1.5 py-1 text-center">1.2</td>
                          <td className="border border-border px-1.5 py-1 text-center">#30</td>
                          <td className="border border-border px-1.5 py-1 text-center">22/01</td>
                          <td className="border border-border px-1.5 py-1">
                            <span className="line-through opacity-60">Instalar tomadas na recepção</span>
                            <span className="ml-1 rounded-md bg-green-100 px-1 py-0.5 text-xs font-bold text-green-700 uppercase">Resolvido</span>
                          </td>
                          <td className="border border-border px-1.5 py-1 text-center">Maria</td>
                          <td className="border border-border px-1.5 py-1 text-center">25/11</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="border-t border-dashed border-border pt-3 text-center text-xs text-muted-foreground">
                  Documento gerado pelo Atas Digitais
                </div>
              </div>
            </div>
          </div>
          </div>
        </section>

        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">O que é incluído no PDF</h2>
          <ul className="space-y-2 text-muted-foreground">
            <li className="flex items-start gap-2"><span className="text-green-600">✓</span>Sua logo personalizada (se configurada em <Link to="/docs/personalizacao" className="text-primary hover:underline">Personalização</Link>)</li>
            <li className="flex items-start gap-2"><span className="text-green-600">✓</span>Cabeçalho com número, data e local</li>
            <li className="flex items-start gap-2"><span className="text-green-600">✓</span>Lista completa de participantes</li>
            <li className="flex items-start gap-2"><span className="text-green-600">✓</span>Todos os tópicos e itens</li>
            <li className="flex items-start gap-2"><span className="text-green-600">✓</span>Desenvolvimentos de cada item</li>
            <li className="flex items-start gap-2"><span className="text-green-600">✓</span>Responsáveis, prazos e status</li>
          </ul>
        </section>

        <section className="rounded-lg border border-border bg-muted/50 p-6">
          <h3 className="mb-3 text-lg font-semibold text-foreground">Dica: Configurações de impressão</h3>
          <ul className="space-y-1 text-sm text-muted-foreground">
            <li>• Marque "Imprimir gráficos de fundo"</li>
            <li>• Use "Retrato" para atas normais</li>
            <li>• Use "Paisagem" para o Mapa de Atas</li>
          </ul>
        </section>

        {/* Como distribuir */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Distribuindo a ata</h2>
          <p className="mb-4 text-muted-foreground">
            Após exportar o PDF, você pode distribuir a ata para todos os envolvidos:
          </p>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-lg border border-border p-4 text-center">
              <div className="mx-auto mb-3 flex size-12 items-center justify-center rounded-lg bg-blue-100">
                <Mail size={20} className="text-blue-600" />
              </div>
              <h4 className="mb-1 font-semibold text-foreground">E-mail</h4>
              <p className="text-xs text-muted-foreground">Anexe o PDF e envie para a lista de participantes</p>
            </div>
            <div className="rounded-lg border border-border p-4 text-center">
              <div className="mx-auto mb-3 flex size-12 items-center justify-center rounded-lg bg-green-100">
                <Share2 size={20} className="text-green-600" />
              </div>
              <h4 className="mb-1 font-semibold text-foreground">WhatsApp</h4>
              <p className="text-xs text-muted-foreground">Compartilhe diretamente em grupos ou conversas</p>
            </div>
            <div className="rounded-lg border border-border p-4 text-center">
              <div className="mx-auto mb-3 flex size-12 items-center justify-center rounded-lg bg-orange-100">
                <FolderOpen size={20} className="text-orange-600" />
              </div>
              <h4 className="mb-1 font-semibold text-foreground">Drive/Nuvem</h4>
              <p className="text-xs text-muted-foreground">Salve em pasta compartilhada para acesso de todos</p>
            </div>
          </div>
        </section>

        {/* Boas práticas */}
        <section className="rounded-lg borderborder-primary/20 bg-primary/5 p-6">
          <h3 className="mb-3 text-lg font-semibold text-foreground">Boas práticas de distribuição</h3>
          <div className="space-y-2 text-sm text-muted-foreground">
            <p className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span><strong className="text-foreground">Envie no mesmo dia:</strong> Distribua a ata logo após a reunião, enquanto as informações estão frescas.</span>
            </p>
            <p className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span><strong className="text-foreground">Inclua todos os presentes:</strong> Garanta que todos os participantes recebam uma cópia.</span>
            </p>
            <p className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span><strong className="text-foreground">Nomeie o arquivo:</strong> Use um padrão como "Ata_30_Projeto_Shopping_27-11-2024.pdf".</span>
            </p>
          </div>
        </section>
      </div>
    </DocsLayout>
  );
}

// Página: Personalização
function PersonalizacaoPage() {
  return (
    <DocsLayout
      title="Personalização"
      description="Configure logo, empresa e tópicos padrão para suas atas"
      keywords="personalizar ata, configurar logo ata, tópicos padrão, customização ata, identidade visual ata"
      previousPage={{ title: 'Organização e colaboração', slug: 'organizacao-e-colaboracao' }}
      nextPage={{ title: 'Guia de boas práticas', slug: 'boas-praticas-atas' }}
    >
      <div className="space-y-8">
        <section>
          <p className="mb-6 text-lg leading-relaxed text-muted-foreground">
            O Atas Digitais permite personalizar a aparência das suas atas e configurar
            padrões que facilitam o preenchimento no dia a dia.
          </p>
        </section>

        {/* Como acessar */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Como acessar</h2>
          <div className="space-y-4">
            <div className="flex items-start gap-4 rounded-lg border border-border p-4">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">1</span>
              <div>
                <h4 className="font-semibold text-foreground">Clique na sua foto de perfil</h4>
                <p className="mt-1 text-sm text-muted-foreground">No canto superior direito da tela, clique no avatar com suas iniciais.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 rounded-lg border border-border p-4">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">2</span>
              <div>
                <h4 className="font-semibold text-foreground">Clique em "Personalização"</h4>
                <p className="mt-1 text-sm text-muted-foreground">No menu que abrir, selecione a opção Personalização.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Mockup: Menu do usuário */}
        <section className="rounded-lg border border-border bg-background overflow-hidden">
          {/* Header mockup */}
          <div className="flex items-center justify-between border-b border-border bg-white px-4 py-3">
            <img src="/logo-stant.svg" alt="Stant" className="h-6" />
            <div className="relative">
              <div className="flex size-9 items-center justify-center rounded-full borderborder-primary/20 bg-primary/10 text-sm font-semibold text-primary">
                JS
              </div>
            </div>
          </div>
          {/* Dropdown aberto */}
          <div className="flex justify-end bg-muted/30 p-4">
            <div className="w-56 overflow-hidden rounded-lg border border-border bg-white shadow-lg">
              <div className="border-b border-border px-3 py-2">
                <p className="text-sm font-medium text-foreground">Minha conta</p>
                <p className="text-xs text-muted-foreground">usuario@email.com</p>
              </div>
              <div className="py-1">
                <div className="flex items-center gap-2 bg-primary/10 px-3 py-2">
                  <Settings className="size-4 text-primary" />
                  <span className="text-sm font-medium text-primary">Personalização</span>
                </div>
              </div>
              <div className="border-t border-border py-1">
                <div className="flex items-center gap-2 px-3 py-2 text-destructive">
                  <LogOut className="size-4" />
                  <span className="text-sm">Sair</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Imagem de Cabeçalho */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Imagem de Cabeçalho das Atas</h2>
          <p className="mb-4 text-muted-foreground">
            Configure uma logo ou imagem que será exibida no cabeçalho de todas as atas exportadas em PDF.
            Ideal para incluir a identidade visual da sua empresa.
          </p>
          <div className="rounded-lg border border-border bg-background p-4">
            <div className="rounded-lg border border-border bg-white p-4">
              <div className="mb-3 flex items-center gap-2">
                <ImageIcon className="size-5 text-primary" />
                <h4 className="font-semibold text-foreground">Imagem de Cabeçalho das Atas</h4>
              </div>
              <p className="mb-4 text-xs text-muted-foreground">
                Esta imagem será exibida no topo de todas as suas atas geradas.
                Recomendamos usar a logo ou identidade visual da sua empresa.
              </p>
              <div className="flex items-center justify-center rounded-lg borderborder-dashed border-border bg-muted/30 py-8">
                <div className="text-center">
                  <Upload className="mx-auto size-8 text-muted-foreground/50" />
                  <p className="mt-2 text-sm text-muted-foreground">Arraste uma imagem ou clique para enviar</p>
                  <p className="mt-1 text-xs text-muted-foreground/70">Tamanho recomendado: 1200x300px (máx. 5MB)</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pré-visualização */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Como aparece no PDF</h2>
          <div className="rounded-lg border border-border bg-gray-100 p-6">
            {/* Mockup do PDF */}
            <div className="mx-auto max-w-2xl rounded-lg border border-border bg-white p-6 shadow-sm">
              {/* Cabeçalho da ata */}
              <div className="mb-4 border-b border-gray-300 pb-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h1 className="mb-1 text-lg font-bold text-gray-900">ATA DE REUNIÃO N.º 3 | Residencial Vila Nova</h1>
                    <p className="text-xs text-gray-600">Data da reunião | 27 de novembro de 2024</p>
                  </div>
                  <div className="flex h-14 w-32 shrink-0 items-center justify-center rounded borderborder-dashed border-primary/40 bg-primary/5">
                    <span className="text-xs font-medium text-primary">Sua Logo</span>
                  </div>
                </div>
                {/* Local e participantes */}
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <div className="flex items-center gap-2 rounded-md bg-gray-50 p-2">
                    <MapPin className="size-4 text-gray-700" />
                    <div>
                      <p className="text-xs font-semibold uppercase text-gray-600">Local</p>
                      <p className="text-xs font-medium">Sala de Reuniões</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 rounded-md bg-gray-50 p-2">
                    <User className="size-4 text-gray-700" />
                    <div>
                      <p className="text-xs font-semibold uppercase text-gray-600">Participantes</p>
                      <p className="text-xs font-medium">5 pessoas</p>
                    </div>
                  </div>
                </div>
              </div>
              {/* Conteúdo placeholder */}
              <div className="space-y-2">
                <div className="h-3 w-3/4 rounded bg-gray-100"></div>
                <div className="h-3 w-full rounded bg-gray-100"></div>
                <div className="h-3 w-5/6 rounded bg-gray-100"></div>
              </div>
            </div>
          </div>
        </section>

        {/* Empresa Padrão */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Empresa Padrão</h2>
          <p className="mb-4 text-muted-foreground">
            Configure o nome da empresa que será pré-preenchido automaticamente ao adicionar
            novos participantes em atas. Economize tempo evitando digitação repetitiva.
          </p>
          <div className="rounded-lg border border-border bg-background p-4">
            <div className="rounded-lg border border-border bg-white p-4">
              <div className="mb-3 flex items-center gap-2">
                <Building2 className="size-5 text-primary" />
                <h4 className="font-semibold text-foreground">Empresa Padrão</h4>
              </div>
              <p className="mb-4 text-xs text-muted-foreground">
                Configure o nome da empresa que será pré-preenchido ao adicionar novos participantes em atas.
              </p>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground">Nome da empresa</label>
                <div className="rounded-md border border-border bg-muted/30 px-3 py-2">
                  <span className="text-sm text-foreground">Construtora ABC Ltda</span>
                </div>
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                Este valor será usado como sugestão ao criar novos participantes.
              </p>
            </div>
          </div>
        </section>

        {/* Tópicos Padrão */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Tópicos Padrão</h2>
          <p className="mb-4 text-muted-foreground">
            Configure tópicos que serão criados automaticamente em todas as novas atas.
            Útil para padronizar a estrutura das suas reuniões.
          </p>
          <div className="rounded-lg border border-border bg-background p-4">
            <div className="rounded-lg border border-border bg-white p-4">
              <div className="mb-3 flex items-center gap-2">
                <FolderTree className="size-5 text-primary" />
                <h4 className="font-semibold text-foreground">Tópicos Padrão</h4>
              </div>
              <p className="mb-4 text-xs text-muted-foreground">
                Configure tópicos que aparecerão automaticamente em todas as novas atas criadas.
              </p>
              {/* Campo para adicionar */}
              <div className="mb-4 flex gap-2">
                <div className="flex-1 rounded-md border border-border bg-muted/30 px-3 py-2">
                  <span className="text-sm text-muted-foreground">Ex: Abertura, Discussão, Encerramento...</span>
                </div>
                <button className="flex size-9 items-center justify-center rounded-md bg-primary text-white">
                  <Plus className="size-4" />
                </button>
              </div>
              {/* Tópicos adicionados */}
              <div className="space-y-2">
                <div className="flex items-center justify-between rounded-md bg-muted/50 p-3">
                  <span className="text-sm font-medium">Abertura</span>
                  <button className="rounded p-1 opacity-50 hover:bg-background hover:opacity-100">
                    <X className="size-4" />
                  </button>
                </div>
                <div className="flex items-center justify-between rounded-md bg-muted/50 p-3">
                  <span className="text-sm font-medium">Pendências da semana anterior</span>
                  <button className="rounded p-1 opacity-50 hover:bg-background hover:opacity-100">
                    <X className="size-4" />
                  </button>
                </div>
                <div className="flex items-center justify-between rounded-md bg-muted/50 p-3">
                  <span className="text-sm font-medium">Novos assuntos</span>
                  <button className="rounded p-1 opacity-50 hover:bg-background hover:opacity-100">
                    <X className="size-4" />
                  </button>
                </div>
                <div className="flex items-center justify-between rounded-md bg-muted/50 p-3">
                  <span className="text-sm font-medium">Encerramento</span>
                  <button className="rounded p-1 opacity-50 hover:bg-background hover:opacity-100">
                    <X className="size-4" />
                  </button>
                </div>
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                Esses tópicos serão criados automaticamente quando você criar uma nova ata.
              </p>
            </div>
          </div>
        </section>

        {/* Dica */}
        <section className="rounded-lg border border-primary/20 bg-primary/5 p-6">
          <h3 className="mb-3 text-lg font-semibold text-foreground">Dica: Padronize suas reuniões</h3>
          <p className="text-sm text-muted-foreground">
            Configurar tópicos padrão ajuda a manter uma estrutura consistente em todas as reuniões.
            Assim, você não esquece de abordar assuntos importantes e a equipe já sabe o que esperar.
          </p>
        </section>
      </div>
    </DocsLayout>
  );
}

// Página: Boas práticas para atas
function BoasPraticasAtasPage() {
  return (
    <DocsLayout
      title="Guia de boas práticas"
      description="Princípios e técnicas para criar atas que realmente funcionam"
      keywords="boas práticas ata, dicas ata reunião, ata eficiente, melhorar documentação reunião, qualidade atas"
      previousPage={{ title: 'Personalização', slug: 'personalizacao' }}
      nextPage={{ title: 'Novidades', slug: 'novidades' }}
    >
      <div className="space-y-8">
        {/* Introdução */}
        <section>
          <p className="text-lg leading-relaxed text-muted-foreground">
            A diferença entre uma ata útil e uma que ninguém consulta está nos detalhes.
            Este guia reúne práticas testadas para criar documentação que <strong className="text-foreground">facilita o acompanhamento</strong> e <strong className="text-foreground">evita mal-entendidos</strong>.
          </p>
        </section>

        {/* Para que serve */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Para que serve este guia?</h2>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-border p-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="flex size-8 items-center justify-center rounded-lg bg-blue-100">
                  <Target size={16} className="text-blue-600" />
                </div>
                <h4 className="font-semibold text-foreground">Clareza nas pendências</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Escreva itens que qualquer pessoa entenda, mesmo sem ter participado da reunião.
              </p>
            </div>
            <div className="rounded-lg border border-border p-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="flex size-8 items-center justify-center rounded-lg bg-green-100">
                  <TrendingUp size={16} className="text-green-600" />
                </div>
                <h4 className="font-semibold text-foreground">Acompanhamento eficiente</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Desenvolvimentos detalhados permitem cobrar sem depender da memória.
              </p>
            </div>
            <div className="rounded-lg border border-border p-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="flex size-8 items-center justify-center rounded-lg bg-orange-100">
                  <Shield size={16} className="text-orange-600" />
                </div>
                <h4 className="font-semibold text-foreground">Evidência documental</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Histórico completo serve como prova em disputas e auditorias.
              </p>
            </div>
            <div className="rounded-lg border border-border p-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="flex size-8 items-center justify-center rounded-lg bg-purple-100">
                  <Zap size={16} className="text-purple-600" />
                </div>
                <h4 className="font-semibold text-foreground">Reuniões produtivas</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Começar sabendo o que cobrar torna cada encontro mais objetivo.
              </p>
            </div>
          </div>
        </section>

        {/* Os 4 princípios */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Os 4 princípios de uma boa ata</h2>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-xl border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-white p-5">
              <div className="mb-3 flex items-center gap-3">
                <span className="flex size-10 items-center justify-center rounded-full bg-blue-500 text-lg font-bold text-white">1</span>
                <h3 className="text-lg font-semibold text-foreground">Específico</h3>
              </div>
              <p className="text-sm text-muted-foreground">Descreva exatamente o que precisa ser feito, não o que "mais ou menos" precisa acontecer.</p>
              <div className="mt-3 space-y-2 text-xs">
                <div className="flex items-center gap-2 rounded bg-white/80 px-2 py-1.5">
                  <X size={12} className="text-destructive" />
                  <span className="text-muted-foreground">"Resolver problema"</span>
                </div>
                <div className="flex items-center gap-2 rounded bg-white/80 px-2 py-1.5">
                  <Check size={12} className="text-green-600" />
                  <span className="text-muted-foreground">"Trocar disjuntor QD-02 que está aquecendo"</span>
                </div>
              </div>
            </div>

            <div className="rounded-xl border-2 border-green-200 bg-gradient-to-br from-green-50 to-white p-5">
              <div className="mb-3 flex items-center gap-3">
                <span className="flex size-10 items-center justify-center rounded-full bg-green-500 text-lg font-bold text-white">2</span>
                <h3 className="text-lg font-semibold text-foreground">Um responsável</h3>
              </div>
              <p className="text-sm text-muted-foreground">Quando todo mundo é responsável, ninguém é. Defina uma pessoa para cobrar.</p>
              <div className="mt-3 space-y-2 text-xs">
                <div className="flex items-center gap-2 rounded bg-white/80 px-2 py-1.5">
                  <X size={12} className="text-destructive" />
                  <span className="text-muted-foreground">"João e Maria"</span>
                </div>
                <div className="flex items-center gap-2 rounded bg-white/80 px-2 py-1.5">
                  <Check size={12} className="text-green-600" />
                  <span className="text-muted-foreground">"João Silva (Eng. Elétrico)"</span>
                </div>
              </div>
            </div>

            <div className="rounded-xl border-2 border-orange-200 bg-gradient-to-br from-orange-50 to-white p-5">
              <div className="mb-3 flex items-center gap-3">
                <span className="flex size-10 items-center justify-center rounded-full bg-orange-500 text-lg font-bold text-white">3</span>
                <h3 className="text-lg font-semibold text-foreground">Com prazo</h3>
              </div>
              <p className="text-sm text-muted-foreground">Data concreta, não "em breve". Prazo realista que permita cobrança objetiva.</p>
              <div className="mt-3 space-y-2 text-xs">
                <div className="flex items-center gap-2 rounded bg-white/80 px-2 py-1.5">
                  <X size={12} className="text-destructive" />
                  <span className="text-muted-foreground">"O mais rápido possível"</span>
                </div>
                <div className="flex items-center gap-2 rounded bg-white/80 px-2 py-1.5">
                  <Check size={12} className="text-green-600" />
                  <span className="text-muted-foreground">"Até 15/02/2025"</span>
                </div>
              </div>
            </div>

            <div className="rounded-xl border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-white p-5">
              <div className="mb-3 flex items-center gap-3">
                <span className="flex size-10 items-center justify-center rounded-full bg-purple-500 text-lg font-bold text-white">4</span>
                <h3 className="text-lg font-semibold text-foreground">Atualizado</h3>
              </div>
              <p className="text-sm text-muted-foreground">Cada reunião deve registrar o que mudou, não apenas repetir o status anterior.</p>
              <div className="mt-3 space-y-2 text-xs">
                <div className="flex items-center gap-2 rounded bg-white/80 px-2 py-1.5">
                  <X size={12} className="text-destructive" />
                  <span className="text-muted-foreground">"Em andamento"</span>
                </div>
                <div className="flex items-center gap-2 rounded bg-white/80 px-2 py-1.5">
                  <Check size={12} className="text-green-600" />
                  <span className="text-muted-foreground">"Orçamento aprovado. Execução dia 20/01"</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Comparativo visual melhorado */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Compare: item mal escrito vs bem escrito</h2>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border-2 border-destructive/30 bg-destructive/5 p-4">
              <div className="mb-3 flex items-center gap-2">
                <X size={16} className="text-destructive" />
                <span className="font-semibold text-destructive">Evite</span>
              </div>
              <div className="rounded-lg border border-border bg-background p-4 shadow-sm">
                <span className="rounded-md bg-yellow-100 px-1.5 py-0.5 text-xs font-medium text-yellow-700">Pendente</span>
                <p className="my-3 font-medium text-foreground">Resolver problema técnico</p>
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span>João e Maria</span>
                  <span className="text-muted-foreground/50">Sem prazo</span>
                </div>
                <div className="mt-3 border-t border-border pt-3">
                  <p className="text-xs italic text-muted-foreground">"Em andamento"</p>
                </div>
              </div>
              <ul className="mt-3 space-y-1 text-xs text-destructive">
                <li className="flex items-center gap-1.5"><X size={10} /> Descrição vaga</li>
                <li className="flex items-center gap-1.5"><X size={10} /> Dois responsáveis</li>
                <li className="flex items-center gap-1.5"><X size={10} /> Sem prazo</li>
                <li className="flex items-center gap-1.5"><X size={10} /> Desenvolvimento genérico</li>
              </ul>
            </div>

            <div className="rounded-lg border-2 border-green-300 bg-green-50 p-4">
              <div className="mb-3 flex items-center gap-2">
                <Check size={16} className="text-green-600" />
                <span className="font-semibold text-green-700">Correto</span>
              </div>
              <div className="rounded-lg border border-border bg-background p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <span className="rounded-md bg-yellow-100 px-1.5 py-0.5 text-xs font-medium text-yellow-700">Pendente</span>
                  <span className="text-xs text-muted-foreground">Ata 28</span>
                </div>
                <p className="my-3 font-medium text-foreground">Substituir disjuntor do quadro QD-02 (aquecimento)</p>
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span className="font-medium">Carlos Silva</span>
                  <span className="font-medium text-foreground">15/02/2025</span>
                </div>
                <div className="mt-3 border-t border-border pt-3">
                  <p className="text-xs text-muted-foreground">"Disjuntor adquirido. Troca programada para 12/02."</p>
                </div>
              </div>
              <ul className="mt-3 space-y-1 text-xs text-green-700">
                <li className="flex items-center gap-1.5"><Check size={10} /> Descrição específica</li>
                <li className="flex items-center gap-1.5"><Check size={10} /> Responsável único</li>
                <li className="flex items-center gap-1.5"><Check size={10} /> Prazo definido</li>
                <li className="flex items-center gap-1.5"><Check size={10} /> Desenvolvimento detalhado</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Desenvolvimentos - tabela */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Desenvolvimentos: o que escrever</h2>
          <p className="mb-4 text-muted-foreground">Desenvolvimentos vagos não ajudam ninguém. Compare:</p>
          <div className="overflow-hidden rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="w-1/3 p-3 text-left font-semibold text-destructive">Evite</th>
                  <th className="p-3 text-left font-semibold text-green-700">Prefira</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border">
                  <td className="p-3 text-muted-foreground">"Em andamento"</td>
                  <td className="p-3 text-muted-foreground">"Orçamento recebido. Aguardando aprovação até 20/01."</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 text-muted-foreground">"Sem novidades"</td>
                  <td className="p-3 text-muted-foreground">"Fornecedor informou atraso de 5 dias. Nova previsão: 25/01."</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 text-muted-foreground">"Pendente"</td>
                  <td className="p-3 text-muted-foreground">"Aguardando liberação da área pela equipe de segurança."</td>
                </tr>
                <tr>
                  <td className="p-3 text-muted-foreground">"OK"</td>
                  <td className="p-3 text-muted-foreground">"Material entregue e conferido. Instalação inicia amanhã."</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Fluxo de reunião */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Fluxo recomendado</h2>
          <div className="rounded-lg border border-border bg-background p-5">
            <div className="relative space-y-0 border-l-2 border-slate-200 pl-6">
              <div className="relative pb-5">
                <div className="absolute -left-[36px] top-1/2 -translate-y-1/2 flex size-6 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-600">1</div>
                <div className="rounded-md bg-blue-50 p-3">
                  <h4 className="mb-1 font-semibold text-foreground">Antes da reunião</h4>
                  <p className="text-sm text-muted-foreground">Abra o <strong>Mapa de Atas</strong> e revise as pendências abertas. Saiba o que cobrar.</p>
                </div>
              </div>
              <div className="relative pb-5">
                <div className="absolute -left-[36px] top-1/2 -translate-y-1/2 flex size-6 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-600">2</div>
                <div className="rounded-md bg-blue-50 p-3">
                  <h4 className="mb-1 font-semibold text-foreground">Durante a reunião</h4>
                  <p className="text-sm text-muted-foreground"><strong>Clone a última ata</strong> e registre desenvolvimentos em tempo real. Adicione novos itens conforme surgem.</p>
                </div>
              </div>
              <div className="relative">
                <div className="absolute -left-[36px] top-1/2 -translate-y-1/2 flex size-6 items-center justify-center rounded-full bg-green-100 text-xs font-bold text-green-600">3</div>
                <div className="rounded-md bg-green-50 p-3">
                  <h4 className="mb-1 font-semibold text-foreground">Após a reunião</h4>
                  <p className="text-sm text-muted-foreground">Revise, <strong>finalize a ata</strong> e exporte o PDF para distribuir aos participantes.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mapa de Atas */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Use o Mapa de Atas</h2>
          <p className="mb-4 text-muted-foreground">O Mapa de Atas é sua ferramenta de gestão. Use para:</p>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="flex items-start gap-3 rounded-lg border border-border p-4">
              <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-orange-100">
                <AlertCircle size={16} className="text-orange-600" />
              </div>
              <div>
                <h4 className="font-semibold text-foreground">Identificar itens travados</h4>
                <p className="text-xs text-muted-foreground">Pendências abertas há muito tempo aparecem em destaque.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-border p-4">
              <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-blue-100">
                <ClipboardList size={16} className="text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-foreground">Preparar reuniões</h4>
                <p className="text-xs text-muted-foreground">Chegue sabendo exatamente o que precisa ser discutido.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-border p-4">
              <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-green-100">
                <Presentation size={16} className="text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-foreground">Apresentar à gestão</h4>
                <p className="text-xs text-muted-foreground">Exporte em PDF para mostrar o panorama geral do projeto.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-border p-4">
              <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-purple-100">
                <FileSearch size={16} className="text-purple-600" />
              </div>
              <div>
                <h4 className="font-semibold text-foreground">Auditar histórico</h4>
                <p className="text-xs text-muted-foreground">Consulte quando e como cada decisão foi tomada.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Checklist final */}
        <section className="rounded-xl border-2 border-primary/20 bg-primary/5 p-6">
          <h2 className="mb-4 text-lg font-semibold text-foreground">Checklist antes de finalizar</h2>
          <div className="grid gap-2 md:grid-cols-2">
            <div className="flex items-center gap-2 rounded-lg bg-background px-3 py-2">
              <div className="flex size-5 items-center justify-center rounded border border-primary/30">
                <Check size={12} className="text-primary" />
              </div>
              <span className="text-sm text-muted-foreground">Cada item tem descrição clara?</span>
            </div>
            <div className="flex items-center gap-2 rounded-lg bg-background px-3 py-2">
              <div className="flex size-5 items-center justify-center rounded border border-primary/30">
                <Check size={12} className="text-primary" />
              </div>
              <span className="text-sm text-muted-foreground">Cada item tem UM responsável?</span>
            </div>
            <div className="flex items-center gap-2 rounded-lg bg-background px-3 py-2">
              <div className="flex size-5 items-center justify-center rounded border border-primary/30">
                <Check size={12} className="text-primary" />
              </div>
              <span className="text-sm text-muted-foreground">Cada item tem prazo definido?</span>
            </div>
            <div className="flex items-center gap-2 rounded-lg bg-background px-3 py-2">
              <div className="flex size-5 items-center justify-center rounded border border-primary/30">
                <Check size={12} className="text-primary" />
              </div>
              <span className="text-sm text-muted-foreground">Itens resolvidos foram marcados?</span>
            </div>
            <div className="flex items-center gap-2 rounded-lg bg-background px-3 py-2">
              <div className="flex size-5 items-center justify-center rounded border border-primary/30">
                <Check size={12} className="text-primary" />
              </div>
              <span className="text-sm text-muted-foreground">Todos os participantes listados?</span>
            </div>
            <div className="flex items-center gap-2 rounded-lg bg-background px-3 py-2">
              <div className="flex size-5 items-center justify-center rounded border border-primary/30">
                <Check size={12} className="text-primary" />
              </div>
              <span className="text-sm text-muted-foreground">Desenvolvimentos estão detalhados?</span>
            </div>
          </div>
        </section>

        {/* Dica final */}
        <section className="rounded-lg border border-border bg-muted/30 p-6">
          <div className="flex items-start gap-3">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-yellow-100">
              <Lightbulb size={20} className="text-yellow-600" />
            </div>
            <div>
              <h3 className="mb-2 text-lg font-semibold text-foreground">Dica de ouro</h3>
              <p className="text-muted-foreground">
                <strong className="text-foreground">Teste do leitor externo:</strong> se alguém que não estava na reunião ler a ata, vai entender o que precisa ser feito?
                Se a resposta for não, revise antes de finalizar.
              </p>
            </div>
          </div>
        </section>
      </div>
    </DocsLayout>
  );
}

// Página: Organização e colaboração (unificada)
function OrganizacaoColaboracaoPage() {
  return (
    <DocsLayout
      title="Organização e colaboração"
      description="Como estruturar projetos e trabalhar em equipe"
      keywords="organização projetos, colaboração equipe, trabalho em equipe atas, estruturar projetos"
      previousPage={{ title: 'Convidar equipe', slug: 'convidar-equipe' }}
      nextPage={{ title: 'Personalização', slug: 'personalizacao' }}
    >
      <div className="space-y-8">
        {/* Introdução */}
        <section>
          <p className="mb-6 text-lg leading-relaxed text-muted-foreground">
            A forma como você organiza seus projetos e colabora com a equipe impacta diretamente na
            facilidade de encontrar informações e acompanhar o andamento dos projetos.
          </p>
        </section>

        {/* Para que serve */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">Para que serve?</h2>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-border p-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="flex size-8 items-center justify-center rounded-lg bg-blue-100">
                  <FolderTree size={16} className="text-blue-600" />
                </div>
                <h4 className="font-semibold text-foreground">Estruturar projetos</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Aprenda estratégias para organizar projetos de forma eficiente.
              </p>
            </div>
            <div className="rounded-lg border border-border p-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="flex size-8 items-center justify-center rounded-lg bg-green-100">
                  <Users size={16} className="text-green-600" />
                </div>
                <h4 className="font-semibold text-foreground">Trabalhar em equipe</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Defina papéis e fluxos de trabalho colaborativos.
              </p>
            </div>
            <div className="rounded-lg border border-border p-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="flex size-8 items-center justify-center rounded-lg bg-orange-100">
                  <Edit3 size={16} className="text-orange-600" />
                </div>
                <h4 className="font-semibold text-foreground">Padronizar nomenclaturas</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Use nomes consistentes para facilitar a busca.
              </p>
            </div>
            <div className="rounded-lg border border-border p-4">
              <div className="mb-2 flex items-center gap-2">
                <div className="flex size-8 items-center justify-center rounded-lg bg-purple-100">
                  <Share2 size={16} className="text-purple-600" />
                </div>
                <h4 className="font-semibold text-foreground">Compartilhar com clientes</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Aumente a transparência adicionando representantes do cliente.
              </p>
            </div>
          </div>
        </section>

        {/* Estratégias de organização - Visual */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">
            Estratégias de organização de projetos
          </h2>
          <div className="grid gap-4 md:grid-cols-3">
            {/* Por projeto */}
            <div className="rounded-lg borderborder-blue-200 bg-blue-50 p-4">
              <div className="mb-3 flex items-center gap-2">
                <div className="flex size-8 items-center justify-center rounded-lg bg-blue-500 text-white">
                  <Building2 size={16} />
                </div>
                <span className="font-semibold text-foreground">Por projeto</span>
              </div>
              <p className="mb-3 text-xs text-muted-foreground">
                Cada iniciativa é um projeto separado (obras, entregas, etc.).
              </p>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 rounded-md bg-white/80 px-2 py-1.5 text-xs">
                  <div className="size-2 rounded-full bg-blue-500" />
                  <span>Shopping Norte - Fase 2</span>
                </div>
                <div className="flex items-center gap-2 rounded-md bg-white/80 px-2 py-1.5 text-xs">
                  <div className="size-2 rounded-full bg-blue-500" />
                  <span>Edifício Comercial Centro</span>
                </div>
                <div className="flex items-center gap-2 rounded-md bg-white/80 px-2 py-1.5 text-xs">
                  <div className="size-2 rounded-full bg-blue-500" />
                  <span>Reforma Hospital Municipal</span>
                </div>
              </div>
            </div>

            {/* Por cliente */}
            <div className="rounded-lg borderborder-green-200 bg-green-50 p-4">
              <div className="mb-3 flex items-center gap-2">
                <div className="flex size-8 items-center justify-center rounded-lg bg-green-500 text-white">
                  <Users size={16} />
                </div>
                <span className="font-semibold text-foreground">Por cliente</span>
              </div>
              <p className="mb-3 text-xs text-muted-foreground">
                Útil para contratos de longo prazo com o mesmo cliente.
              </p>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 rounded-md bg-white/80 px-2 py-1.5 text-xs">
                  <div className="size-2 rounded-full bg-green-500" />
                  <span>Petrobras - Manutenção 2025</span>
                </div>
                <div className="flex items-center gap-2 rounded-md bg-white/80 px-2 py-1.5 text-xs">
                  <div className="size-2 rounded-full bg-green-500" />
                  <span>Vale - Expansão Unidade 3</span>
                </div>
              </div>
            </div>

            {/* Por contrato */}
            <div className="rounded-lg borderborder-purple-200 bg-purple-50 p-4">
              <div className="mb-3 flex items-center gap-2">
                <div className="flex size-8 items-center justify-center rounded-lg bg-purple-500 text-white">
                  <FileText size={16} />
                </div>
                <span className="font-semibold text-foreground">Por contrato</span>
              </div>
              <p className="mb-3 text-xs text-muted-foreground">
                Quando o mesmo cliente tem múltiplos contratos.
              </p>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 rounded-md bg-white/80 px-2 py-1.5 text-xs">
                  <div className="size-2 rounded-full bg-purple-500" />
                  <span>2024-001 - Fiscalização BR-101</span>
                </div>
                <div className="flex items-center gap-2 rounded-md bg-white/80 px-2 py-1.5 text-xs">
                  <div className="size-2 rounded-full bg-purple-500" />
                  <span>2024-002 - Supervisão Porto</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Comparativo visual: Nome ruim vs Nome bom */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">
            Compare: nomes de projeto
          </h2>
          <div className="grid gap-4 md:grid-cols-2">
            {/* Nome RUIM */}
            <div className="rounded-lg borderborder-destructive/30 bg-destructive/5 p-4">
              <div className="mb-3 flex items-center gap-2">
                <span className="rounded-md bg-destructive/20 px-2 py-0.5 text-xs font-medium text-destructive">Evite</span>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-3 rounded-md border border-border bg-white p-2.5">
                  <div className="size-8 shrink-0 rounded bg-gray-200" />
                  <div>
                    <p className="text-xs font-medium">Obra 1</p>
                    <p className="text-xs text-muted-foreground">Sem descrição</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 rounded-md border border-border bg-white p-2.5">
                  <div className="size-8 shrink-0 rounded bg-gray-200" />
                  <div>
                    <p className="text-xs font-medium">Cliente ABC</p>
                    <p className="text-xs text-muted-foreground">Sem descrição</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 rounded-md border border-border bg-white p-2.5">
                  <div className="size-8 shrink-0 rounded bg-gray-200" />
                  <div>
                    <p className="text-xs font-medium">Projeto novo</p>
                    <p className="text-xs text-muted-foreground">Sem descrição</p>
                  </div>
                </div>
              </div>
              <ul className="mt-3 space-y-1 text-xs text-destructive">
                <li className="flex items-center gap-1.5"><X size={10} /> Nomes genéricos e confusos</li>
                <li className="flex items-center gap-1.5"><X size={10} /> Sem imagem de identificação</li>
                <li className="flex items-center gap-1.5"><X size={10} /> Sem descrição ou contexto</li>
              </ul>
            </div>

            {/* Nome BOM */}
            <div className="rounded-lg borderborder-green-300 bg-green-50 p-4">
              <div className="mb-3 flex items-center gap-2">
                <span className="rounded-md bg-green-200 px-2 py-0.5 text-xs font-medium text-green-700">Correto</span>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-3 rounded-md border border-border bg-white p-2.5">
                  <div className="size-8 shrink-0 rounded bg-gradient-to-br from-blue-400 to-blue-600" />
                  <div>
                    <p className="text-xs font-medium">Obra Shopping Norte - Fase 2</p>
                    <p className="text-xs text-muted-foreground">Av. Brasil, 1500 - Centro</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 rounded-md border border-border bg-white p-2.5">
                  <div className="size-8 shrink-0 rounded bg-gradient-to-br from-green-400 to-green-600" />
                  <div>
                    <p className="text-xs font-medium">ABC Engenharia - Manutenção 2025</p>
                    <p className="text-xs text-muted-foreground">Contrato anual de manutenção</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 rounded-md border border-border bg-white p-2.5">
                  <div className="size-8 shrink-0 rounded bg-gradient-to-br from-orange-400 to-orange-600" />
                  <div>
                    <p className="text-xs font-medium">Fiscalização BR-101 Lote 3</p>
                    <p className="text-xs text-muted-foreground">Contrato 2024-001</p>
                  </div>
                </div>
              </div>
              <ul className="mt-3 space-y-1 text-xs text-green-700">
                <li className="flex items-center gap-1.5"><Check size={10} /> Nomes descritivos e únicos</li>
                <li className="flex items-center gap-1.5"><Check size={10} /> Imagem para identificação visual</li>
                <li className="flex items-center gap-1.5"><Check size={10} /> Descrição com localização/contrato</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Papéis típicos - Visual aprimorado */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">
            Papéis típicos em uma equipe
          </h2>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-lg border border-border bg-gradient-to-b from-blue-50 to-white p-4">
              <div className="mb-3 flex size-10 items-center justify-center rounded-lg bg-blue-500 text-white">
                <Edit3 size={18} />
              </div>
              <h4 className="mb-2 font-semibold text-foreground">Secretário da reunião</h4>
              <p className="text-xs text-muted-foreground">
                Responsável por registrar os assuntos durante a reunião. Cria a ata, adiciona participantes e registra desenvolvimentos em tempo real.
              </p>
              <div className="mt-3 flex flex-wrap gap-1">
                <span className="rounded-md bg-blue-100 px-1.5 py-0.5 text-xs text-blue-700">Criar atas</span>
                <span className="rounded-md bg-blue-100 px-1.5 py-0.5 text-xs text-blue-700">Editar itens</span>
                <span className="rounded-md bg-blue-100 px-1.5 py-0.5 text-xs text-blue-700">Registrar</span>
              </div>
            </div>

            <div className="rounded-lg border border-border bg-gradient-to-b from-green-50 to-white p-4">
              <div className="mb-3 flex size-10 items-center justify-center rounded-lg bg-green-500 text-white">
                <BarChart3 size={18} />
              </div>
              <h4 className="mb-2 font-semibold text-foreground">Coordenador do projeto</h4>
              <p className="text-xs text-muted-foreground">
                Acompanha o andamento geral pelo Mapa de Atas. Cobra responsáveis por itens atrasados e garante a distribuição das atas.
              </p>
              <div className="mt-3 flex flex-wrap gap-1">
                <span className="rounded-md bg-green-100 px-1.5 py-0.5 text-xs text-green-700">Mapa de Atas</span>
                <span className="rounded-md bg-green-100 px-1.5 py-0.5 text-xs text-green-700">Acompanhar</span>
                <span className="rounded-md bg-green-100 px-1.5 py-0.5 text-xs text-green-700">Cobrar</span>
              </div>
            </div>

            <div className="rounded-lg border border-border bg-gradient-to-b from-orange-50 to-white p-4">
              <div className="mb-3 flex size-10 items-center justify-center rounded-lg bg-orange-500 text-white">
                <Wrench size={18} />
              </div>
              <h4 className="mb-2 font-semibold text-foreground">Responsáveis técnicos</h4>
              <p className="text-xs text-muted-foreground">
                Consultam as atas para verificar suas pendências e prazos. Podem adicionar desenvolvimentos aos itens sob sua responsabilidade.
              </p>
              <div className="mt-3 flex flex-wrap gap-1">
                <span className="rounded-md bg-orange-100 px-1.5 py-0.5 text-xs text-orange-700">Consultar</span>
                <span className="rounded-md bg-orange-100 px-1.5 py-0.5 text-xs text-orange-700">Pendências</span>
                <span className="rounded-md bg-orange-100 px-1.5 py-0.5 text-xs text-orange-700">Desenvolver</span>
              </div>
            </div>
          </div>
        </section>

        {/* Fluxo de trabalho em equipe - Visual timeline */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">
            Fluxo típico de trabalho em equipe
          </h2>
          <div className="rounded-lg border border-border bg-background p-5">
            <div className="relative space-y-0 border-l-2 border-slate-200 pl-6">
              {/* Antes */}
              <div className="relative pb-6">
                <div className="absolute -left-[37px] flex size-6 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-600">1</div>
                <div className="rounded-md border border-border bg-muted/30 p-3">
                  <div className="mb-1 flex items-center gap-2">
                    <span className="text-xs font-semibold text-primary">Antes da reunião</span>
                    <span className="rounded-md bg-blue-100 px-1.5 py-0.5 text-xs text-blue-700">Secretário</span>
                  </div>
                  <p className="text-xs text-muted-foreground">Clona a última ata para criar a nova e revisa os itens pendentes que serão discutidos.</p>
                  <div className="mt-2 flex items-center gap-2">
                    <div className="flex items-center gap-1 rounded bg-white px-1.5 py-0.5 text-xs text-muted-foreground">
                      <Copy size={10} />
                      <span>Clonar ata</span>
                    </div>
                    <div className="flex items-center gap-1 rounded bg-white px-1.5 py-0.5 text-xs text-muted-foreground">
                      <Eye size={10} />
                      <span>Revisar pendências</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Durante */}
              <div className="relative pb-6">
                <div className="absolute -left-[37px] flex size-6 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-600">2</div>
                <div className="rounded-md border border-border bg-muted/30 p-3">
                  <div className="mb-1 flex items-center gap-2">
                    <span className="text-xs font-semibold text-primary">Durante a reunião</span>
                    <span className="rounded-md bg-blue-100 px-1.5 py-0.5 text-xs text-blue-700">Secretário</span>
                  </div>
                  <p className="text-xs text-muted-foreground">Registra desenvolvimentos e novos itens em tempo real conforme os assuntos são discutidos.</p>
                  <div className="mt-2 flex items-center gap-2">
                    <div className="flex items-center gap-1 rounded bg-white px-1.5 py-0.5 text-xs text-muted-foreground">
                      <Edit3 size={10} />
                      <span>Registrar itens</span>
                    </div>
                    <div className="flex items-center gap-1 rounded bg-white px-1.5 py-0.5 text-xs text-muted-foreground">
                      <MessageSquare size={10} />
                      <span>Desenvolvimentos</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Depois */}
              <div className="relative pb-6">
                <div className="absolute -left-[37px] flex size-6 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-600">3</div>
                <div className="rounded-md border border-border bg-muted/30 p-3">
                  <div className="mb-1 flex items-center gap-2">
                    <span className="text-xs font-semibold text-primary">Após a reunião</span>
                    <span className="rounded-md bg-blue-100 px-1.5 py-0.5 text-xs text-blue-700">Secretário</span>
                  </div>
                  <p className="text-xs text-muted-foreground">Revisão final, finalização da ata e exportação do PDF para distribuição.</p>
                  <div className="mt-2 flex items-center gap-2">
                    <div className="flex items-center gap-1 rounded bg-white px-1.5 py-0.5 text-xs text-muted-foreground">
                      <CheckCircle size={10} />
                      <span>Finalizar</span>
                    </div>
                    <div className="flex items-center gap-1 rounded bg-white px-1.5 py-0.5 text-xs text-muted-foreground">
                      <Download size={10} />
                      <span>Exportar PDF</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Distribuição */}
              <div className="relative">
                <div className="absolute -left-[37px] flex size-6 items-center justify-center rounded-full bg-green-100 text-xs font-bold text-green-600">
                  <Check size={12} />
                </div>
                <div className="rounded-md border border-green-200 bg-green-50 p-3">
                  <div className="mb-1 flex items-center gap-2">
                    <span className="text-xs font-semibold text-green-700">Distribuição</span>
                    <span className="rounded-md bg-green-200 px-1.5 py-0.5 text-xs text-green-700">Todos</span>
                  </div>
                  <p className="text-xs text-green-800">PDF enviado por e-mail ou WhatsApp para todos os participantes e interessados.</p>
                  <div className="mt-2 flex items-center gap-2">
                    <div className="flex items-center gap-1 rounded bg-white px-1.5 py-0.5 text-xs text-green-700">
                      <Mail size={10} />
                      <span>E-mail</span>
                    </div>
                    <div className="flex items-center gap-1 rounded bg-white px-1.5 py-0.5 text-xs text-green-700">
                      <Share2 size={10} />
                      <span>WhatsApp</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Boas práticas de colaboração */}
        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">
            Boas práticas de colaboração
          </h2>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 p-3">
              <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-green-500 text-white">
                <Check size={12} />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground">Defina um responsável pela ata</h4>
                <p className="text-xs text-muted-foreground">Evite que várias pessoas editem ao mesmo tempo.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 p-3">
              <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-green-500 text-white">
                <Check size={12} />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground">Distribua o PDF após finalizar</h4>
                <p className="text-xs text-muted-foreground">Envie para todos os participantes e partes interessadas.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 p-3">
              <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-green-500 text-white">
                <Check size={12} />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground">Use o compartilhamento do sistema</h4>
                <p className="text-xs text-muted-foreground">Colaboradores podem consultar diretamente sem depender de e-mails.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 p-3">
              <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-green-500 text-white">
                <Check size={12} />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground">Padronize tópicos e nomenclaturas</h4>
                <p className="text-xs text-muted-foreground">Todos usam os mesmos nomes de tópicos para consistência.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Quem pode ser colaborador */}
        <section className="rounded-lg border border-border bg-muted/50 p-6">
          <h3 className="mb-3 text-lg font-semibold text-foreground">
            Quem pode ser colaborador?
          </h3>
          <p className="mb-4 text-sm text-muted-foreground">
            Qualquer pessoa com conta no Atas Digitais pode ser adicionada como colaborador de um projeto:
          </p>
          <div className="grid gap-2 md:grid-cols-2">
            <div className="flex items-center gap-2 rounded-md bg-white px-3 py-2">
              <div className="flex size-6 items-center justify-center rounded-full bg-blue-100">
                <Users size={12} className="text-blue-600" />
              </div>
              <span className="text-sm text-muted-foreground">Colegas de trabalho da sua empresa</span>
            </div>
            <div className="flex items-center gap-2 rounded-md bg-white px-3 py-2">
              <div className="flex size-6 items-center justify-center rounded-full bg-green-100">
                <Building2 size={12} className="text-green-600" />
              </div>
              <span className="text-sm text-muted-foreground">Profissionais de empresas parceiras</span>
            </div>
            <div className="flex items-center gap-2 rounded-md bg-white px-3 py-2">
              <div className="flex size-6 items-center justify-center rounded-full bg-orange-100">
                <User size={12} className="text-orange-600" />
              </div>
              <span className="text-sm text-muted-foreground">Representantes do cliente</span>
            </div>
            <div className="flex items-center gap-2 rounded-md bg-white px-3 py-2">
              <div className="flex size-6 items-center justify-center rounded-full bg-purple-100">
                <ClipboardCheck size={12} className="text-purple-600" />
              </div>
              <span className="text-sm text-muted-foreground">Fiscalização e supervisão</span>
            </div>
          </div>
        </section>

        {/* Dica final */}
        <section className="rounded-lg borderborder-primary/20 bg-primary/5 p-6">
          <h3 className="mb-3 text-lg font-semibold text-foreground">
            Transparência com o cliente
          </h3>
          <p className="text-muted-foreground">
            Considere adicionar representantes do cliente como colaboradores do projeto.
            Isso permite que eles acompanhem o andamento em tempo real, aumentando a
            transparência e a confiança no relacionamento.
          </p>
        </section>
      </div>
    </DocsLayout>
  );
}

// Página: Organizando projetos (DEPRECATED - kept for backwards compatibility)
function OrganizandoProjetosPage() {
  return (
    <DocsLayout
      title="Organizando projetos"
      description="Como estruturar projetos para facilitar a gestão"
      keywords="organizar projetos obra, estruturar projetos, nomenclatura projetos, gestão projetos ata"
      previousPage={{ title: 'Boas práticas para atas', slug: 'boas-praticas-atas' }}
      nextPage={{ title: 'Acompanhando decisões', slug: 'acompanhando-decisoes' }}
    >
      <div className="space-y-8">
        <section>
          <p className="mb-6 text-lg leading-relaxed text-muted-foreground">
            A forma como você organiza seus projetos no Atas Digitais impacta diretamente na
            facilidade de encontrar informações e acompanhar o andamento dos projetos.
          </p>
        </section>

        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">
            Estratégias de organização
          </h2>
          <div className="space-y-4">
            <div className="rounded-lg border border-border p-5">
              <h4 className="mb-2 font-semibold text-foreground">Por obra específica</h4>
              <p className="mb-3 text-sm text-muted-foreground">
                Cada iniciativa (obra, cliente, contrato) é um projeto separado.
              </p>
              <div className="rounded-md bg-muted/50 p-3 font-mono text-sm text-muted-foreground">
                <div>📁 Obra Shopping Norte - Fase 2</div>
                <div>📁 Obra Edifício Comercial Centro</div>
                <div>📁 Reforma Hospital Municipal</div>
              </div>
            </div>

            <div className="rounded-lg border border-border p-5">
              <h4 className="mb-2 font-semibold text-foreground">Por cliente</h4>
              <p className="mb-3 text-sm text-muted-foreground">
                Útil quando você tem contratos de longo prazo com o mesmo cliente.
              </p>
              <div className="rounded-md bg-muted/50 p-3 font-mono text-sm text-muted-foreground">
                <div>📁 Cliente Petrobras - Manutenção 2025</div>
                <div>📁 Cliente Vale - Expansão Unidade 3</div>
              </div>
            </div>

            <div className="rounded-lg border border-border p-5">
              <h4 className="mb-2 font-semibold text-foreground">Por contrato</h4>
              <p className="mb-3 text-sm text-muted-foreground">
                Quando o mesmo cliente tem múltiplos contratos independentes.
              </p>
              <div className="rounded-md bg-muted/50 p-3 font-mono text-sm text-muted-foreground">
                <div>📁 Contrato 2024-001 - Fiscalização BR-101</div>
                <div>📁 Contrato 2024-002 - Supervisão Porto</div>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">
            Boas práticas para nomes de projetos
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="p-3 text-left font-semibold text-foreground">Evite</th>
                  <th className="p-3 text-left font-semibold text-foreground">Prefira</th>
                </tr>
              </thead>
              <tbody className="text-muted-foreground">
                <tr className="border-b border-border">
                  <td className="p-3">"Obra 1"</td>
                  <td className="p-3">"Obra Shopping Norte - Fase 2"</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3">"Cliente ABC"</td>
                  <td className="p-3">"ABC Engenharia - Manutenção 2025"</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3">"Projeto novo"</td>
                  <td className="p-3">"Fiscalização BR-101 Lote 3"</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="rounded-lg border border-border bg-muted/50 p-6">
          <h3 className="mb-3 text-lg font-semibold text-foreground">
            Quando criar um novo projeto?
          </h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-green-600">✓</span>
              <span>Quando iniciar uma nova obra ou contrato</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-600">✓</span>
              <span>Quando a equipe de participantes muda significativamente</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-600">✓</span>
              <span>Quando o escopo do trabalho é independente de outros projetos</span>
            </li>
          </ul>
        </section>

        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">
            Descrição do projeto
          </h2>
          <p className="mb-4 text-muted-foreground">
            Use o campo de descrição para adicionar informações úteis:
          </p>
          <ul className="space-y-2 text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span>Endereço ou localização da obra</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span>Número do contrato</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span>Data de início e previsão de término</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span>Principais envolvidos no projeto</span>
            </li>
          </ul>
        </section>

        <section className="rounded-lg borderborder-primary/20 bg-primary/5 p-6">
          <h3 className="mb-3 text-lg font-semibold text-foreground">
            Imagem de capa
          </h3>
          <p className="text-muted-foreground">
            Adicionar uma foto da obra como imagem de capa ajuda a identificar rapidamente
            o projeto no Dashboard, especialmente quando você gerencia muitos projetos simultaneamente.
          </p>
        </section>
      </div>
    </DocsLayout>
  );
}

// Página: Acompanhando decisões
function AcompanhandoDecisoesPage() {
  return (
    <DocsLayout
      title="Acompanhando decisões"
      description="Como usar o Atas Digitais para rastrear decisões e pendências"
      keywords="acompanhar decisões, rastrear pendências, controle decisões obra, follow-up reuniões"
      previousPage={{ title: 'Organizando projetos', slug: 'organizando-projetos' }}
      nextPage={{ title: 'Colaboração em equipe', slug: 'colaboracao-em-equipe' }}
    >
      <div className="space-y-8">
        <section>
          <p className="mb-6 text-lg leading-relaxed text-muted-foreground">
            Uma das principais vantagens do Atas Digitais é a capacidade de acompanhar decisões
            e pendências ao longo do tempo. Veja como aproveitar ao máximo essa funcionalidade.
          </p>
        </section>

        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">
            Rastreando a evolução de um item
          </h2>
          <p className="mb-4 text-muted-foreground">
            Cada item mantém um histórico completo desde sua criação:
          </p>
          <div className="rounded-lg border border-border bg-background p-4">
            <div className="space-y-3 text-sm">
              <div className="rounded-md bg-muted/50 p-3">
                <div className="font-medium text-foreground">
                  Item: Aprovar projeto de fundações
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  Responsável: Eng. Carlos | Criado na Ata 25
                </div>
              </div>
              <div className="ml-4 space-y-2 border-l-2 border-primary/30 pl-4">
                <div className="rounded-md bg-muted/30 p-2">
                  <span className="text-xs font-medium text-muted-foreground">Ata 25 - 05/01</span>
                  <p className="text-muted-foreground">"Projeto em revisão pelo calculista"</p>
                </div>
                <div className="rounded-md bg-muted/30 p-2">
                  <span className="text-xs font-medium text-muted-foreground">Ata 26 - 12/01</span>
                  <p className="text-muted-foreground">"Calculista solicitou ajustes no dimensionamento"</p>
                </div>
                <div className="rounded-md bg-muted/30 p-2">
                  <span className="text-xs font-medium text-muted-foreground">Ata 27 - 19/01</span>
                  <p className="text-muted-foreground">"Ajustes realizados, projeto enviado para aprovação"</p>
                </div>
                <div className="rounded-md bg-green-50 p-2">
                  <span className="text-xs font-medium text-green-700">Ata 28 - 26/01</span>
                  <p className="text-muted-foreground">"Projeto aprovado pelo cliente" ✓</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">
            Usando o Mapa de Atas
          </h2>
          <p className="mb-4 text-muted-foreground">
            O Mapa de Atas é a ferramenta ideal para ter uma visão geral:
          </p>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-border p-5">
              <h4 className="mb-2 font-semibold text-foreground">Identificar itens travados</h4>
              <p className="text-sm text-muted-foreground">
                Veja quais pendências estão abertas há muito tempo e precisam de atenção especial.
              </p>
            </div>
            <div className="rounded-lg border border-border p-5">
              <h4 className="mb-2 font-semibold text-foreground">Preparar reuniões</h4>
              <p className="text-sm text-muted-foreground">
                Antes da reunião, revise os itens pendentes para já saber o que cobrar.
              </p>
            </div>
            <div className="rounded-lg border border-border p-5">
              <h4 className="mb-2 font-semibold text-foreground">Apresentar status</h4>
              <p className="text-sm text-muted-foreground">
                Exporte o Mapa em PDF para apresentar o panorama geral à diretoria ou ao cliente.
              </p>
            </div>
            <div className="rounded-lg border border-border p-5">
              <h4 className="mb-2 font-semibold text-foreground">Auditar histórico</h4>
              <p className="text-sm text-muted-foreground">
                Consulte quando e como cada decisão foi tomada, útil em disputas contratuais.
              </p>
            </div>
          </div>
        </section>

        <section className="rounded-lg border border-border bg-muted/50 p-6">
          <h3 className="mb-3 text-lg font-semibold text-foreground">
            Fluxo recomendado para reuniões
          </h3>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-600">1</span>
              <p className="text-sm text-muted-foreground">
                <strong className="text-foreground">Antes:</strong> Abra o Mapa de Atas e revise os itens pendentes
              </p>
            </div>
            <div className="flex items-start gap-3">
              <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-600">2</span>
              <p className="text-sm text-muted-foreground">
                <strong className="text-foreground">Durante:</strong> Clone a última ata e registre desenvolvimentos em tempo real
              </p>
            </div>
            <div className="flex items-start gap-3">
              <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-600">3</span>
              <p className="text-sm text-muted-foreground">
                <strong className="text-foreground">Depois:</strong> Finalize a ata e exporte o PDF para distribuição
              </p>
            </div>
          </div>
        </section>

        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">
            Documentando decisões importantes
          </h2>
          <p className="mb-4 text-muted-foreground">
            Algumas decisões têm impacto significativo no projeto. Registre-as de forma clara:
          </p>
          <ul className="space-y-2 text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span><strong className="text-foreground">Mudanças de escopo:</strong> "Cliente aprovou adicional de R$ 50.000 para instalação de gerador"</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span><strong className="text-foreground">Alterações de prazo:</strong> "Prazo de entrega alterado de 30/03 para 15/04 devido a chuvas"</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span><strong className="text-foreground">Definições técnicas:</strong> "Optou-se por fundação em estacas devido ao solo argiloso"</span>
            </li>
          </ul>
        </section>

        <section className="rounded-lg borderborder-primary/20 bg-primary/5 p-6">
          <h3 className="mb-3 text-lg font-semibold text-foreground">
            Valor para disputas contratuais
          </h3>
          <p className="text-muted-foreground">
            O histórico detalhado de desenvolvimentos serve como evidência em casos de disputas.
            Ele mostra exatamente quando cada assunto foi discutido, quem era responsável
            e qual foi a evolução até a resolução.
          </p>
        </section>
      </div>
    </DocsLayout>
  );
}

// Página: Colaboração em equipe
function ColaboracaoEmEquipePage() {
  return (
    <DocsLayout
      title="Colaboração em equipe"
      description="Como trabalhar em equipe usando o Atas Digitais"
      keywords="colaboração em equipe, trabalho colaborativo, equipe atas, compartilhar atas, múltiplos usuários"
      previousPage={{ title: 'Acompanhando decisões', slug: 'acompanhando-decisoes' }}
      nextPage={{ title: 'Novidades', slug: 'novidades' }}
    >
      <div className="space-y-8">
        <section>
          <p className="mb-6 text-lg leading-relaxed text-muted-foreground">
            O Atas Digitais permite que múltiplas pessoas da equipe colaborem no mesmo projeto.
            Veja como organizar o trabalho em equipe de forma eficiente.
          </p>
        </section>

        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">
            Papéis típicos em uma equipe
          </h2>
          <div className="space-y-4">
            <div className="rounded-lg border border-border p-5">
              <h4 className="mb-2 font-semibold text-foreground">Secretário da reunião</h4>
              <p className="text-sm text-muted-foreground">
                Responsável por registrar os assuntos durante a reunião. Geralmente é quem
                cria a ata, adiciona participantes e registra os desenvolvimentos em tempo real.
              </p>
            </div>

            <div className="rounded-lg border border-border p-5">
              <h4 className="mb-2 font-semibold text-foreground">Coordenador do projeto</h4>
              <p className="text-sm text-muted-foreground">
                Acompanha o andamento geral pelo Mapa de Atas. Cobra responsáveis por itens
                atrasados e garante que as atas sejam finalizadas e distribuídas.
              </p>
            </div>

            <div className="rounded-lg border border-border p-5">
              <h4 className="mb-2 font-semibold text-foreground">Responsáveis técnicos</h4>
              <p className="text-sm text-muted-foreground">
                Consultam as atas para verificar suas pendências e prazos. Podem adicionar
                desenvolvimentos aos itens sob sua responsabilidade.
              </p>
            </div>
          </div>
        </section>

        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">
            Boas práticas de colaboração
          </h2>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="flex items-start gap-3 rounded-lg border border-border p-4">
              <span className="text-green-600">✓</span>
              <div>
                <h4 className="font-semibold text-foreground">Defina um responsável pela ata</h4>
                <p className="text-sm text-muted-foreground">Evite que várias pessoas editem ao mesmo tempo.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-border p-4">
              <span className="text-green-600">✓</span>
              <div>
                <h4 className="font-semibold text-foreground">Distribua o PDF após finalizar</h4>
                <p className="text-sm text-muted-foreground">Envie para todos os participantes e partes interessadas.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-border p-4">
              <span className="text-green-600">✓</span>
              <div>
                <h4 className="font-semibold text-foreground">Use o compartilhamento do sistema</h4>
                <p className="text-sm text-muted-foreground">Colaboradores podem consultar diretamente sem depender de e-mails.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-border p-4">
              <span className="text-green-600">✓</span>
              <div>
                <h4 className="font-semibold text-foreground">Padronize tópicos e nomenclaturas</h4>
                <p className="text-sm text-muted-foreground">Todos usam os mesmos nomes de tópicos para consistência.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="rounded-lg border border-border bg-muted/50 p-6">
          <h3 className="mb-3 text-lg font-semibold text-foreground">
            Quem pode ser colaborador?
          </h3>
          <p className="mb-4 text-sm text-muted-foreground">
            Qualquer pessoa com conta no Atas Digitais pode ser adicionada como colaborador de um projeto:
          </p>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span>Colegas de trabalho da sua empresa</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span>Profissionais de empresas parceiras</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span>Representantes do cliente</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span>Fiscalização e supervisão</span>
            </li>
          </ul>
        </section>

        <section>
          <h2 className="mb-4 text-2xl font-semibold text-foreground">
            Fluxo típico de trabalho
          </h2>
          <div className="space-y-3">
            <div className="flex items-start gap-4 rounded-lg border border-border p-4">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">1</span>
              <div>
                <h4 className="font-semibold text-foreground">Antes da reunião</h4>
                <p className="text-sm text-muted-foreground">
                  O secretário clona a última ata e compartilha o link com os participantes para que revisem as pendências.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 rounded-lg border border-border p-4">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">2</span>
              <div>
                <h4 className="font-semibold text-foreground">Durante a reunião</h4>
                <p className="text-sm text-muted-foreground">
                  O secretário registra desenvolvimentos e novos itens em tempo real conforme os assuntos são discutidos.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 rounded-lg border border-border p-4">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">3</span>
              <div>
                <h4 className="font-semibold text-foreground">Após a reunião</h4>
                <p className="text-sm text-muted-foreground">
                  Revisão final, finalização da ata e exportação do PDF para distribuição por e-mail ou WhatsApp.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="rounded-lg borderborder-primary/20 bg-primary/5 p-6">
          <h3 className="mb-3 text-lg font-semibold text-foreground">
            Transparência com o cliente
          </h3>
          <p className="text-muted-foreground">
            Considere adicionar representantes do cliente como colaboradores do projeto.
            Isso permite que eles acompanhem o andamento em tempo real, aumentando a
            transparência e a confiança no relacionamento.
          </p>
        </section>
      </div>
    </DocsLayout>
  );
}

// Página: Novidades
function NovidadesPage() {
  return (
    <DocsLayout
      title="Novidades"
      description="Atualizações e melhorias recentes do Atas Digitais"
      keywords="novidades stant atas, atualizações sistema, changelog, novas funcionalidades, melhorias"
      previousPage={{ title: 'Guia de boas práticas', slug: 'boas-praticas-atas' }}
    >
      <div className="space-y-8">
        <section>
          <p className="mb-6 text-lg leading-relaxed text-muted-foreground">
            Acompanhe as atualizações e melhorias mais recentes do Atas Digitais.
            Estamos sempre trabalhando para tornar a ferramenta mais útil para você.
          </p>
        </section>

        <section>
          <div className="space-y-6">
            {/* Novembro 2025 */}
            <div className="rounded-lg border border-border p-6">
              <div className="mb-4 flex items-center gap-3">
                <span className="rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary">Novembro 2025</span>
                <span className="text-xs text-muted-foreground">Versão atual</span>
              </div>
              <h3 className="mb-3 text-lg font-semibold text-foreground">Central de Documentação</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="text-green-600">✓</span>
                  <span>Nova seção de documentação completa do sistema</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600">✓</span>
                  <span>Busca rápida por tópicos de ajuda</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600">✓</span>
                  <span>Guias práticos para uso avançado</span>
                </li>
              </ul>
            </div>

            {/* Outubro 2025 */}
            <div className="rounded-lg border border-border p-6">
              <div className="mb-4">
                <span className="rounded-full bg-muted px-3 py-1 text-sm font-medium text-muted-foreground">Outubro 2025</span>
              </div>
              <h3 className="mb-3 text-lg font-semibold text-foreground">Melhorias na experiência</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="text-green-600">✓</span>
                  <span>Salvamento automático mais rápido</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600">✓</span>
                  <span>Melhoria no desempenho do Mapa de Atas</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600">✓</span>
                  <span>Correções de bugs reportados pelos usuários</span>
                </li>
              </ul>
            </div>

            {/* Setembro 2025 */}
            <div className="rounded-lg border border-border p-6">
              <div className="mb-4">
                <span className="rounded-full bg-muted px-3 py-1 text-sm font-medium text-muted-foreground">Setembro 2025</span>
              </div>
              <h3 className="mb-3 text-lg font-semibold text-foreground">Funcionalidade de clonagem</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="text-green-600">✓</span>
                  <span>Clone atas anteriores com um clique</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600">✓</span>
                  <span>Itens pendentes são trazidos automaticamente</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600">✓</span>
                  <span>Participantes e tópicos preservados</span>
                </li>
              </ul>
            </div>

            {/* Agosto 2025 */}
            <div className="rounded-lg border border-border p-6">
              <div className="mb-4">
                <span className="rounded-full bg-muted px-3 py-1 text-sm font-medium text-muted-foreground">Agosto 2025</span>
              </div>
              <h3 className="mb-3 text-lg font-semibold text-foreground">Mapa de Atas</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="text-green-600">✓</span>
                  <span>Visualização consolidada de todos os itens</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600">✓</span>
                  <span>Histórico completo de desenvolvimentos</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600">✓</span>
                  <span>Exportação do Mapa em PDF</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        <section className="rounded-lg borderborder-primary/20 bg-primary/5 p-6">
          <h3 className="mb-3 text-lg font-semibold text-foreground">
            Sugestões e feedback
          </h3>
          <p className="text-muted-foreground">
            Tem alguma sugestão de melhoria ou encontrou um problema? Entre em contato
            conosco pelo e-mail de suporte. Sua opinião é fundamental para continuarmos
            evoluindo o Atas Digitais.
          </p>
        </section>
      </div>
    </DocsLayout>
  );
}

export default function Docs() {
  usePageTitle('Documentação');
  const navigate = useNavigate();
  const { t } = useTranslation('docs');
  const { t: tCommon } = useTranslation('common');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showSplash, setShowSplash] = useState(false);

  const handleNavigateToLogin = useCallback(async () => {
    setShowSplash(true);
    await new Promise<void>((resolve) => setTimeout(resolve, 900));
    navigate('/login');
  }, [navigate]);

  return (
    <DocsSidebarProvider>
    <div className="relative flex min-h-screen flex-col bg-background">
      {showSplash && (
        <SplashScreen visible={showSplash} variant="post-login" subtitle={tCommon('loading')} />
      )}

      {/* Mobile Header */}
      <header className="sticky top-0 z-50 flex items-center justify-between border-b border-border bg-background/95 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/60 lg:hidden">
        <Link to="/">
          <img src="/logo-stant.svg" alt="Stant" className="h-7 w-auto dark:brightness-0 dark:invert" />
        </Link>

        <div className="flex items-center gap-2">
          <LanguageSwitcher variant="compact" />
          <ThemeToggle />
          <Button variant="ghost" size="sm" onClick={handleNavigateToLogin}>
            <ArrowLeft className="mr-2 size-4" />
            {t('nav.back')}
          </Button>
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon" className="lg:hidden">
                {mobileMenuOpen ? (
                  <X className="size-5" />
                ) : (
                  <Menu className="size-5" />
                )}
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-0">
              <DocsSidebar onNavigate={() => setMobileMenuOpen(false)} />
            </SheetContent>
          </Sheet>
        </div>
      </header>

      {/* Desktop Header - Voltar e Busca */}
      <div className="hidden border-b border-border bg-background px-6 py-3 lg:flex lg:items-center lg:justify-between">
        <Button variant="ghost" size="sm" onClick={handleNavigateToLogin}>
          <ArrowLeft className="mr-2 size-4" />
          {t('nav.back')}
        </Button>

        <div className="flex items-center gap-2">
          <DocsSearch />
          <LanguageSwitcher variant="default" />
          <ThemeToggle />
        </div>
      </div>

      {/* Container principal com sidebar e conteúdo */}
      <div className="flex-1 flex flex-col lg:flex-row">
        {/* Desktop Sidebar */}
        <DocsSidebar />

        {/* Main Content */}
        <main className="flex-1 bg-muted/30">
          <Routes>
            <Route path="/" element={<Navigate to="/docs/o-que-e" replace />} />

            {/* Introdução */}
            <Route path="/o-que-e" element={<IntroductionPageWrapper />} />
            <Route path="/conceitos-fundamentais" element={<ConceitosFundamentaisPageWrapper />} />

            {/* Funcionalidades */}
            <Route path="/visao-geral" element={<VisaoGeralPageWrapper />} />
            <Route path="/projetos" element={<ProjetosPageWrapper />} />
            <Route path="/atas-de-reuniao" element={<AtasDeReuniaoPageWrapper />} />
            <Route path="/topicos-e-itens" element={<TopicosEItensPageWrapper />} />
            <Route path="/desenvolvimentos" element={<DesenvolvimentosPageWrapper />} />
            <Route path="/convidar-equipe" element={<ConvidarEquipePageWrapper />} />
            <Route path="/mapa-de-atas" element={<MapaDeAtasPageWrapper />} />
            <Route path="/personalizacao" element={<PersonalizacaoPageWrapper />} />
            <Route path="/preview-e-exportacao" element={<PreviewExportacaoPageWrapper />} />

            {/* Guias */}
            <Route path="/boas-praticas-atas" element={<BoasPraticasAtasPageWrapper />} />
            <Route path="/organizacao-e-colaboracao" element={<OrganizacaoColaboracaoPageWrapper />} />

            {/* Changelog */}
            <Route path="/novidades" element={<NovidadesPageWrapper />} />

            {/* Redirects for old URLs */}
            <Route path="/por-que-usar" element={<Navigate to="/docs/o-que-e" replace />} />
            <Route path="/conceitos-basicos" element={<Navigate to="/docs/o-que-e" replace />} />
            <Route path="/criar-conta" element={<Navigate to="/docs/visao-geral" replace />} />
            <Route path="/criar-projeto" element={<Navigate to="/docs/projetos" replace />} />
            <Route path="/primeira-ata" element={<Navigate to="/docs/atas-de-reuniao" replace />} />
            <Route path="/dashboard" element={<Navigate to="/docs/visao-geral" replace />} />
            <Route path="/participantes" element={<ParticipantesPageWrapper />} />
            <Route path="/compartilhamento" element={<Navigate to="/docs/convidar-equipe" replace />} />
            <Route path="/organizando-projetos" element={<Navigate to="/docs/organizacao-e-colaboracao" replace />} />
            <Route path="/acompanhando-decisoes" element={<Navigate to="/docs/boas-praticas-atas" replace />} />
            <Route path="/colaboracao-em-equipe" element={<Navigate to="/docs/organizacao-e-colaboracao" replace />} />
          </Routes>
        </main>
      </div>
    </div>
    </DocsSidebarProvider>
  );
}
